import { AlertTriangle, ArrowRight, Award, BarChart3, BookOpen, Brain, Calendar, CheckCircle, ChevronLeft, ChevronRight, Circle, Clock, Code, Eye, EyeOff, FileText, Flame, Github, Globe, Languages, Linkedin, Lock, LogOut, Mail, MessageCircle, Mic, MicOff, Moon, Plus, RotateCcw, Settings, Sparkles, Star, Sun, Target, Trash2, Trophy, Upload, User, Video, Volume2, VolumeX, X, Youtube, Zap } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import ACTPracticeTest from './ACTPracticeTest';
import * as analytics from './analytics';
import AnalyticsDashboard from './AnalyticsDashboard';
import ChemistrySimulation from './ChemistrySimulation';
import CodeInterpreter from './CodeInterpreter';
import DiscoverFeed from './DiscoverFeed';
import JEEPracticeTest from './JEEPracticeTest';
import MathVisualization from './MathVisualization';
import NEETPracticeTest from './NEETPracticeTest';
import { extractPdfText } from './pdfExtractor';
import PhysicsSimulation from './PhysicsSimulation';
import PointsDisplay from './PointsDisplay';
import * as pointsSystem from './pointsSystem';
import { extractPptText } from './pptExtractor';
import SATPracticeTest from './SATpracticeTest';
import StudyCompanion from './StudyCompanion';
import { supabase } from './supabaseClient';
import Tutorial from './Tutorial';
import './Tutorial.css';
import YouTubeUpload from './YouTubeUpload';
import AITutorialGenerator from './AITutorialGenerator';

// Import separated components
import {
  createTranslationFunction,
  LanguageButton,
  LanguageModal,
  LanguageSetting,
  SUPPORTED_LANGUAGES
} from './languageTranslation';

import {
  FooterLegalLinks,
  PrivacyModal,
  TermsModal
} from './legalModals';

import {
  DonationBanner,
  FloatingDonationButton,
  useDonationBanner
} from './donationBanner';

import {
  DeveloperButton,
  DeveloperFooterLink
} from './developerSection';


// ========== API CONFIGURATION ==========
// Replace with your actual OpenAI API key
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const NalemStudyCircle = () => {

  const [passwordValidation, setPasswordValidation] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
    });
  // Authentication State
  const [currentView, setCurrentView] = useState('landing');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [showLogin, setShowLogin] = useState(true);

  const [showPassword, setShowPassword] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);

  // App State
  const [darkMode, setDarkMode] = useState(false);
  const [learningGoal, setLearningGoal] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [currentLesson, setCurrentLesson] = useState(0);
  const [progress, setProgress] = useState(0);
  const [questionsLeft, setQuestionsLeft] = useState(10);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [completedLessons, setCompletedLessons] = useState(new Set());
  const [quizAnswers, setQuizAnswers] = useState({});
  const [showQuizResults, setShowQuizResults] = useState(false);
  const [roadmapGenerated, setRoadmapGenerated] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentRoadmap, setCurrentRoadmap] = useState(null);
  const [showSATPracticeTest, setShowSATPracticeTest] = useState(false);
  const [satTestResults, setSatTestResults] = useState([]);
  const [showACTPracticeTest, setShowACTPracticeTest] = useState(false);
  const [actTestResults, setActTestResults] = useState([]);
  const [showNEETPracticeTest, setShowNEETPracticeTest] = useState(false);
  const [neetTestResults, setNeetTestResults] = useState([]);
  const [showJEEPracticeTest, setShowJEEPracticeTest] = useState(false);
  const [jeeTestResults, setJeeTestResults] = useState([]);
  const [showCodeInterpreter, setShowCodeInterpreter] = useState(false);
  const [showAnalyticsDashboard, setShowAnalyticsDashboard] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [userGoals, setUserGoals] = useState([]);
  const [currentQuiz, setCurrentQuiz] = useState(null);
  const [quizExplanations, setQuizExplanations] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedDays, setSelectedDays] = useState([]);
  const [studySchedule, setStudySchedule] = useState(null);
  const [missedDays, setMissedDays] = useState([]);
  const [lessonQuizzes, setLessonQuizzes] = useState({}); // Store unique quizzes for each lesson
  const [currentDate, setCurrentDate] = useState(new Date());
  const [lastActivityDate, setLastActivityDate] = useState(null);
  
  // FIXED: Move quiz state to main component level to prevent reset on re-renders
  const [lessonQuiz, setLessonQuiz] = useState(null);
  const [quizLoading, setQuizLoading] = useState(false);
  const [quizStarted, setQuizStarted] = useState(false);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [questionStartTime, setQuestionStartTime] = useState(null);
  const [showHint, setShowHint] = useState(false);
  const [hintShown, setHintShown] = useState({});
  const [timeSpentOnQuestion, setTimeSpentOnQuestion] = useState(0);
  const [quizResults, setQuizResults] = useState(null);
  const [quizFeedback, setQuizFeedback] = useState(null);
  const [quizInitialized, setQuizInitialized] = useState(false);

  // Points & Rewards System State
  const [userPointsData, setUserPointsData] = useState({ total_points: 0, level: 1, streak_days: 0 });
  const [newAchievements, setNewAchievements] = useState([]);
  const [showAchievementModal, setShowAchievementModal] = useState(false);
  const [simulationInteractions, setSimulationInteractions] = useState(0);

  // Quiz Tracking States
  const [quizStartTime, setQuizStartTime] = useState(null);
  const [questionTimes, setQuestionTimes] = useState({});
  const [quizEstimatedTime, setQuizEstimatedTime] = useState(0);
  const [needsHelp, setNeedsHelp] = useState({});
  const [helpOffered, setHelpOffered] = useState({});
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [helpQuestion, setHelpQuestion] = useState(null);
  
  // Pomodoro Timer States
  const [pomodoroActive, setPomodoroActive] = useState(false);
  const [pomodoroTime, setPomodoroTime] = useState(25 * 60); // 25 minutes in seconds
  const [pomodoroType, setPomodoroType] = useState('work'); // 'work' or 'break'
  const [pomodoroSession, setPomodoroSession] = useState(1);
  const [customPomodoroSettings, setCustomPomodoroSettings] = useState({
    workDuration: 25,
    shortBreak: 5,
    longBreak: 15,
    sessionsUntilLongBreak: 4
  });
  const [showPomodoroModal, setShowPomodoroModal] = useState(false);
  const [autoStartPomodoro, setAutoStartPomodoro] = useState(false);

  // NEW: Delete confirmation states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [goalToDelete, setGoalToDelete] = useState(null);

  // NEW: Analytics and Gamification states
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [currentAnalytics, setCurrentAnalytics] = useState(null);
  const [userPoints, setUserPoints] = useState(0);
  const [userBadges, setUserBadges] = useState([]);
  const [showBadgeModal, setShowBadgeModal] = useState(false);
  const [newBadgeEarned, setNewBadgeEarned] = useState(null);
  const [courseAnalytics, setCourseAnalytics] = useState({});
  const [practiceTestMode, setPracticeTestMode] = useState(false);
  const [currentPracticeTest, setCurrentPracticeTest] = useState(null);
  const [practiceTestResults, setPracticeTestResults] = useState(null);

  // NEW: Advanced Features States
  const [showSchedulingModal, setShowSchedulingModal] = useState(false);
  const [studyTimeSlots, setStudyTimeSlots] = useState([]);
  const [showVocabularyModal, setShowVocabularyModal] = useState(false);
  const [vocabularyLists, setVocabularyLists] = useState([]);
  const [showTutorialGenerator, setShowTutorialGenerator] = useState(false);
  const [vocabularyModalSource, setVocabularyModalSource] = useState(null); // Track where vocab modal was opened from
  const [flashcardSource, setFlashcardSource] = useState(null); // Track where flashcard modal was opened from
  const [currentVocabList, setCurrentVocabList] = useState(null);
  const [flashcardMode, setFlashcardMode] = useState(false);
  const [currentFlashcard, setCurrentFlashcard] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [vocabularyQuizMode, setVocabularyQuizMode] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [showNotesUpload, setShowNotesUpload] = useState(false);
  const [uploadedNotes, setUploadedNotes] = useState([]);
  const [showYouTubeUpload, setShowYouTubeUpload] = useState(false);
  const [showStudyCompanion, setShowStudyCompanion] = useState(false);
  const [companionVideoInfo, setCompanionVideoInfo] = useState(null);
  const [showDiscoverFeed, setShowDiscoverFeed] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  // NEW: Text-to-Speech for Vocabulary
  const [speechEnabled, setSpeechEnabled] = useState(true);
  
  // NEW: Donation Banner State
  const {
    showDonationBanner,
    setShowDonationBanner,
    isDismissed: donationDismissed,
    handleDismiss: handleDonationDismiss
  } = useDonationBanner();

  // NEW: Developer Modal State
  const [showDeveloperModal, setShowDeveloperModal] = useState(false);
  
  const speakWord = (text) => {
    if ('speechSynthesis' in window && speechEnabled) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.8;
      utterance.volume = 0.7;
      window.speechSynthesis.speak(utterance);
    }
  };
  const validatePassword = (password) => {
  const validation = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /\d/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
  };
  setPasswordValidation(validation);
  return Object.values(validation).every(Boolean);
};

  // Translation helper function
  const t = createTranslationFunction(currentLanguage);

  // NEW: Test Prep Detection Keywords
  const TEST_PREP_KEYWORDS = {
    'ap': ['ap biology', 'ap chemistry', 'ap physics', 'ap calculus', 'ap computer science', 'ap history', 'ap english', 'ap psychology'],
    'sat': ['sat prep', 'sat test', 'sat math', 'sat reading', 'sat writing'],
    'act': ['act prep', 'act test', 'act math', 'act reading', 'act science'],
    'gre': ['gre prep', 'gre test', 'gre math', 'gre verbal'],
    'gmat': ['gmat prep', 'gmat test'],
    'neet': ['neet prep', 'neet biology', 'neet chemistry', 'neet physics'],
    'jee': ['jee main', 'jee advanced', 'jee prep'],
    'mcat': ['mcat prep', 'mcat test'],
    'lsat': ['lsat prep', 'lsat test']
  };

  // NEW: Badge System Configuration
  const BADGE_SYSTEM = {
    study_time: [
      { id: 'study_novice', name: 'Study Novice', description: 'Complete 1 hour of study', icon: '📚', threshold: 3600000, color: 'bg-green-500' },
      { id: 'study_warrior', name: 'Study Warrior', description: 'Complete 10 hours of study', icon: '⚔️', threshold: 36000000, color: 'bg-blue-500' },
      { id: 'study_master', name: 'Study Master', description: 'Complete 50 hours of study', icon: '🎓', threshold: 180000000, color: 'bg-purple-500' },
      { id: 'study_legend', name: 'Study Legend', description: 'Complete 100 hours of study', icon: '👑', threshold: 360000000, color: 'bg-yellow-500' }
    ],
    lessons: [
      { id: 'lesson_starter', name: 'Lesson Starter', description: 'Complete 1 lesson', icon: '🌱', threshold: 1, color: 'bg-green-500' },
      { id: 'lesson_explorer', name: 'Lesson Explorer', description: 'Complete 5 lessons', icon: '🧭', threshold: 5, color: 'bg-blue-500' },
      { id: 'lesson_achiever', name: 'Lesson Achiever', description: 'Complete 20 lessons', icon: '🏆', threshold: 20, color: 'bg-purple-500' },
      { id: 'lesson_champion', name: 'Lesson Champion', description: 'Complete 50 lessons', icon: '🔥', threshold: 50, color: 'bg-red-500' }
    ],
    streaks: [
      { id: 'streak_starter', name: 'Consistent Learner', description: 'Maintain a 3-day streak', icon: '⭐', threshold: 3, color: 'bg-yellow-500' },
      { id: 'streak_warrior', name: 'Dedication Master', description: 'Maintain a 7-day streak', icon: '💫', threshold: 7, color: 'bg-purple-500' },
      { id: 'streak_legend', name: 'Unstoppable Force', description: 'Maintain a 30-day streak', icon: '⚡', threshold: 30, color: 'bg-red-500' }
    ],
    courses: [
      { id: 'course_finisher', name: 'Course Finisher', description: 'Complete your first course', icon: '🎯', threshold: 1, color: 'bg-green-500' },
      { id: 'multi_mastery', name: 'Multi-Mastery', description: 'Complete 3 courses', icon: '🧠', threshold: 3, color: 'bg-blue-500' },
      { id: 'learning_expert', name: 'Learning Expert', description: 'Complete 10 courses', icon: '🚀', threshold: 10, color: 'bg-purple-500' }
    ]
  };

  // Add debounced handlers for text inputs
  const [chatInputBuffer, setChatInputBuffer] = useState('');

  


  useEffect(() => {
    const timeout = setTimeout(() => {
      setChatInput(chatInputBuffer);
    }, 100);
    return () => clearTimeout(timeout);
  }, [chatInputBuffer]);
  const [dailyProgress, setDailyProgress] = useState({});
  const [weeklyStreak, setWeeklyStreak] = useState(0);
  const [totalStudyTime, setTotalStudyTime] = useState(0);
  const [currentSession, setCurrentSession] = useState({ start: null, duration: 0 });

  const chatEndRef = useRef(null);

  // Persistent Storage Functions
  const saveToStorage = (key, data) => {
    try {
      // Use a setTimeout to defer storage operations and prevent blocking
      setTimeout(() => {
        const storage = JSON.parse(localStorage.getItem('nalem_study_circle_data') || '{}');
        storage[key] = data;
        localStorage.setItem('nalem_study_circle_data', JSON.stringify(storage));
      }, 0);
    } catch (error) {
      console.error('Error saving to storage:', error);
    }
  };

  const loadFromStorage = (key, defaultValue = null) => {
    try {
      const storage = JSON.parse(localStorage.getItem('nalem_study_circle_data') || '{}');
      return storage[key] || defaultValue;
    } catch (error) {
      console.error('Error loading from storage:', error);
      return defaultValue;
    }
  };

const saveUserData = async (userId, userData) => {
  try {
    // For demo user, keep using localStorage
    if (userId === 'demo-user-123') {
      setTimeout(() => {
        const storage = JSON.parse(localStorage.getItem('nalem_study_circle_data') || '{}');
        const users = storage.users || {};
        users[userId] = userData;
        storage.users = users;
        localStorage.setItem('nalem_study_circle_data', JSON.stringify(storage));
      }, 0);
      return;
    }

    // For real users, save to Supabase
    console.log('Saving user data to database for user:', userId);

    // Helper to recursively sanitize all string fields
    function sanitize(obj) {
      if (typeof obj === 'string') {
        // Remove null bytes and unsupported Unicode escapes
        return obj.replace(/[\u0000\x00\0]/g, '').replace(/\\u[0-9A-Fa-f]{4}/g, '');
      } else if (Array.isArray(obj)) {
        return obj.map(sanitize);
      } else if (obj && typeof obj === 'object') {
        const clean = {};
        for (const k in obj) clean[k] = sanitize(obj[k]);
        return clean;
      }
      return obj;
    }

    const profileData = {
      id: userId,
      email: sanitize(userData.email),
      name: sanitize(userData.name),
      profile: sanitize(userData.profile || {}),
      session_data: sanitize(userData.sessionData || {}),
      updated_at: new Date().toISOString()
    };

    console.log('Profile data to save:', profileData);

    const { data, error } = await supabase
      .from('user_profiles')
      .upsert(profileData, {
        onConflict: 'id',
        returning: 'minimal'
      });

    if (error) {
      console.error('Supabase upsert error:', error);
      throw new Error(`Database error: ${error.message}`);
    }

    console.log('User data saved successfully');
    return data;
  } catch (error) {
    console.error('Error in saveUserData:', error);
    throw error;
  }
};

  const loadUserData = async (userId) => {
  try {
    // For demo user, use localStorage
    if (userId === 'demo-user-123') {
      const users = loadFromStorage('users', {});
      return users[userId] || null;
    }

    // For real users, load from Supabase
    console.log('Loading user data for:', userId);
    
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No rows returned - user profile doesn't exist yet
        console.log('No user profile found for:', userId);
        return null;
      }
      console.error('Error loading user data:', error);
      throw error;
    }

    if (data) {
      console.log('User data loaded successfully');
      return {
        id: data.id,
        email: data.email,
        name: data.name,
        profile: data.profile,
        sessionData: data.session_data
      };
    }

    return null;
  } catch (error) {
    console.error('Error in loadUserData:', error);
    throw error;
  }
};

  const saveCurrentSession = async () => {
  if (!currentUser) return;
  
  try {
    const sessionData = {
      currentView,
      currentRoadmap,
      userGoals,
      progress,
      completedLessons: Array.from(completedLessons),
      questionsLeft,
      chatMessages,
      totalStudyTime,
      dailyProgress,
      weeklyStreak,
      currentLesson,
      darkMode,
      studySchedule,
      missedDays,
      lessonQuizzes,
      lastActivityDate: new Date().toISOString().split('T')[0],
      autoStartPomodoro,
      userPoints,
      userBadges,
      courseAnalytics,
      studyTimeSlots,
      vocabularyLists,
      currentLanguage,
      uploadedNotes,
      satTestResults
    };
    
    const updatedUser = { ...currentUser, sessionData };
    await saveUserData(currentUser.id, updatedUser);
    setCurrentUser(updatedUser);
  } catch (error) {
    console.error('Error saving session:', error);
  }
};

  const restoreUserSession = (user) => {
    if (user.sessionData) {
      const data = user.sessionData;
      setCurrentRoadmap(data.currentRoadmap || (data.userGoals && data.userGoals[0]) || null);
      setUserGoals(data.userGoals || []);
      setProgress(data.progress || 0);
      setCompletedLessons(new Set(data.completedLessons || []));
      setQuestionsLeft(data.questionsLeft !== undefined ? data.questionsLeft : 10);
      setChatMessages(data.chatMessages || []);
      setTotalStudyTime(data.totalStudyTime || 0);
      setDailyProgress(data.dailyProgress || {});
      setWeeklyStreak(data.weeklyStreak || 0);
      setCurrentLesson(data.currentLesson || 0);
      setDarkMode(data.darkMode || false);
      setStudySchedule(data.studySchedule || null);
      setMissedDays(data.missedDays || []);
      setLessonQuizzes(data.lessonQuizzes || {});
      setLastActivityDate(data.lastActivityDate || null);
      setAutoStartPomodoro(data.autoStartPomodoro || false); // Restore auto-start preference
      setUserPoints(data.userPoints || 0); // NEW: Restore points
      setUserBadges(data.userBadges || []); // NEW: Restore badges
      setCourseAnalytics(data.courseAnalytics || {}); // NEW: Restore analytics
      setStudyTimeSlots(data.studyTimeSlots || []); // NEW: Restore study schedule
      setVocabularyLists(data.vocabularyLists || []); // NEW: Restore vocabulary
      setCurrentLanguage(data.currentLanguage || 'en'); // NEW: Restore language
      setUploadedNotes(data.uploadedNotes || []); // NEW: Restore notes
      setSatTestResults(data.satTestResults || []); // NEW: Restore SAT test results
      
      // Initialize buffer states
      // setLearningGoalBuffer is now local to OnboardingPage
      setChatInputBuffer('');
    }
  };

  // Initialize data on app load
// Replace the existing initialization useEffect with this updated version
useEffect(() => {
  // Initialize demo account in localStorage if it doesn't exist
  const users = loadFromStorage('users', {});
  if (!users['demo-user-123']) {
    const demoUser = {
      id: 'demo-user-123',
      email: 'demo@nalemstudy.com',
      password: 'demo123',
      name: 'Demo User',
      profile: {
        streak: 5,
        totalGoals: 2,
        completedLessons: 8,
        joinDate: '2024-01-15',
        preferences: { darkMode: false, notifications: true }
      },
      sessionData: {
        userGoals: [
          {
            id: 1704067200000,
            title: "Learn Python Programming Fundamentals",
            duration: "21 days",
            description: "Master Python basics for web development and automation",
            createdAt: "2024-01-01T00:00:00.000Z",
            progress: 35,
            completedLessons: [0, 1],
            schedule: {
              weekly_pattern: {
                Monday: { active: true, recommended_time: '1-2 hours', flexibility: 'moderate' },
                Wednesday: { active: true, recommended_time: '1-2 hours', flexibility: 'moderate' },
                Friday: { active: true, recommended_time: '1-2 hours', flexibility: 'moderate' },
                Saturday: { active: true, recommended_time: '2-3 hours', flexibility: 'moderate' }
              },
              total_days: 4,
              estimated_completion: '21 days',
              adaptive: true
            },
            lessons: [
              {
                day: 1,
                title: "Python Setup and Variables",
                description: "Install Python and learn about variables, data types",
                duration: "2-3 hours",
                content: "Welcome to Python! Today you'll set up your development environment and learn the fundamental building blocks of Python programming.",
                tasks: ["Install Python 3.9+", "Set up VS Code", "Practice with variables"],
                resources: {
                  videos: [
                    {
                      title: "Python for Everybody - Course 1: Getting Started",
                      channel: "freeCodeCamp.org",
                      url: "https://youtube.com/watch?v=8DvywoWv6fI",
                      duration: "13:40:00"
                    }
                  ],
                  articles: [
                    {
                      title: "Python Beginner's Guide",
                      source: "Python.org",
                      url: "https://docs.python.org/3/tutorial/"
                    }
                  ],
                  books: [
                    {
                      title: "Automate the Boring Stuff with Python",
                      author: "Al Sweigart",
                      url: "https://automatetheboringstuff.com/",
                      pages: "1-25"
                    }
                  ],
                  practice: [
                    {
                      title: "Python Basics Exercises",
                      source: "HackerRank",
                      url: "https://hackerrank.com/domains/python",
                      problems: ["Say Hello World", "Python If-Else", "Arithmetic Operators"]
                    }
                  ]
                }
              },
              {
                day: 2,
                title: "Control Flow and Functions", 
                description: "Master if statements, loops, and creating functions",
                duration: "3-4 hours",
                content: "Learn how to control program flow with conditional statements and loops, then organize your code with functions.",
                tasks: ["Write if/else statements", "Create for and while loops", "Define custom functions"],
                resources: {
                  videos: [
                    {
                      title: "Python Functions Tutorial",
                      channel: "Corey Schafer", 
                      url: "https://youtube.com/watch?v=9Os0o3wzS_I",
                      duration: "22:00"
                    }
                  ],
                  articles: [
                    {
                      title: "Python Control Flow",
                      source: "Real Python",
                      url: "https://realpython.com/python-conditional-statements/"
                    }
                  ],
                  practice: [
                    {
                      title: "Control Flow Exercises",
                      source: "Codewars",
                      url: "https://codewars.com/kata/search/python",
                      problems: ["FizzBuzz", "Grade Calculator", "Number Guessing Game"]
                    }
                  ]
                }
              }
            ]
          }
        ],
        completedLessons: [0, 1],
        progress: 35,
        questionsLeft: 7,
        totalStudyTime: 7200000,
        dailyProgress: {
          [new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]]: 1,
          [new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]]: 2,
          [new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]]: 1,
          [new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]]: 2,
          [new Date().toISOString().split('T')[0]]: 1
        },
        weeklyStreak: 5,
        lessonQuizzes: {
          0: {
            questions: [
              {
                question: "What is the correct way to install Python on your system?",
                options: ["Download from python.org", "Use npm install", "Install via Microsoft Store only", "Copy files manually"],
                correct: 0,
                explanation: {
                  correct_reason: "python.org provides the official, most up-to-date Python installer for all operating systems",
                  incorrect_reasons: {
                    "1": "npm is for Node.js packages, not Python installation",
                    "2": "Microsoft Store is one option but not the primary recommendation",
                    "3": "Manual file copying doesn't properly configure Python"
                  },
                  concept: "Python installation best practices"
                }
              }
            ]
          }
        },
        chatMessages: [
          {
            type: 'ai',
            content: 'Welcome! I\'m your AI tutor. Feel free to ask me any questions about your Python learning journey!',
            timestamp: new Date('2024-01-15T10:00:00')
          }
        ],
        autoStartPomodoro: false,
        userPoints: 2350,
        userBadges: [
          { id: 'study_novice', earnedAt: '2024-01-10T10:00:00.000Z' },
          { id: 'lesson_starter', earnedAt: '2024-01-11T14:30:00.000Z' },
          { id: 'lesson_explorer', earnedAt: '2024-01-14T16:45:00.000Z' },
          { id: 'streak_starter', earnedAt: '2024-01-13T09:15:00.000Z' }
        ],
        courseAnalytics: {
          1704067200000: {
            totalStudyTime: 7200000,
            lessonCompletionTimes: [3600000, 4200000],
            quizScores: [85, 92],
            averageQuizScore: 88.5,
            strongTopics: ['Variables', 'Basic Syntax'],
            improvementAreas: ['Functions', 'Loops'],
            studyPattern: 'evening_learner',
            consistency: 0.8
          }
        }
      }
    };
    
    users['demo-user-123'] = demoUser;
    saveToStorage('users', users);
  }

  // Check for existing Supabase session
  const initializeAuth = async () => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('Error getting session:', error);
        return;
      }

      if (session?.user) {
        // User is logged in with Supabase
        const userData = await loadUserData(session.user.id);
        if (userData) {
          setCurrentUser(userData);
          setIsAuthenticated(true);
          restoreUserSession(userData);
          setCurrentView(userData.sessionData?.currentView || 'dashboard');
        }
      } else {
        // Check for saved demo session in localStorage
        const savedAuth = loadFromStorage('currentUser', null);
        if (savedAuth && savedAuth.id === 'demo-user-123') {
          const userData = await loadUserData(savedAuth.id);
          if (userData) {
            setCurrentUser(userData);
            setIsAuthenticated(true);
            restoreUserSession(userData);
            setCurrentView(userData.sessionData?.currentView || 'dashboard');
          }
        }
      }
    } catch (error) {
      console.error('Error initializing auth:', error);
    }
  };

  initializeAuth();

  // Listen for auth state changes
  // Listen for auth state changes - REPLACE THE EXISTING onAuthStateChange SECTION WITH THIS
const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
  if (event === 'SIGNED_OUT') {
    // Handle sign out
    setIsAuthenticated(false);
    setCurrentUser(null);
    setCurrentView('landing');
  } else if (event === 'SIGNED_IN' && session?.user) {
    // Handle sign in (both email/password and Google)
    console.log('User signed in:', session.user.id, 'Provider:', session.user.app_metadata.provider);
    
    try {
      let userData = await loadUserData(session.user.id);
      
      // If no user data exists (first time Google sign in), create profile
      if (!userData) {
        console.log('Creating new user profile for:', session.user.email);
        
        // Extract name from Google user data or use email
        const userName = session.user.user_metadata?.full_name || 
                        session.user.user_metadata?.name || 
                        session.user.email?.split('@')[0] || 
                        'User';
        
        const newUserData = {
          id: session.user.id,
          email: session.user.email,
          name: userName,
          profile: {
            streak: 0,
            totalGoals: 0,
            completedLessons: 0,
            joinDate: new Date().toISOString().split('T')[0],
            preferences: { darkMode: false, notifications: true }
          },
          sessionData: {}
        };
        
        // Save the new user profile
        await saveUserData(session.user.id, newUserData);
        userData = newUserData;
        
        console.log('New user profile created successfully');
      }
      
      // Set user as authenticated and restore session
      setCurrentUser(userData);
      setIsAuthenticated(true);
      restoreUserSession(userData);
      setCurrentView(userData.sessionData?.currentView || 'dashboard');
      
      console.log('User authenticated and redirected to dashboard');
      
    } catch (error) {
      console.error('Error handling sign in:', error);
      alert('Error setting up your account. Please try again.');
    }
  }
});

  // Cleanup subscription on unmount
  return () => {
    subscription.unsubscribe();
  };
}, []);

  // Auto-save user session periodically
  // Replace the existing auto-save useEffect that runs every 30 seconds with this
useEffect(() => {
  if (!currentUser) return;
  
  const interval = setInterval(async () => {
    await saveCurrentSession();
  }, 30000); // Save every 30 seconds

  return () => clearInterval(interval);
}, [currentUser]); // Only depend on currentUser

// Replace the existing debounced save useEffect with this
useEffect(() => {
  if (!currentUser) return;
  
  const timeoutId = setTimeout(async () => {
    await saveCurrentSession();
  }, 2000); // Debounce saves by 2 seconds

  return () => clearTimeout(timeoutId);
}, [currentRoadmap, userGoals, completedLessons, progress, autoStartPomodoro, userPoints, userBadges, courseAnalytics, studyTimeSlots, vocabularyLists, currentLanguage, uploadedNotes]);

  useEffect(() => {
    if (chatMessages.length > 0) {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages]);

  useEffect(() => {
    if (currentSession.start) {
      const interval = setInterval(() => {
        setCurrentSession(prev => ({
          ...prev,
          duration: Date.now() - prev.start
        }));
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [currentSession.start]);

  // Update current date every minute and check for missed days
  useEffect(() => {
    const updateDate = () => {
      setCurrentDate(new Date());
      checkForMissedDays();
    };
    
    updateDate(); // Initial call
    const interval = setInterval(updateDate, 60000); // Update every minute
    
    return () => clearInterval(interval);
  }, [dailyProgress, studySchedule]);

  // Calculate accurate streak based on consecutive days
  useEffect(() => {
    calculateStreak();
  }, [dailyProgress]);

  // Pomodoro Timer Effect
  useEffect(() => {
    let interval = null;
    if (pomodoroActive && pomodoroTime > 0) {
      interval = setInterval(() => {
        setPomodoroTime(time => time - 1);
      }, 1000);
    } else if (pomodoroTime === 0) {
      handlePomodoroComplete();
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [pomodoroActive, pomodoroTime]);

  // Load user points and update streak on login
  useEffect(() => {
    if (currentUser && currentUser.id) {
      loadUserPoints();
      updateUserStreak();
    }
  }, [currentUser]);

  const loadUserPoints = async () => {
    if (currentUser && currentUser.id) {
      const points = await pointsSystem.getUserPoints(currentUser.id);
      if (points) {
        setUserPointsData(points);
      }
    }
  };

  const updateUserStreak = async () => {
    if (currentUser && currentUser.id) {
      await pointsSystem.updateUserStreak(currentUser.id);
      await loadUserPoints(); // Reload points after updating streak
    }
  };

  // Quiz Question Timer Effect
  useEffect(() => {
    if (questionStartTime && currentQuiz && !showQuizResults) {
      const interval = setInterval(() => {
        const timeSpent = (Date.now() - questionStartTime) / 1000;
        const estimatedTimePerQuestion = quizEstimatedTime / (currentQuiz.questions?.length || 1);
        const timeThreshold = estimatedTimePerQuestion * 1.5; // 50% over estimated time
        
        if (timeSpent > timeThreshold && !helpOffered[currentQuestionIndex]) {
          setHelpOffered(prev => ({ ...prev, [currentQuestionIndex]: true }));
          offerQuestionHelp(currentQuestionIndex);
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [questionStartTime, currentQuestionIndex, currentQuiz, showQuizResults, quizEstimatedTime, helpOffered]);

  const handlePomodoroComplete = () => {
    setPomodoroActive(false);
    
    if (pomodoroType === 'work') {
      // Work session complete, start break
      const isLongBreak = pomodoroSession % customPomodoroSettings.sessionsUntilLongBreak === 0;
      const breakDuration = isLongBreak ? customPomodoroSettings.longBreak : customPomodoroSettings.shortBreak;
      
      setPomodoroTime(breakDuration * 60);
      setPomodoroType('break');
      
      // Show completion notification
      const message = {
        type: 'ai',
        content: `🍅 Work session complete! Time for a ${isLongBreak ? 'long' : 'short'} break. You've completed ${pomodoroSession} session${pomodoroSession > 1 ? 's' : ''} today.`,
        timestamp: new Date(),
        isSystemMessage: true
      };
      setChatMessages(prev => [...prev, message]);
    } else {
      // Break complete, ready for next work session
      setPomodoroTime(customPomodoroSettings.workDuration * 60);
      setPomodoroType('work');
      setPomodoroSession(prev => prev + 1);
      
      const message = {
        type: 'ai',
        content: `✨ Break time over! Ready to start your next focused learning session? Click the timer to begin.`,
        timestamp: new Date(),
        isSystemMessage: true
      };
      setChatMessages(prev => [...prev, message]);
    }
  };

  const startPomodoro = () => {
    setPomodoroActive(true);
    if (pomodoroType === 'work') {
      startSession(); // Start study session tracking
    }
  };

  const pausePomodoro = () => {
    setPomodoroActive(false);
  };

  const resetPomodoro = () => {
    setPomodoroActive(false);
    setPomodoroTime(pomodoroType === 'work' ? customPomodoroSettings.workDuration * 60 : customPomodoroSettings.shortBreak * 60);
  };

  // NEW: Auto-start Pomodoro when entering lesson
  const autoStartPomodoroOnLesson = () => {
    if (autoStartPomodoro && pomodoroType === 'work' && !pomodoroActive) {
      // Add a small delay to let the lesson page load
      setTimeout(() => {
        setPomodoroActive(true);
        startSession();
        
        // Show notification
        const message = {
          type: 'ai',
          content: `🍅 Auto-started your ${customPomodoroSettings.workDuration}-minute focus session! Happy learning!`,
          timestamp: new Date(),
          isSystemMessage: true
        };
        setChatMessages(prev => [...prev, message]);
      }, 1500);
    }
  };

  const offerQuestionHelp = (questionIndex) => {
    setHelpQuestion(questionIndex);
    setShowHelpModal(true);
  };

  const acceptHelp = () => {
    if (helpQuestion !== null) {
      // Mark question as needing help (will be marked wrong)
      setNeedsHelp(prev => ({ ...prev, [helpQuestion]: true }));
      setQuizAnswers(prev => ({ ...prev, [helpQuestion]: -1 })); // -1 indicates help was requested
      setShowHelpModal(false);
      setHelpQuestion(null);
    }
  };

  const declineHelp = () => {
    setShowHelpModal(false);
    setHelpQuestion(null);
  };

  const startQuizQuestion = (questionIndex) => {
    setCurrentQuestionIndex(questionIndex);
    setQuestionStartTime(Date.now());
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const calculateStreak = () => {
    const sortedDates = Object.keys(dailyProgress).sort().reverse();
    if (sortedDates.length === 0) {
      setWeeklyStreak(0);
      return;
    }

    let streak = 0;
    const today = new Date().toISOString().split('T')[0];
    let checkDate = new Date(today);

    // Check if user studied today or yesterday (allow for timezone differences)
    const hasStudiedToday = sortedDates.includes(today);
    const yesterday = new Date(checkDate);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toISOString().split('T')[0];
    const hasStudiedYesterday = sortedDates.includes(yesterdayStr);

    if (!hasStudiedToday && !hasStudiedYesterday) {
      setWeeklyStreak(0);
      return;
    }

    // Start from today or yesterday
    if (!hasStudiedToday) {
      checkDate = yesterday;
    }

    // Count consecutive days
    while (true) {
      const dateStr = checkDate.toISOString().split('T')[0];
      if (sortedDates.includes(dateStr)) {
        streak++;
        checkDate.setDate(checkDate.getDate() - 1);
      } else {
        break;
      }
    }

    setWeeklyStreak(streak);
  };

  const checkForMissedDays = () => {
    if (!studySchedule || !currentRoadmap) return;
    
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    
    // Get the last study date
    const studyDates = Object.keys(dailyProgress).sort();
    const lastStudyDate = studyDates[studyDates.length - 1];
    
    if (!lastStudyDate) return;
    
    const daysSinceLastStudy = Math.floor((today - new Date(lastStudyDate)) / (1000 * 60 * 60 * 24));
    
    // Check if user missed scheduled study days
    if (daysSinceLastStudy > 1) {
      const missedDaysList = [];
      for (let i = 1; i < daysSinceLastStudy; i++) {
        const missedDate = new Date(today);
        missedDate.setDate(missedDate.getDate() - i);
        const dayOfWeek = missedDate.getDay();
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        
        if (studySchedule.weekly_pattern[dayNames[dayOfWeek]]?.active) {
          missedDaysList.push(missedDate.toISOString().split('T')[0]);
        }
      }
      
      if (missedDaysList.length > 0) {
        setMissedDays(prev => [...new Set([...prev, ...missedDaysList])]);
        if (missedDaysList.length >= 2) {
          adaptLearningPlan(missedDaysList.length);
        }
      }
    }
  };

  // NEW: Test Prep Detection and Specialized Generation
  const detectTestPrepGoal = (goalText) => {
    const lowerGoal = goalText.toLowerCase();
    for (const [testType, keywords] of Object.entries(TEST_PREP_KEYWORDS)) {
      if (keywords.some(keyword => lowerGoal.includes(keyword)) || lowerGoal.includes(testType)) {
        return testType.toUpperCase();
      }
    }
    return null;
  };

  const generateTestPrepRoadmap = async (testType, specificGoal) => {
    const testConfigs = {
      'SAT': {
        duration: '12 weeks',
        practiceTests: 8,
        sections: ['Math', 'Evidence-Based Reading', 'Writing and Language'],
        totalTime: 180, // 3 hours
        description: 'Comprehensive SAT preparation with full-length practice tests and targeted skill building'
      },
      'ACT': {
        duration: '10 weeks', 
        practiceTests: 6,
        sections: ['English', 'Math', 'Reading', 'Science'],
        totalTime: 175, // 2 hours 55 minutes
        description: 'Complete ACT prep with timed practice tests and section-specific strategies'
      },
      'AP': {
        duration: '16 weeks',
        practiceTests: 4,
        sections: ['Multiple Choice', 'Free Response'],
        totalTime: 180, // 3 hours (varies by subject)
        description: 'AP exam preparation with college-level content mastery and test strategies'
      },
      'NEET': {
        duration: '24 weeks',
        practiceTests: 12,
        sections: ['Physics', 'Chemistry', 'Biology'],
        totalTime: 180, // 3 hours
        description: 'Complete NEET preparation covering all medical entrance exam topics'
      },
      'GRE': {
        duration: '8 weeks',
        practiceTests: 6,
        sections: ['Verbal Reasoning', 'Quantitative Reasoning', 'Analytical Writing'],
        totalTime: 225, // 3 hours 45 minutes
        description: 'Graduate school preparation with advanced reasoning and analytical skills'
      }
    };

    const config = testConfigs[testType] || testConfigs['SAT'];
    
    try {
      const messages = [
        {
          role: 'system',
          content: `Create a comprehensive ${testType} test preparation roadmap with REAL practice tests. Include actual full-length practice tests that match the real exam format, timing, and difficulty. Structure as JSON with this format:
          {
            "title": "${testType} Test Preparation",
            "duration": "${config.duration}",
            "description": "${config.description}",
            "testType": "${testType}",
            "practiceTests": [],
            "lessons": []
          }
          
          Include ${config.practiceTests} full-length practice tests with real timing (${config.totalTime} minutes total) and authentic questions for each section: ${config.sections.join(', ')}.`
        },
        {
          role: 'user',
          content: `Create a detailed ${testType} preparation roadmap for: "${specificGoal}". Include:
          1. ${config.practiceTests} full-length practice tests with real exam timing
          2. Section-specific preparation lessons
          3. Test-taking strategies and tips
          4. Score improvement techniques
          5. Real practice questions and explanations
          Make it comprehensive and exam-focused.`
        }
      ];

      const response = await makeOpenAIRequest(messages, 4000);
      const roadmapData = JSON.parse(response);
      
      // Add practice test metadata
      roadmapData.practiceTests = Array.from({length: config.practiceTests}, (_, i) => ({
        id: i + 1,
        title: `${testType} Practice Test ${i + 1}`,
        totalTime: config.totalTime,
        sections: config.sections.map(section => ({
          name: section,
          questions: testType === 'SAT' ? (section === 'Math' ? 58 : 52) : 
                    testType === 'ACT' ? (section === 'English' ? 75 : section === 'Math' ? 60 : section === 'Reading' ? 40 : 40) :
                    25,
          timeLimit: Math.floor(config.totalTime / config.sections.length)
        })),
        completed: false,
        score: null
      }));
      
      return roadmapData;
    } catch (error) {
      console.error('Error generating test prep roadmap:', error);
      return createFallbackTestPrepRoadmap(testType, config, specificGoal);
    }
  };

  const createFallbackTestPrepRoadmap = (testType, config, specificGoal) => {
    return {
      id: Date.now(),
      title: `${testType} Test Preparation`,
      duration: config.duration,
      description: config.description,
      testType: testType,
      createdAt: new Date().toISOString(),
      progress: 0,
      completedLessons: [],
      schedule: studySchedule,
      practiceTests: Array.from({length: config.practiceTests}, (_, i) => ({
        id: i + 1,
        title: `${testType} Practice Test ${i + 1}`,
        totalTime: config.totalTime,
        sections: config.sections.map(section => ({
          name: section,
          questions: 25,
          timeLimit: Math.floor(config.totalTime / config.sections.length)
        })),
        completed: false,
        score: null
      })),
      lessons: [
        {
          day: 1,
          title: `${testType} Test Overview and Strategy`,
          description: `Understanding the ${testType} format, timing, and scoring`,
          duration: "2-3 hours",
          content: `Master the ${testType} exam with proven strategies and comprehensive preparation.`,
          tasks: [`Learn ${testType} format`, "Practice time management", "Set target score"],
          resources: {
            videos: [{"title": `${testType} Complete Guide`, "channel": "Khan Academy", "url": "https://youtube.com/watch?v=example", "duration": "45:00"}],
            articles: [{"title": `Official ${testType} Guide`, "source": "College Board", "url": "https://collegeboard.org"}],
            practice: [{"title": `${testType} Practice Questions`, "source": "Official Practice", "url": "https://practice.collegeboard.org", "problems": ["Section 1", "Section 2"]}]
          }
        },
        {
          day: 7,
          title: "First Practice Test",
          description: "Take your first full-length practice test",
          duration: `${config.totalTime} minutes`,
          content: "Complete a full practice test under timed conditions to establish your baseline score.",
          tasks: ["Complete full practice test", "Review answers", "Identify weak areas"],
          practiceTest: true,
          testId: 1
        }
      ]
    };
  };

  // NEW: Gamification Functions
  const awardPoints = (activity, amount) => {
    const newPoints = userPoints + amount;
    setUserPoints(newPoints);
    
    // Check for new badges
    checkForNewBadges(activity, amount, newPoints);
    
    // Show points notification
    const message = {
      type: 'ai',
      content: `🎉 +${amount} points earned for ${activity}! Total: ${newPoints} points`,
      timestamp: new Date(),
      isSystemMessage: true
    };
    setChatMessages(prev => [...prev, message]);
  };

  const checkForNewBadges = (activity, amount, totalPoints) => {
    const newBadges = [];
    
    // Check study time badges
    if (activity === 'study_time') {
      BADGE_SYSTEM.study_time.forEach(badge => {
        if (totalStudyTime >= badge.threshold && !userBadges.find(b => b.id === badge.id)) {
          newBadges.push(badge);
        }
      });
    }
    
    // Check lesson badges
    if (activity === 'lesson_completion') {
      BADGE_SYSTEM.lessons.forEach(badge => {
        if (completedLessons.size >= badge.threshold && !userBadges.find(b => b.id === badge.id)) {
          newBadges.push(badge);
        }
      });
    }
    
    // Check streak badges
    if (activity === 'daily_study') {
      BADGE_SYSTEM.streaks.forEach(badge => {
        if (weeklyStreak >= badge.threshold && !userBadges.find(b => b.id === badge.id)) {
          newBadges.push(badge);
        }
      });
    }
    
    // Check course badges
    if (activity === 'course_completion') {
      const completedCourses = userGoals.filter(goal => (goal.progress || 0) >= 100).length;
      BADGE_SYSTEM.courses.forEach(badge => {
        if (completedCourses >= badge.threshold && !userBadges.find(b => b.id === badge.id)) {
          newBadges.push(badge);
        }
      });
    }
    
    // Award new badges
    if (newBadges.length > 0) {
      const updatedBadges = [...userBadges, ...newBadges.map(badge => ({
        id: badge.id,
        earnedAt: new Date().toISOString()
      }))];
      setUserBadges(updatedBadges);
      
      // Show badge notification
      newBadges.forEach(badge => {
        setNewBadgeEarned(badge);
        setShowBadgeModal(true);
      });
    }
  };

  // NEW: Analytics Generation
  const generateCourseAnalytics = async (roadmapId) => {
    const roadmap = userGoals.find(g => g.id === roadmapId);
    if (!roadmap) return null;

    const analytics = courseAnalytics[roadmapId] || {};
    
    // Calculate comprehensive analytics
    const completedLessonsCount = (roadmap.completedLessons || []).length;
    const totalLessons = roadmap.lessons?.length || 1;
    const completionRate = (completedLessonsCount / totalLessons) * 100;
    
    // Generate AI insights
    try {
      const messages = [
        {
          role: 'system',
          content: 'You are an AI learning analyst. Provide detailed, encouraging insights about the student\'s learning journey with specific recommendations for improvement.'
        },
        {
          role: 'user',
          content: `Analyze this learning data and provide insights:
          Course: ${roadmap.title}
          Completion Rate: ${completionRate}%
          Study Time: ${Math.round(totalStudyTime / 3600000)} hours
          Quiz Average: ${analytics.averageQuizScore || 'N/A'}%
          Lessons Completed: ${completedLessonsCount}/${totalLessons}
          Streak: ${weeklyStreak} days
          
          Provide encouraging insights and specific recommendations for continued learning.`
        }
      ];

      const aiInsights = await makeOpenAIRequest(messages, 800);
      
      return {
        ...analytics,
        courseTitle: roadmap.title,
        completionRate,
        totalStudyTime,
        lessonsCompleted: completedLessonsCount,
        totalLessons,
        currentStreak: weeklyStreak,
        aiInsights,
        completedAt: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error generating analytics:', error);
      return {
        ...analytics,
        courseTitle: roadmap.title,
        completionRate,
        totalStudyTime,
        lessonsCompleted: completedLessonsCount,
        totalLessons,
        currentStreak: weeklyStreak,
        aiInsights: "Congratulations on your learning progress! Keep up the great work and continue building on your achievements.",
        completedAt: new Date().toISOString()
      };
    }
  };

  // NEW: Study Scheduling Functions
  const addStudyTimeSlot = (courseId, courseName, dayOfWeek, startTime, endTime) => {
    const newSlot = {
      id: Date.now(),
      courseId,
      courseName,
      dayOfWeek,
      startTime,
      endTime,
      active: true,
      createdAt: new Date().toISOString()
    };
    
    setStudyTimeSlots(prev => [...prev, newSlot]);
    
    // Award points for scheduling
    awardPoints('study_scheduling', 10);
    
    // Show confirmation
    const message = {
      type: 'ai',
      content: `📅 Study session scheduled for ${courseName} on ${dayOfWeek} from ${startTime} to ${endTime}!`,
      timestamp: new Date(),
      isSystemMessage: true
    };
    setChatMessages(prev => [...prev, message]);
  };

  const removeStudyTimeSlot = (slotId) => {
    setStudyTimeSlots(prev => prev.filter(slot => slot.id !== slotId));
  };

  // NEW: AI-Powered Vocabulary Extraction
  const extractVocabularyWithAI = async (textContent, fileName) => {
    try {
      setAiLoading(true);
      
      // Limit content length to avoid token limits
      const maxContentLength = 4000;
      const truncatedContent = textContent.length > maxContentLength 
        ? textContent.substring(0, maxContentLength) + "..."
        : textContent;
      
      console.log('Extracting vocabulary from content length:', textContent.length);
      console.log('Content preview:', truncatedContent.substring(0, 200));
      
      const messages = [
        {
          role: "system",
          content: "You are an expert educational content analyzer specializing in vocabulary extraction for flashcards. Your task is to carefully analyze text content and identify the most important vocabulary words that students need to learn. Focus on key terms, concepts, technical terms, and academic vocabulary that are essential for understanding the content. You must extract exactly 20-25 vocabulary words."
        },
        {
          role: "user",
          content: `Carefully analyze the following text content and extract exactly 20-25 of the most important vocabulary words for creating educational flashcards. 

IMPORTANT INSTRUCTIONS:
- Extract EXACTLY 20-25 vocabulary words (not fewer)
- Focus on words that are essential for understanding the content
- Include technical terms, academic vocabulary, key concepts, and important terminology
- Each word should be a single term or short phrase (1-3 words max)
- Provide clear, concise definitions that a student can understand
- Use examples from the actual content when possible

Text content to analyze:
${truncatedContent}

Return your response as a JSON object with this EXACT structure:
{
  "vocabulary": [
    {
      "word": "single word or short phrase",
      "definition": "clear, concise definition in simple language",
      "example": "example sentence using the word in context",
      "difficulty": "easy|medium|hard",
      "category": "subject category"
    }
  ]
}

CRITICAL REQUIREMENTS:
1. Extract EXACTLY 20-25 vocabulary words (minimum 20, maximum 25)
2. Each word must be essential for understanding the content
3. Definitions must be clear and educational
4. Examples should relate to the original content
5. Assign appropriate difficulty levels
6. Categorize by subject area

Return ONLY the JSON object, no additional text or explanations.`
        }
      ];

      console.log('Sending request to AI with content length:', truncatedContent.length);
      const response = await makeOpenAIRequest(messages, 4000);
      
      if (!response) {
        throw new Error('No response from AI service');
      }

      console.log('AI response received, length:', response.length);
      console.log('AI response preview:', response.substring(0, 300));

      // Parse the AI response
      let aiData;
      try {
        // Clean the response to ensure it's valid JSON
        let cleanedResponse = response.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
        
        // Remove any text before the first { and after the last }
        const firstBrace = cleanedResponse.indexOf('{');
        const lastBrace = cleanedResponse.lastIndexOf('}');
        if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
          cleanedResponse = cleanedResponse.substring(firstBrace, lastBrace + 1);
        }
        
        console.log('Cleaned response for parsing:', cleanedResponse.substring(0, 200));
        aiData = JSON.parse(cleanedResponse);
      } catch (parseError) {
        console.error('Failed to parse AI response:', parseError);
        console.error('Raw response:', response);
        throw new Error('AI response was not valid JSON format');
      }

      if (!aiData.vocabulary || !Array.isArray(aiData.vocabulary)) {
        console.error('AI response structure:', aiData);
        throw new Error('AI response did not contain vocabulary array');
      }

      console.log('AI extracted vocabulary count:', aiData.vocabulary.length);

      // Validate and clean the vocabulary data
      const validatedWords = aiData.vocabulary
        .filter(item => item.word && item.definition && item.word.trim().length > 0)
        .map(item => ({
          word: item.word.trim(),
          definition: item.definition.trim(),
          example: item.example ? item.example.trim() : '',
          difficulty: ['easy', 'medium', 'hard'].includes(item.difficulty) ? item.difficulty : 'medium',
          category: item.category ? item.category.trim() : 'general'
        }));

      console.log('Validated words count:', validatedWords.length);
      console.log('Sample words:', validatedWords.slice(0, 3));

      if (validatedWords.length === 0) {
        throw new Error('No valid vocabulary words were extracted');
      }

      if (validatedWords.length < 5) {
        console.warn('Only extracted', validatedWords.length, 'words, which is fewer than expected');
      }

      setAiLoading(false);
      return validatedWords;

    } catch (error) {
      console.error('AI vocabulary extraction failed:', error);
      setAiLoading(false);
      throw error;
    }
  };

  // NEW: Enhanced Vocabulary System Functions
  const uploadVocabularyList = async (file) => {
    try {
      const text = await file.text();
      
      // Check if it's a CSV format (comma-separated word,definition,example)
      const lines = text.split('\n').filter(line => line.trim());
      const isCSVFormat = lines.some(line => {
        const parts = line.split(',').map(part => part.trim());
        return parts.length >= 2 && parts[0] && parts[1];
      });

      let words = [];

      if (isCSVFormat) {
        // Handle traditional CSV format
        lines.forEach(line => {
          const parts = line.split(',').map(part => part.trim());
          if (parts.length >= 2) {
            words.push({
              word: parts[0],
              definition: parts[1],
              example: parts[2] || '',
              difficulty: parts[3] || 'medium',
              category: parts[4] || 'general'
            });
          }
        });
      } else {
        // Use AI to extract vocabulary from plain text
        try {
          words = await extractVocabularyWithAI(text, file.name);
          console.log('AI extraction successful, got', words.length, 'words');
        } catch (aiError) {
          console.error('AI extraction failed, falling back to enhanced manual parsing:', aiError);
          
          // Enhanced fallback: try to extract meaningful words from text
          const wordMatches = text.match(/\b[A-Za-z]{4,}\b/g) || [];
          
          // Count word frequency to find important words
          const wordCounts = {};
          wordMatches.forEach(word => {
            const lowerWord = word.toLowerCase();
            if (lowerWord.length >= 4 && !['this', 'that', 'with', 'from', 'they', 'have', 'been', 'were', 'said', 'each', 'which', 'their', 'time', 'will', 'about', 'there', 'could', 'other', 'after', 'first', 'well', 'also', 'where', 'much', 'some', 'very', 'when', 'here', 'just', 'into', 'over', 'think', 'back', 'years', 'work', 'such', 'make', 'good', 'most', 'even', 'new', 'want', 'because', 'any', 'these', 'give', 'day', 'may', 'say', 'use', 'man', 'find', 'great', 'before', 'move', 'right', 'boy', 'old', 'too', 'same', 'tell', 'does', 'set', 'three', 'small', 'large', 'big', 'high', 'different', 'important', 'every', 'near', 'add', 'food', 'between', 'own', 'below', 'country', 'plant', 'last', 'school', 'father', 'keep', 'tree', 'never', 'start', 'city', 'earth', 'eye', 'light', 'thought', 'head', 'under', 'story', 'saw', 'left', 'dont', 'few', 'while', 'along', 'might', 'close', 'something', 'seem', 'next', 'hard', 'open', 'example', 'begin', 'life', 'always', 'those', 'both', 'paper', 'together', 'got', 'group', 'often', 'run', 'important', 'until', 'children', 'side', 'feet', 'car', 'miles', 'night', 'walked', 'white', 'sea', 'began', 'grew', 'took', 'river', 'four', 'carry', 'state', 'once', 'book', 'hear', 'stop', 'without', 'second', 'later', 'miss', 'idea', 'enough', 'eat', 'face', 'watch', 'far', 'indian', 'real', 'almost', 'let', 'above', 'girl', 'sometimes', 'mountain', 'cut', 'young', 'talk', 'soon', 'list', 'song', 'leave', 'family', 'it\'s'].includes(lowerWord)) {
              wordCounts[lowerWord] = (wordCounts[lowerWord] || 0) + 1;
            }
          });
          
          // Get most frequent words
          const sortedWords = Object.entries(wordCounts)
            .sort(([,a], [,b]) => b - a)
            .slice(0, 25)
            .map(([word]) => word);
          
          words = sortedWords.map(word => ({
            word: word,
            definition: `Definition for ${word} (please add manually)`,
            example: '',
            difficulty: 'medium',
            category: 'general'
          }));
          
          console.log('Fallback extraction got', words.length, 'words');
        }
      }
      
      if (words.length > 0) {
        const newVocabList = {
          id: Date.now(),
          name: file.name.replace('.csv', '').replace('.txt', '').replace('.pdf', ''),
          words: words,
          progress: 0,
          createdAt: new Date().toISOString(),
          aiGenerated: !isCSVFormat // Mark if generated by AI
        };
        
        setVocabularyLists(prev => [...prev, newVocabList]);
        awardPoints('vocabulary_upload', 25);
        
        // Show success message
        const message = isCSVFormat 
          ? `Successfully uploaded ${words.length} vocabulary words from CSV file!`
          : `AI successfully analyzed your content and created ${words.length} flashcards!`;
        alert(message);
        
        console.log('Vocabulary list created:', newVocabList);
        return newVocabList;
      } else {
        throw new Error('No vocabulary words could be extracted from the file');
      }
    } catch (error) {
      console.error('Error uploading vocabulary:', error);
      alert(`Error uploading vocabulary file: ${error.message}\n\nPlease ensure your file contains either:\n1. CSV format: word,definition,example\n2. Plain text content for AI analysis`);
    }
    return null;
  };

  // NEW: Enhance vocabulary list with AI-generated definitions
  const enhanceVocabularyWithAI = async (vocabList) => {
    try {
      setAiLoading(true);
      
      // Get words that need better definitions
      const wordsNeedingEnhancement = vocabList.words.filter(word => 
        !word.definition || 
        word.definition.includes('please add manually') ||
        word.definition.length < 10
      );

      if (wordsNeedingEnhancement.length === 0) {
        setAiLoading(false);
        return vocabList;
      }

      const messages = [
        {
          role: "system",
          content: "You are an expert educational content creator. Your task is to provide clear, concise, and educational definitions for vocabulary words that will be used in flashcards."
        },
        {
          role: "user",
          content: `Please provide better definitions for the following vocabulary words. For each word, provide a clear, concise definition and a relevant example sentence.

Words to enhance:
${wordsNeedingEnhancement.map(word => `- ${word.word}: ${word.definition || 'No definition provided'}`).join('\n')}

Return your response as a JSON object with this exact structure:
{
  "enhanced_words": [
    {
      "word": "term",
      "definition": "clear, concise definition",
      "example": "example sentence using the word in context",
      "difficulty": "easy|medium|hard",
      "category": "subject category"
    }
  ]
}

Requirements:
1. Provide clear, student-friendly definitions
2. Include relevant example sentences
3. Assign appropriate difficulty levels
4. Categorize words by subject area when possible
5. Keep definitions concise but informative

Only return valid JSON, no additional text.`
        }
      ];

      const response = await makeOpenAIRequest(messages, 2000);
      
      if (!response) {
        throw new Error('No response from AI service');
      }

      // Parse the AI response
      let aiData;
      try {
        const cleanedResponse = response.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
        aiData = JSON.parse(cleanedResponse);
      } catch (parseError) {
        console.error('Failed to parse AI response:', parseError);
        throw new Error('AI response was not valid JSON format');
      }

      if (!aiData.enhanced_words || !Array.isArray(aiData.enhanced_words)) {
        throw new Error('AI response did not contain enhanced_words array');
      }

      // Update the vocabulary list with enhanced definitions
      const enhancedWords = vocabList.words.map(word => {
        const enhanced = aiData.enhanced_words.find(ew => ew.word.toLowerCase() === word.word.toLowerCase());
        if (enhanced) {
          return {
            ...word,
            definition: enhanced.definition || word.definition,
            example: enhanced.example || word.example,
            difficulty: enhanced.difficulty || word.difficulty,
            category: enhanced.category || word.category
          };
        }
        return word;
      });

      const enhancedVocabList = {
        ...vocabList,
        words: enhancedWords,
        aiEnhanced: true
      };

      // Update the vocabulary lists state
      setVocabularyLists(prev => 
        prev.map(list => list.id === vocabList.id ? enhancedVocabList : list)
      );

      setAiLoading(false);
      return enhancedVocabList;

    } catch (error) {
      console.error('AI vocabulary enhancement failed:', error);
      setAiLoading(false);
      throw error;
    }
  };

  // NEW: Delete vocabulary list function
  const deleteVocabularyList = (vocabListId) => {
    if (window.confirm('Are you sure you want to delete this vocabulary list? This action cannot be undone.')) {
      setVocabularyLists(prev => prev.filter(list => list.id !== vocabListId));
      
      // If the deleted list was currently being used in flashcards or quiz, reset the state
      if (currentVocabList && currentVocabList.id === vocabListId) {
        setCurrentVocabList(null);
        setFlashcardMode(false);
        setVocabularyQuizMode(false);
        setCurrentFlashcard(0);
        setShowAnswer(false);
      }
      
      // Award points for cleanup (small amount)
      awardPoints('vocabulary_cleanup', 5);
    }
  };

  // NEW: Delete individual word from vocabulary list
  const deleteWordFromVocabularyList = (vocabListId, wordIndex) => {
    if (window.confirm('Are you sure you want to delete this word from the vocabulary list?')) {
      setVocabularyLists(prev => 
        prev.map(list => 
          list.id === vocabListId 
            ? { ...list, words: list.words.filter((_, index) => index !== wordIndex) }
            : list
        )
      );
      
      // Update current vocab list if it's the one being modified
      if (currentVocabList && currentVocabList.id === vocabListId) {
        const updatedList = vocabularyLists.find(list => list.id === vocabListId);
        if (updatedList) {
          const newWords = updatedList.words.filter((_, index) => index !== wordIndex);
          setCurrentVocabList({ ...updatedList, words: newWords });
          
          // Adjust current flashcard index if necessary
          if (currentFlashcard >= newWords.length) {
            setCurrentFlashcard(Math.max(0, newWords.length - 1));
          }
        }
      }
    }
  };

  const generateFillInBlankQuiz = (vocabList) => {
    const questions = vocabList.words.slice(0, 10).map(word => {
      const sentence = word.example || `The ${word.word} was very important.`;
      const blankedSentence = sentence.replace(new RegExp(word.word, 'gi'), '______');
      
      return {
        question: blankedSentence,
        correctAnswer: word.word,
        definition: word.definition,
        type: 'fill_in_blank'
      };
    });
    
    return {
      title: `Fill in the Blank: ${vocabList.name}`,
      questions: questions,
      vocabListId: vocabList.id
    };
  };

  // ENHANCED: Intelligent File Upload with Auto-Detection
  const uploadNotesFile = async (file) => {
    try {
      let content = '';
      let isPdf = false;
      let isPpt = false;
      const fileName = file.name.toLowerCase();

      // Extract content based on file type
      if (fileName.endsWith('.pdf')) {
        isPdf = true;
        try {
          console.log('Attempting PDF text extraction...');
          content = await extractPdfText(file);
          console.log('PDF extraction successful. Content length:', content.length);
          console.log('PDF content preview:', content.substring(0, 100));

          // Validate that we got actual text, not binary data
          if (content.includes('%PDF') || content.includes('\0') || content.length < 10) {
            throw new Error('PDF extraction returned binary data instead of text');
          }
        } catch (pdfError) {
          console.error('PDF extraction failed:', pdfError);
          alert(`Error processing PDF file: ${pdfError.message}\n\nPlease try one of these solutions:\n1. Use a text file (.txt) instead\n2. Copy and paste the text directly\n3. Ensure the PDF has selectable/extractable text (not scanned images)\n4. Try a different PDF file`);
          return null;
        }
      } else if (fileName.endsWith('.pptx') || fileName.endsWith('.ppt')) {
        isPpt = true;
        try {
          console.log('Attempting PowerPoint text extraction...');
          content = await extractPptText(file);
          console.log('PowerPoint extraction successful. Content length:', content.length);
          console.log('PowerPoint content preview:', content.substring(0, 100));

          // Validate that we got actual text
          if (content.length < 10) {
            throw new Error('PowerPoint extraction returned insufficient text');
          }
        } catch (pptError) {
          console.error('PowerPoint extraction failed:', pptError);
          alert(`Error processing PowerPoint file: ${pptError.message}\n\nPlease try one of these solutions:\n1. Ensure the file is in .pptx format (PowerPoint 2007 or later)\n2. Use a text file (.txt) instead\n3. Copy and paste the text directly from your slides\n4. Try a different PowerPoint file`);
          return null;
        }
      } else {
        content = await file.text();
        console.log('Text file content length:', content.length);
      }

      // INTELLIGENT ANALYSIS: Determine if content is vocabulary or notes
      console.log('Analyzing content type...');
      const contentType = await analyzeContentType(content);
      console.log('Content type detected:', contentType);

      if (contentType.type === 'vocabulary') {
        // Process as vocabulary list
        console.log('Processing as vocabulary list...');
        await processAsVocabulary(content, file.name);

        // Don't close the modal - flashcards will display inline
        const message = {
          type: 'ai',
          content: `✅ Vocabulary list created from "${file.name}" with ${contentType.wordCount || 'multiple'} words! View flashcards below.`,
          timestamp: new Date(),
          isSystemMessage: true
        };
        setChatMessages(prev => [...prev, message]);
      } else {
        // Process as notes - store for course generation
        const newNote = {
          id: Date.now(),
          name: file.name,
          content: content,
          uploadedAt: new Date().toISOString(),
          processed: false,
          isPdf: isPdf,
          isPpt: isPpt,
          fileSize: file.size,
          contentType: contentType.type
        };

        setUploadedNotes(prev => [...prev, newNote]);

        const message = {
          type: 'ai',
          content: `📄 Notes uploaded: "${file.name}". Click "Generate Course" to create a learning path from these notes.`,
          timestamp: new Date(),
          isSystemMessage: true
        };
        setChatMessages(prev => [...prev, message]);

        return newNote;
      }
    } catch (error) {
      console.error('Error uploading notes:', error);
      alert('Error uploading notes file.');
    }
    return null;
  };

  // AI-powered content type analysis
  const analyzeContentType = async (content) => {
    try {
      // First, do a quick heuristic check
      const lines = content.split('\n').filter(line => line.trim().length > 0);
      const avgLineLength = lines.reduce((sum, line) => sum + line.length, 0) / lines.length;

      // If average line length is very short and we have many short lines, likely vocabulary
      if (avgLineLength < 50 && lines.length > 5) {
        // Check if lines follow vocabulary pattern (word: definition or word - definition)
        const vocabPatternCount = lines.filter(line =>
          line.includes(':') || line.includes('-') || line.includes('–')
        ).length;

        if (vocabPatternCount / lines.length > 0.5) {
          return { type: 'vocabulary', confidence: 85, wordCount: lines.length };
        }
      }

      // If content has paragraphs (multiple sentences together), likely notes
      const paragraphPattern = /[.!?]\s+[A-Z]/g;
      const sentenceCount = (content.match(paragraphPattern) || []).length;

      if (sentenceCount > 10 && avgLineLength > 100) {
        return { type: 'notes', confidence: 90 };
      }

      // Use AI for ambiguous cases
      const messages = [
        {
          role: 'system',
          content: `You are a content analyzer. Determine if text content is:
1. A VOCABULARY LIST (individual words with definitions, often on separate lines or with delimiters)
2. EDUCATIONAL NOTES (paragraphs of text explaining concepts)

Respond ONLY with JSON: {"type": "vocabulary" or "notes", "confidence": 0-100, "reason": "brief explanation"}`
        },
        {
          role: 'user',
          content: `Analyze this content (first 500 characters):\n\n${content.substring(0, 500)}\n\nIs this a vocabulary list or educational notes?`
        }
      ];

      const response = await makeOpenAIRequest(messages, 100);
      const result = JSON.parse(response);

      return result;
    } catch (error) {
      console.error('Error analyzing content type:', error);
      // Default to notes if analysis fails
      return { type: 'notes', confidence: 50, reason: 'Analysis failed, defaulting to notes' };
    }
  };

  // Process content as vocabulary
  const processAsVocabulary = async (content, fileName) => {
    try {
      // Extract vocabulary from content
      const words = await extractVocabularyWithAI(content);

      if (!words || words.length === 0) {
        throw new Error('No vocabulary words could be extracted');
      }

      const newList = {
        id: Date.now(),
        name: fileName.replace(/\.[^/.]+$/, ''), // Remove file extension
        words: words,
        uploadedAt: new Date().toISOString()
      };

      setVocabularyLists(prev => [...prev, newList]);

      // Award points for vocabulary upload (25 points base + 1 per word)
      awardPoints('vocabulary_upload', 25 + words.length);

      console.log(`Vocabulary list created with ${words.length} words`);
    } catch (error) {
      console.error('Error processing vocabulary:', error);

      const errorMessage = {
        type: 'ai',
        content: `❌ Error processing vocabulary: ${error.message}`,
        timestamp: new Date(),
        isSystemMessage: true
      };
      setChatMessages(prev => [...prev, errorMessage]);
    }
  };

  const generateCourseFromNotes = async (noteId) => {
    const note = uploadedNotes.find(n => n.id === noteId);
    if (!note) {
      console.error('Note not found for ID:', noteId);
      return;
    }
    
    setIsGenerating(true);
    try {
      // Check for active goals limit like in roadmap generation
      const activeGoalsCount = userGoals.length;
      if (activeGoalsCount >= 10) {
        setIsGenerating(false);
        alert('You have reached the maximum of 10 active learning goals. Please delete an existing goal before adding a new one.');
        return;
      }
      
      let noteContent = note.content;
      
      // Validate content exists
      console.log('Original note content length:', noteContent?.length);
      console.log('Note content preview:', noteContent?.substring(0, 100));
      
      if (!noteContent || noteContent.trim().length === 0) {
        console.error('Note content is empty:', { noteContent, note });
        throw new Error('Note content is empty or invalid');
      }
      
      // Check if content contains binary data (PDF not properly extracted)
      if (noteContent.includes('%PDF') || noteContent.includes('\0') || noteContent.match(/^[\x00-\x08\x0B\x0C\x0E-\x1F\x7F-\xFF]/)) {
        console.error('Note content appears to be binary data:', noteContent.substring(0, 200));
        throw new Error('The uploaded file contains binary data that could not be converted to text. Please ensure the PDF has extractable text or use a text file.');
      }
      
      // Sanitize and limit note content for OpenAI
      function sanitizeText(text) {
        const sanitized = text.replace(/[\u0000\x00\0]/g, '').replace(/\\u[0-9A-Fa-f]{4}/g, '').replace(/\s+/g, ' ').trim();
        console.log('Text sanitization: original length', text.length, 'sanitized length', sanitized.length);
        return sanitized;
      }
      
      noteContent = sanitizeText(noteContent);
      
      if (noteContent.length < 50) {
        console.error('Note content too short:', noteContent.length, 'characters');
        throw new Error('Note content is too short to generate a meaningful course. Please provide more detailed notes.');
      }
      
      if (noteContent.length > 12000) {
        console.log('Truncating note content from', noteContent.length, 'to 12000 characters');
        noteContent = noteContent.slice(0, 12000) + '...';
      }
      // Map course to original roadmap if available
      let mappedRoadmap = null;
      if (userGoals.length > 0) {
        mappedRoadmap = userGoals[0]; // Use first roadmap as template
      }
      const messages = [
        {
          role: 'system',
          content: `You are an expert learning coach. Analyze the provided notes and create a comprehensive learning course with REAL, SPECIFIC resources. Return your response as a JSON object with this structure: 
          {
            "title": "Course Title Based on Notes",
            "duration": "X days/weeks", 
            "description": "Brief description",
            "lessons": [
              {
                "day": 1,
                "title": "Lesson Title",
                "description": "What the student will learn",
                "duration": "X hours",
                "content": "Detailed lesson content with explanations",
                "tasks": ["Task 1", "Task 2", "Task 3"],
                "resources": {
                  "videos": [{"title": "Real YouTube Video Title", "channel": "Channel Name", "url": "https://youtube.com/watch?v=VIDEO_ID", "duration": "XX:XX"}],
                  "articles": [{"title": "Article Title", "source": "Website Name", "url": "https://realurl.com"}],
                  "books": [{"title": "Book Title", "author": "Author Name", "url": "https://freebook.url", "pages": "X-Y"}],
                  "practice": [{"title": "Practice Problem Set", "source": "Source Name", "url": "https://practice.url", "problems": ["Problem 1", "Problem 2"]}]
                }
              }
            ]
          }
          
          IMPORTANT: Provide REAL, existing YouTube videos, articles, books, and practice resources. Use actual URLs, channel names, and specific content titles.${mappedRoadmap ? `\n          If possible, align the course structure with this existing roadmap format:\n          ${JSON.stringify(mappedRoadmap.lessons, null, 2)}` : ''}`
        },
        {
          role: 'user',
          content: `Create a comprehensive learning course based on these notes:\n\n${noteContent}\n\nMake it educational, well-structured, include practical exercises, and provide REAL, SPECIFIC resources for each lesson.`
        }
      ];
      console.log('Sending request to OpenAI with content length:', noteContent.length);
      console.log('Messages being sent:', JSON.stringify(messages, null, 2));
      
      let response;
      try {
        response = await makeOpenAIRequest(messages, 3500);
        console.log('OpenAI response received successfully:', response ? response.substring(0, 200) + '...' : 'null');
      } catch (apiError) {
        console.error('OpenAI API Error Details:', {
          message: apiError?.message,
          stack: apiError?.stack,
          name: apiError?.name
        });
        throw new Error('AI API error: ' + (apiError?.message || 'Failed to connect to AI service'));
      }
      
      let courseData;
      try {
        if (!response || typeof response !== 'string') {
          throw new Error('Invalid response format from AI service');
        }
        courseData = JSON.parse(response);
        console.log('Course data parsed successfully');
      } catch (jsonError) {
        console.error('JSON parsing error:', jsonError);
        console.error('Response content:', response);
        throw new Error('AI response was not valid JSON. Please try again.');
      }
      // Add metadata
      courseData.id = Date.now();
      courseData.createdAt = new Date().toISOString();
      courseData.progress = 0;
      courseData.completedLessons = [];
      courseData.schedule = studySchedule;
      courseData.adaptiveTimeline = true;
      courseData.sourceNotes = noteId;
      courseData.generatedFromNotes = true;
      setCurrentRoadmap(courseData);
      setRoadmapGenerated(true);
      
      // Add to user goals
      const updatedGoals = [...userGoals, courseData];
      setUserGoals(updatedGoals);
      
      // Award points for creating a course from notes
      awardPoints('notes_course_creation', 200);
      
      // Update user profile
      if (currentUser) {
        const updatedUser = {
          ...currentUser,
          profile: {
            ...currentUser.profile,
            totalGoals: updatedGoals.length
          }
        };
        setCurrentUser(updatedUser);
        saveUserData(currentUser.id, updatedUser);
      }
      
      setUploadedNotes(prev => prev.map(n => n.id === noteId ? {...n, processed: true} : n));
      setCurrentView('roadmap');
    } catch (error) {
      console.error('Error generating course from notes:', error);
      // Professional error message in chat
      const errorMessage = {
        type: 'ai',
        content: `❌ Error generating course from notes: ${error.message}`,
        timestamp: new Date(),
        isSystemMessage: true,
        style: {
          color: darkMode ? '#F87171' : '#B91C1C',
          background: darkMode ? '#1F2937' : '#FEE2E2',
          border: '1px solid #F87171',
          fontWeight: 'bold',
          padding: '12px',
          borderRadius: '12px',
          margin: '12px 0'
        }
      };
      setChatMessages(prev => [...prev, errorMessage]);
    }
    setIsGenerating(false);
  };

  const handleDeleteGoal = (goal) => {
    setGoalToDelete(goal);
    setShowDeleteModal(true);
  };

  const confirmDeleteGoal = () => {
    if (!goalToDelete || !currentUser) return;

    // Remove from userGoals
    const updatedGoals = userGoals.filter(goal => goal.id !== goalToDelete.id);
    setUserGoals(updatedGoals);

    // If deleted goal is current roadmap, clear it
    if (currentRoadmap && currentRoadmap.id === goalToDelete.id) {
      setCurrentRoadmap(null);
      setProgress(0);
      setCompletedLessons(new Set());
      setCurrentLesson(0);
    }

    // Update user profile
    const updatedUser = {
      ...currentUser,
      profile: {
        ...currentUser.profile,
        totalGoals: updatedGoals.length
      }
    };
    setCurrentUser(updatedUser);

    // Save to storage - this will permanently delete it
    saveUserData(currentUser.id, updatedUser);

    // Close modal and reset state
    setShowDeleteModal(false);
    setGoalToDelete(null);

    // Show success message
    const message = {
      type: 'ai',
      content: `🗑️ Learning goal "${goalToDelete.title}" has been permanently deleted.`,
      timestamp: new Date(),
      isSystemMessage: true
    };
    setChatMessages(prev => [...prev, message]);
  };

  const cancelDeleteGoal = () => {
    setShowDeleteModal(false);
    setGoalToDelete(null);
  };

const handleAuth = async (isLogin, formData) => {
  setAuthLoading(true);
  
  try {
    if (isLogin) {
      // Handle login
      // console.log('Attempting login for:', formData.email);
      const { data, error } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password,
      });

      if (error) {
        console.error('Login error:', error);
        alert(error.message);
        setAuthLoading(false);
        return;
      }

      if (data.user) {
        console.log('Login successful, loading user data...');
        
        // Load user profile data
        const userData = await loadUserData(data.user.id);
        if (userData) {
          setCurrentUser(userData);
          setIsAuthenticated(true);
          restoreUserSession(userData);
          setCurrentView(userData.sessionData?.currentView || 'dashboard');
        } else {
          // Create minimal profile for existing users without profiles
          const newUserData = {
            id: data.user.id,
            email: data.user.email,
            name: data.user.user_metadata?.name || 'User',
            profile: {
              streak: 0,
              totalGoals: 0,
              completedLessons: 0,
              joinDate: new Date().toISOString().split('T')[0],
              preferences: { darkMode: false, notifications: true }
            },
            sessionData: {}
          };
          
          // Save profile for next time
          try {
            await saveUserData(data.user.id, newUserData);
            console.log('Created missing user profile');
          } catch (saveError) {
            console.warn('Could not save user profile:', saveError);
          }
          
          setCurrentUser(newUserData);
          setIsAuthenticated(true);
          setCurrentView('dashboard');
        }
      }
    } else {
      // Handle signup
      // console.log('Attempting signup for:', formData.email);
      const { data, error } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            name: formData.name
          }
        }
      });

      console.log('Signup response:', { data, error });

      if (error) {
        console.error('Signup error:', error);
        alert(`Signup failed: ${error.message}`);
        setAuthLoading(false);
        return;
      }

      if (data.user) {
        // Check if email confirmation is required
        if (!data.session) {
          alert('Account created! Please check your email for confirmation link, then try logging in.');
          setShowLogin(true);
          setAuthLoading(false);
          return;
        }

        // User is immediately signed in - create profile
        console.log('Creating user profile...');
        
        const newUserData = {
          id: data.user.id,
          email: data.user.email,
          // name: authForm.name,
          profile: {
            streak: 0,
            totalGoals: 0,
            completedLessons: 0,
            joinDate: new Date().toISOString().split('T')[0],
            preferences: { darkMode: false, notifications: true }
          },
          sessionData: {}
        };

        // Create the user profile in database
        try {
          await saveUserData(data.user.id, newUserData);
          console.log('User profile created successfully');
          
          setCurrentUser(newUserData);
          setIsAuthenticated(true);
          setCurrentView('dashboard');
          alert('Account created successfully! Welcome to Nalem Study Circle!');
        } catch (saveError) {
          console.error('Failed to create user profile:', saveError);
          alert('Account created but there was an issue setting up your profile. Please try logging in.');
          setShowLogin(true);
        }
      }
    }
  } catch (error) {
    console.error('Authentication error:', error);
    alert(`Authentication failed: ${error.message}`);
  }
  
  setAuthLoading(false);
  // setAuthForm({ email: '', password: '', name: '' }); // Removed: setAuthForm is now local to AuthPage
};

// Add this function right after the handleAuth function (around line 1089)
const handleGoogleAuth = async () => {
  setAuthLoading(true);
  
  try {
    console.log('Attempting Google Sign In...');
    
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin // This ensures it redirects back to your app
      }
    });

    if (error) {
      console.error('Google Sign In error:', error);
      alert(`Google Sign In failed: ${error.message}`);
      setAuthLoading(false);
      return;
    }

    // The auth state change will be handled by the existing onAuthStateChange listener
    // No need to do anything else here as the redirect will handle the rest
    
  } catch (error) {
    console.error('Google authentication error:', error);
    alert(`Google Sign In failed: ${error.message}`);
    setAuthLoading(false);
  }
};

const saveDemoUserToLocalStorage = (userData) => {
  try {
    setTimeout(() => {
      const storage = JSON.parse(localStorage.getItem('nalem_study_circle_data') || '{}');
      storage.currentUser = { id: userData.id, email: userData.email };
      localStorage.setItem('nalem_study_circle_data', JSON.stringify(storage));
    }, 0);
  } catch (error) {
    console.error('Error saving demo user to localStorage:', error);
  }
};

  const loginAsDemo = () => {
  setAuthLoading(true);
  
  // Initialize demo account if it doesn't exist
  const users = loadFromStorage('users', {});
  if (!users['demo-user-123']) {
    const demoUser = {
      id: 'demo-user-123',
      email: 'demo@nalemstudy.com',
      password: 'demo123',
      name: 'Demo User',
      profile: {
        streak: 5,
        totalGoals: 2,
        completedLessons: 8,
        joinDate: '2024-01-15',
        preferences: { darkMode: false, notifications: true }
      },
      sessionData: {
        userGoals: [
          {
            id: 1704067200000,
            title: "Learn Python Programming Fundamentals",
            duration: "21 days",
            description: "Master Python basics for web development and automation",
            createdAt: "2024-01-01T00:00:00.000Z",
            progress: 35,
            completedLessons: [0, 1],
            schedule: {
              weekly_pattern: {
                Monday: { active: true, recommended_time: '1-2 hours', flexibility: 'moderate' },
                Wednesday: { active: true, recommended_time: '1-2 hours', flexibility: 'moderate' },
                Friday: { active: true, recommended_time: '1-2 hours', flexibility: 'moderate' },
                Saturday: { active: true, recommended_time: '2-3 hours', flexibility: 'moderate' }
              },
              total_days: 4,
              estimated_completion: '21 days',
              adaptive: true
            },
            lessons: [
              {
                day: 1,
                title: "Python Setup and Variables",
                description: "Install Python and learn about variables, data types",
                duration: "2-3 hours",
                content: "Welcome to Python! Today you'll set up your development environment and learn the fundamental building blocks of Python programming.",
                tasks: ["Install Python 3.9+", "Set up VS Code", "Practice with variables"],
                resources: {
                  videos: [
                    {
                      title: "Python for Everybody - Course 1: Getting Started",
                      channel: "freeCodeCamp.org",
                      url: "https://youtube.com/watch?v=8DvywoWv6fI",
                      duration: "13:40:00"
                    }
                  ],
                  articles: [
                    {
                      title: "Python Beginner's Guide",
                      source: "Python.org",
                      url: "https://docs.python.org/3/tutorial/"
                    }
                  ],
                  books: [
                    {
                      title: "Automate the Boring Stuff with Python",
                      author: "Al Sweigart",
                      url: "https://automatetheboringstuff.com/",
                      pages: "1-25"
                    }
                  ],
                  practice: [
                    {
                      title: "Python Basics Exercises",
                      source: "HackerRank",
                      url: "https://hackerrank.com/domains/python",
                      problems: ["Say Hello World", "Python If-Else", "Arithmetic Operators"]
                    }
                  ]
                }
              },
              {
                day: 2,
                title: "Control Flow and Functions", 
                description: "Master if statements, loops, and creating functions",
                duration: "3-4 hours",
                content: "Learn how to control program flow with conditional statements and loops, then organize your code with functions.",
                tasks: ["Write if/else statements", "Create for and while loops", "Define custom functions"],
                resources: {
                  videos: [
                    {
                      title: "Python Functions Tutorial",
                      channel: "Corey Schafer", 
                      url: "https://youtube.com/watch?v=9Os0o3wzS_I",
                      duration: "22:00"
                    }
                  ],
                  articles: [
                    {
                      title: "Python Control Flow",
                      source: "Real Python",
                      url: "https://realpython.com/python-conditional-statements/"
                    }
                  ],
                  practice: [
                    {
                      title: "Control Flow Exercises",
                      source: "Codewars",
                      url: "https://codewars.com/kata/search/python",
                      problems: ["FizzBuzz", "Grade Calculator", "Number Guessing Game"]
                    }
                  ]
                }
              }
            ]
          }
        ],
        completedLessons: [0, 1],
        progress: 35,
        questionsLeft: 7,
        totalStudyTime: 7200000,
        dailyProgress: {
          [new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]]: 1,
          [new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]]: 2,
          [new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]]: 1,
          [new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]]: 2,
          [new Date().toISOString().split('T')[0]]: 1
        },
        weeklyStreak: 5,
        lessonQuizzes: {
          0: {
            questions: [
              {
                question: "What is the correct way to install Python on your system?",
                options: ["Download from python.org", "Use npm install", "Install via Microsoft Store only", "Copy files manually"],
                correct: 0,
                explanation: {
                  correct_reason: "python.org provides the official, most up-to-date Python installer for all operating systems",
                  incorrect_reasons: {
                    "1": "npm is for Node.js packages, not Python installation",
                    "2": "Microsoft Store is one option but not the primary recommendation",
                    "3": "Manual file copying doesn't properly configure Python"
                  },
                  concept: "Python installation best practices"
                }
              }
            ]
          }
        },
        chatMessages: [
          {
            type: 'ai',
            content: 'Welcome! I\'m your AI tutor. Feel free to ask me any questions about your Python learning journey!',
            timestamp: new Date('2024-01-15T10:00:00')
          }
        ],
        autoStartPomodoro: false,
        userPoints: 2350,
        userBadges: [
          { id: 'study_novice', earnedAt: '2024-01-10T10:00:00.000Z' },
          { id: 'lesson_starter', earnedAt: '2024-01-11T14:30:00.000Z' },
          { id: 'lesson_explorer', earnedAt: '2024-01-14T16:45:00.000Z' },
          { id: 'streak_starter', earnedAt: '2024-01-13T09:15:00.000Z' }
        ],
        courseAnalytics: {
          1704067200000: {
            totalStudyTime: 7200000,
            lessonCompletionTimes: [3600000, 4200000],
            quizScores: [85, 92],
            averageQuizScore: 88.5,
            strongTopics: ['Variables', 'Basic Syntax'],
            improvementAreas: ['Functions', 'Loops'],
            studyPattern: 'evening_learner',
            consistency: 0.8
          }
        }
      }
    };
    
    users['demo-user-123'] = demoUser;
    saveToStorage('users', users);
  }

  setTimeout(() => {
    const demoUser = users['demo-user-123'];
    if (demoUser) {
      setCurrentUser(demoUser);
      setIsAuthenticated(true);
      restoreUserSession(demoUser);
      setCurrentView(demoUser.sessionData?.currentView || 'dashboard');
    }
    saveDemoUserToLocalStorage(demoUser);
    setAuthLoading(false);
    // setAuthForm({ email: '', password: '', name: '' }); // Removed: setAuthForm is now local to AuthPage
  }, 500);
};

  const logout = async () => {
  try {
    // Save current session before logout
    await saveCurrentSession();
    
    // Clear localStorage for demo user
    if (currentUser?.id === 'demo-user-123') {
      localStorage.removeItem('nalem_study_circle_data');
      // Reinitialize demo data structure
      const demoStorage = { users: {}, currentUser: null };
      localStorage.setItem('nalem_study_circle_data', JSON.stringify(demoStorage));
    } else {
      // Only sign out from Supabase if not demo user
      await supabase.auth.signOut();
    }

    // Reset all states
    setIsAuthenticated(false);
    setCurrentUser(null);
    setCurrentView('landing');
    
    // Reset states
    setUserGoals([]);
    setCurrentRoadmap(null);
    setCompletedLessons(new Set());
    setChatMessages([]);
    setProgress(0);
    setQuestionsLeft(10);
    setTotalStudyTime(0);
    setDailyProgress({});
    setWeeklyStreak(0);
    setLearningGoal('');
    setChatInput('');
    
    // Reset buffer states
    // setLearningGoalBuffer is now local to OnboardingPage
    setChatInputBuffer('');
  } catch (error) {
    console.error('Error during logout:', error);
  }
};

  // AI Integration Functions
  const makeOpenAIRequest = async (messages, maxTokens = 1000) => {
  try {
    // Use relative path when BACKEND_URL is empty (production)
    // or full URL for local development
    const apiUrl = BACKEND_URL ? `${BACKEND_URL}/api/openai` : '/api/openai';
    console.log('Making request to:', apiUrl);
    
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        messages: messages,
        max_tokens: maxTokens,
        temperature: 0.7
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.error || `Request failed: ${response.statusText}`);
    }

    const data = await response.json();
    return data.choices[0].message.content;
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
};

  const generateAIRoadmap = async () => {
    setIsGenerating(true);
    setAiLoading(true);
    
    const goalText = learningGoal; // learningGoalBuffer is now local to OnboardingPage
    const detectedTestType = detectTestPrepGoal(goalText);
    const isExamType = detectedTestType && ['SAT','ACT','AP','NEET','GRE','LSAT','MCAT','TOEFL','IELTS','JEE','GMAT'].includes(detectedTestType);
    try {
      // Limit active goals to 10
      const activeGoalsCount = userGoals.length;
      if (activeGoalsCount >= 10) {
        setIsGenerating(false);
        setAiLoading(false);
        alert('You have reached the maximum of 10 active learning goals. Please delete an existing goal before adding a new one.');
        return;
      }
      let roadmapData;
      // Always use regular AI roadmap generation for all goals
      const messages = [
        {
          role: 'system',
          content: `You are an expert learning coach. Create detailed, actionable learning roadmaps with REAL, SPECIFIC resources. Return your response as a JSON object with this structure: 
          {
            "title": "Learning Goal Title",
            "duration": "X days/weeks", 
            "description": "Brief description",
            "lessons": [
              {
                "day": 1,
                "title": "Lesson Title",
                "description": "What the student will learn",
                "duration": "X hours",
                "content": "Detailed lesson content with explanations",
                "tasks": ["Task 1", "Task 2", "Task 3"],
                "resources": {
                  "videos": [{"title": "Real YouTube Video Title", "channel": "Channel Name", "url": "https://youtube.com/watch?v=VIDEO_ID", "duration": "XX:XX"}],
                  "articles": [{"title": "Article Title", "source": "Website Name", "url": "https://realurl.com"}],
                  "books": [{"title": "Book Title", "author": "Author Name", "url": "https://freebook.url", "pages": "X-Y"}],
                  "practice": [{"title": "Practice Problem Set", "source": "Source Name", "url": "https://practice.url", "problems": ["Problem 1", "Problem 2"]}]
                }
              }
            ]
          }
          
          IMPORTANT: Provide REAL, existing YouTube videos, articles, books, and practice resources. Use actual URLs, channel names, and specific content titles.`
        },
        {
          role: 'user',
          content: `Create a comprehensive learning roadmap for: "${goalText}". Include 5-8 lessons with REAL, SPECIFIC resources.`
        }
      ];

      const response = await makeOpenAIRequest(messages, 3500);
      roadmapData = JSON.parse(response);

      // If exam type, add extra practice tests section to each lesson
      if (isExamType && Array.isArray(roadmapData.lessons)) {
        roadmapData.lessons = roadmapData.lessons.map(lesson => {
          // Add extra practiceTests array under resources
          return {
            ...lesson,
            resources: {
              ...lesson.resources,
              practiceTests: [
                // Example real practice test links for major exams
                ...(detectedTestType === 'SAT' ? [
                  { title: 'Official SAT Practice Test 1', url: 'https://satsuite.collegeboard.org/sat/practice-tests', source: 'College Board' },
                  { title: 'Khan Academy SAT Practice', url: 'https://www.khanacademy.org/test-prep/sat', source: 'Khan Academy' }
                ] : []),
                ...(detectedTestType === 'ACT' ? [
                  { title: 'Official ACT Practice Test', url: 'https://www.act.org/content/act/en/products-and-services/the-act/test-preparation.html', source: 'ACT.org' },
                  { title: 'Varsity Tutors ACT Practice', url: 'https://www.varsitytutors.com/act-practice-tests', source: 'Varsity Tutors' }
                ] : []),
                ...(detectedTestType === 'AP' ? [
                  { title: 'AP Practice Exams', url: 'https://apstudents.collegeboard.org/courses', source: 'College Board' },
                  { title: 'Albert.io AP Practice', url: 'https://www.albert.io/ap-exams', source: 'Albert.io' }
                ] : []),
                ...(detectedTestType === 'NEET' ? [
                  { title: 'NEET Practice Papers', url: 'https://neet.nta.nic.in/', source: 'NTA' },
                  { title: 'BYJU’S NEET Practice', url: 'https://byjus.com/neet/neet-practice-papers/', source: 'BYJU’S' }
                ] : []),
                ...(detectedTestType === 'GRE' ? [
                  { title: 'ETS GRE Practice Tests', url: 'https://www.ets.org/gre/test-takers/general-test/prepare.html', source: 'ETS' },
                  { title: 'Magoosh GRE Practice', url: 'https://magoosh.com/gre/gre-practice-test/', source: 'Magoosh' }
                ] : []),
                // Add more exam types as needed
              ]
            }
          };
        });
      }
      
      // Add metadata
      roadmapData.id = Date.now();
      roadmapData.createdAt = new Date().toISOString();
      roadmapData.progress = 0;
      roadmapData.completedLessons = [];
      roadmapData.schedule = studySchedule;
      roadmapData.adaptiveTimeline = true;
      roadmapData.testType = detectedTestType; // Store detected test type
      
      setCurrentRoadmap(roadmapData);
      setRoadmapGenerated(true);

      // Track roadmap generation
      analytics.trackRoadmapGeneration(goalText, detectedTestType);

      // Add to user goals
      const updatedGoals = [...userGoals, roadmapData];
      setUserGoals(updatedGoals);
      
      // Award points for creating a new goal
      awardPoints('goal_creation', 100);
      
      // Update user profile
      if (currentUser) {
        const updatedUser = {
          ...currentUser,
          profile: {
            ...currentUser.profile,
            totalGoals: updatedGoals.length
          }
        };
        setCurrentUser(updatedUser);
        saveUserData(currentUser.id, updatedUser);
      }
      
      setCurrentView('roadmap');
      
    } catch (error) {
      console.error('Error generating roadmap:', error);
      // Enhanced fallback roadmap with real resources
      const fallbackRoadmap = {
        id: Date.now(),
        title: learningGoal,
        duration: "30 days",
        description: "A comprehensive learning path tailored to your goals",
        createdAt: new Date().toISOString(),
        progress: 0,
        completedLessons: [],
        schedule: studySchedule,
        lessons: [
          {
            day: 1,
            title: "Getting Started",
            description: "Foundation concepts and setup",
            duration: "2-3 hours",
            content: "Welcome to your learning journey! Today we'll establish the fundamentals and get you set up for success.",
            tasks: ["Complete initial setup", "Review core concepts", "Create your first project"],
            resources: {
              videos: [{"title": "Programming Tutorial for Beginners", "channel": "Programming with Mosh", "url": "https://youtube.com/watch?v=_uQrJ0TkZlc", "duration": "6:15:01"}],
              articles: [{"title": "Getting Started Guide", "source": "Official Documentation", "url": "https://docs.python.org/3/tutorial/"}],
              books: [{"title": "Python Basics", "author": "Real Python", "url": "https://realpython.com/python-basics/", "pages": "1-50"}],
              practice: [{"title": "Basic Exercises", "source": "HackerRank", "url": "https://hackerrank.com/domains/python", "problems": ["Hello World", "Variable Types"]}]
            }
          }
        ]
      };
      
      setCurrentRoadmap(fallbackRoadmap);
      setRoadmapGenerated(true);
      setUserGoals(prev => [...prev, fallbackRoadmap]);
      setCurrentView('roadmap');
      
      alert('AI roadmap generation failed, using fallback content. Please check your API key configuration.');
    }
    
    setIsGenerating(false);
    setAiLoading(false);
  };

  // IMPROVED: Quiz generation with better persistence
  const generateAIQuiz = async (lessonContent, lessonTitle, lessonIndex) => {
    // Create a unique key for this specific lesson across all roadmaps
    const quizKey = `${currentRoadmap?.id || 'default'}_lesson_${lessonIndex}`;
    
    // Check if we already have a quiz for this specific lesson in this roadmap
    if (lessonQuizzes[quizKey]) {
      console.log('Using existing quiz for lesson', lessonIndex);
      return lessonQuizzes[quizKey];
    }

    console.log('Generating new quiz for lesson', lessonIndex);

    try {
      const messages = [
        {
          role: 'system',
          content: `Create a comprehensive quiz with detailed explanations specifically for the lesson topic. Return JSON with this structure:
          {
            "estimatedMinutes": 8,
            "questions": [
              {
                "question": "Question text specific to the lesson topic",
                "options": ["Option A", "Option B", "Option C", "Option D"],
                "correct": 0,
                "difficulty": "medium",
                "estimatedSeconds": 120,
                "explanation": {
                  "correct_reason": "Why the correct answer is right",
                  "incorrect_reasons": {
                    "0": "Why option A is wrong (if not correct)",
                    "1": "Why option B is wrong (if not correct)", 
                    "2": "Why option C is wrong (if not correct)",
                    "3": "Why option D is wrong (if not correct)"
                  },
                  "concept": "Key concept being tested",
                  "step_by_step": [
                    "Step 1: Identify the key information",
                    "Step 2: Apply the relevant concept", 
                    "Step 3: Eliminate incorrect options",
                    "Step 4: Select the best answer"
                  ]
                }
              }
            ]
          }
          
          Make each question UNIQUE and SPECIFIC to the lesson content. Include realistic time estimates for each question based on difficulty.`
        },
        {
          role: 'user',
          content: `Create 6-7 challenging multiple choice questions with detailed explanations and time estimates specifically for this lesson:

          Lesson Title: "${lessonTitle}"
          Lesson Content: "${lessonContent}"

          Include:
          1. Realistic time estimates for the overall quiz and each question
          2. Step-by-step explanations for solving each problem
          3. Difficulty ratings (easy/medium/hard)
          4. Questions that test specific concepts from this lesson`
        }
      ];

      const response = await makeOpenAIRequest(messages, 2000);
      const quizData = JSON.parse(response);
      
      // Store the quiz for this specific lesson with unique key
      const updatedQuizzes = {
        ...lessonQuizzes,
        [quizKey]: quizData
      };
      setLessonQuizzes(updatedQuizzes);
      
      // Save immediately to prevent loss
      if (currentUser) {
        saveCurrentSession();
      }
      
      return quizData;
    } catch (error) {
      console.error('Error generating quiz:', error);
      // Enhanced fallback quiz with timing and step-by-step explanations
      const fallbackQuiz = {
        estimatedMinutes: 6,
        questions: [
          {
            question: `What is the main learning objective of "${lessonTitle}"?`,
            options: ["Basic understanding", "Advanced implementation", "Practical application", "Conceptual overview"],
            correct: 2,
            difficulty: "medium",
            estimatedSeconds: 90,
            explanation: {
              correct_reason: "The lesson focuses on practical application of the concepts taught",
              incorrect_reasons: {
                "0": "This goes beyond basic understanding to practical skills",
                "1": "This is introductory level, not advanced implementation",
                "3": "While conceptual, the emphasis is on practical application"
              },
              concept: `Core learning objective of ${lessonTitle}`,
              step_by_step: [
                "Step 1: Review the lesson's main focus and goals",
                "Step 2: Identify what type of learning outcome is emphasized", 
                "Step 3: Consider the lesson's practical components",
                "Step 4: Select the option that best matches the hands-on approach"
              ]
            }
          },
          {
            question: `Which approach best supports the goals of "${lessonTitle}"?`,
            options: ["Theoretical study only", "Hands-on practice with concepts", "Memorization of facts", "Passive observation"],
            correct: 1,
            difficulty: "easy",
            estimatedSeconds: 60,
            explanation: {
              correct_reason: "Hands-on practice reinforces the specific concepts taught in this lesson",
              incorrect_reasons: {
                "0": "Theory alone doesn't develop the practical skills this lesson teaches",
                "2": "Memorization doesn't build true understanding of the lesson concepts",
                "3": "Active engagement is needed to master this lesson's objectives"
              },
              concept: `Learning methodology for ${lessonTitle}`,
              step_by_step: [
                "Step 1: Consider what the lesson is trying to teach",
                "Step 2: Think about effective learning strategies",
                "Step 3: Match the approach to the lesson's practical nature",
                "Step 4: Choose the most active learning method"
              ]
            }
          },
          {
            question: `What should you be able to do after completing "${lessonTitle}"?`,
            options: ["Recite definitions", "Apply the concepts practically", "Discuss theory only", "Identify terminology"],
            correct: 1,
            difficulty: "medium",
            estimatedSeconds: 75,
            explanation: {
              correct_reason: "The lesson is designed to help you apply concepts in real situations",
              incorrect_reasons: {
                "0": "Reciting definitions is just the first step, not the end goal",
                "2": "Theory discussion alone doesn't demonstrate mastery",
                "3": "Identifying terms is basic knowledge, not practical competency"
              },
              concept: `Expected outcomes from ${lessonTitle}`,
              step_by_step: [
                "Step 1: Think about the lesson's ultimate goal",
                "Step 2: Consider what demonstrates true understanding",
                "Step 3: Focus on practical application vs. memorization",
                "Step 4: Select the outcome that shows real competency"
              ]
            }
          }
        ]
      };
      
      // Store the fallback quiz for this lesson with unique key
      const updatedQuizzes = {
        ...lessonQuizzes,
        [quizKey]: fallbackQuiz
      };
      setLessonQuizzes(updatedQuizzes);
      
      return fallbackQuiz;
    }
  };

  const generateQuizExplanations = async (quiz, userAnswers) => {
    if (!quiz || !userAnswers) return null;

    try {
      const messages = [
        {
          role: 'system',
          content: 'Provide personalized feedback based on the user\'s quiz performance. Be encouraging and educational.'
        },
        {
          role: 'user',
          content: `Generate personalized feedback for this quiz performance: ${JSON.stringify({quiz, userAnswers})}. Focus on what they got right and gentle correction for mistakes.`
        }
      ];

      const response = await makeOpenAIRequest(messages, 800);
      return response;
    } catch (error) {
      console.error('Error generating explanations:', error);
      return "Great job completing the quiz! Review the explanations below to reinforce your learning.";
    }
  };

  const sendAIMessage = async () => {
    const messageToSend = chatInput || chatInputBuffer;
    if (!messageToSend.trim() || questionsLeft <= 0) return;
    
    const userMessage = { type: 'user', content: messageToSend, timestamp: new Date() };
    setChatMessages(prev => [...prev, userMessage]);
    
    // Clear both states immediately
    setChatInput('');
    setChatInputBuffer('');
    
    try {
      const messages = [
        {
          role: 'system',
          content: `You are an AI tutor for "${currentRoadmap?.title || 'general learning'}". Provide helpful, encouraging, and educational responses. Keep responses concise but informative. Be supportive and motivating.`
        },
        ...chatMessages.slice(-5).map(msg => ({
          role: msg.type === 'user' ? 'user' : 'assistant',
          content: msg.content
        })),
        { role: 'user', content: messageToSend }
      ];

      const response = await makeOpenAIRequest(messages, 500);
      const aiMessage = { type: 'ai', content: response, timestamp: new Date() };
      
      setChatMessages(prev => [...prev, aiMessage]);
      setQuestionsLeft(prev => prev - 1);
      
    } catch (error) {
      console.error('Error sending AI message:', error);
      const errorMessage = { 
        type: 'ai', 
        content: 'I apologize, but I encountered an error. The AI service might be temporarily unavailable. Please check the API configuration or try again later.',
        timestamp: new Date()
      };
      setChatMessages(prev => [...prev, errorMessage]);
    }
  };

  // Schedule and Adaptive Planning Functions
  const generateStudySchedule = (selectedDays, goalDuration) => {
    const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const schedule = {};
    
    selectedDays.forEach(dayIndex => {
      schedule[daysOfWeek[dayIndex]] = {
        active: true,
        recommended_time: '1-2 hours',
        flexibility: 'moderate'
      };
    });
    
    return {
      weekly_pattern: schedule,
      total_days: selectedDays.length,
      estimated_completion: goalDuration,
      adaptive: true
    };
  };

  const adaptLearningPlan = async (missedDays) => {
    if (!currentRoadmap || missedDays < 2) return;

    try {
      const messages = [
        {
          role: 'system',
          content: 'You are an adaptive learning coach. Adjust learning plans based on missed days to help students get back on track.'
        },
        {
          role: 'user',
          content: `User missed ${missedDays} days of their learning plan. Original goal: "${currentRoadmap.title}". Suggest adjustments to help them catch up or modify the timeline. Be encouraging and practical.`
        }
      ];

      const response = await makeOpenAIRequest(messages, 600);
      
      // Show adaptation suggestion to user
      const adaptationMessage = {
        type: 'ai',
        content: `📅 Learning Plan Adjustment: ${response}`,
        timestamp: new Date(),
        isSystemMessage: true
      };
      
      setChatMessages(prev => [...prev, adaptationMessage]);
      
    } catch (error) {
      console.error('Error adapting learning plan:', error);
    }
  };

  // NEW: Developer Modal Component
  const DeveloperModal = () => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8 rounded-3xl ${darkMode ? 'bg-gray-900 border border-gray-700' : 'bg-white border border-gray-200'} shadow-2xl`}>
        <div className="flex justify-between items-center mb-8">
          <h3 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>About the Founder</h3>
          <button
            onClick={() => setShowDeveloperModal(false)}
            className={`p-2 rounded-xl ${darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'} transition-colors duration-300`}
          >
            <X className={`w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
          </button>
        </div>

        <div className="space-y-6">
          {/* Developer Profile */}
          <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gradient-to-r from-purple-900/30 to-blue-900/30 border border-purple-500/30' : 'bg-gradient-to-r from-purple-100 to-blue-100 border border-purple-300'}`}>
            <div className="flex items-center space-x-4 mb-4">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                <User className="w-8 h-8 text-white" />
              </div>
              <div>
                <h4 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  Pranay (Rishi) Nalem
                </h4>
                <p className={`${darkMode ? 'text-purple-300' : 'text-purple-700'}`}>
                  Full-Stack Developer & AI Enthusiast
                </p>
              </div>
            </div>
            <p className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Passionate about creating innovative educational technology solutions that make learning accessible and engaging for everyone.
            </p>
          </div>

          {/* About Section */}
          <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
            <h5 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
              🚀 About This Project
            </h5>
            <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Nalem Study Circle is an advanced AI-powered learning platform designed to revolutionize personalized education. 
              Built with cutting-edge technologies and powered by advanced AI models, this platform creates custom learning 
              paths tailored to individual needs and learning styles.
            </p>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Brain className={`w-5 h-5 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                <span className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  AI-Generated Personalized Learning Paths
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <Trophy className={`w-5 h-5 ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`} />
                <span className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Gamified Learning with Points & Achievements
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <Clock className={`w-5 h-5 ${darkMode ? 'text-green-400' : 'text-green-600'}`} />
                <span className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Integrated Pomodoro Timer & Study Tracking
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <Languages className={`w-5 h-5 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                <span className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Multi-Language Support & Accessibility
                </span>
              </div>
            </div>
          </div>

          {/* Technical Skills */}
          <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
            <h5 className={`text-lg font-semibold mb-3 flex items-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>
              <Code className="w-5 h-5 mr-2" />
              Technical Expertise
            </h5>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h6 className={`font-medium mb-2 ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>Frontend Development</h6>
                <ul className={`text-sm space-y-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <li>• React.js & Modern JavaScript</li>
                  <li>• Responsive UI/UX Design</li>
                  <li>• Tailwind CSS & Animation</li>
                  <li>• Progressive Web Apps</li>
                </ul>
              </div>
              <div>
                <h6 className={`font-medium mb-2 ${darkMode ? 'text-green-300' : 'text-green-700'}`}>AI & Backend</h6>
                <ul className={`text-sm space-y-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <li>• OpenAI API Integration</li>
                  <li>• Node.js & Python</li>
                  <li>• Database Design</li>
                  <li>• RESTful API Development</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Philosophy */}
          <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gradient-to-r from-green-900/30 to-teal-900/30 border border-green-500/30' : 'bg-gradient-to-r from-green-100 to-teal-100 border border-green-300'}`}>
            <h5 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-green-300' : 'text-green-800'}`}>
              💡 Development Philosophy
            </h5>
            <blockquote className={`italic ${darkMode ? 'text-green-200' : 'text-green-700'}`}>
              "Education should be personalized, engaging, and accessible to everyone. By combining artificial intelligence 
              with thoughtful user experience design, we can create learning platforms that adapt to individual needs and 
              inspire lifelong learning."
            </blockquote>
            <p className={`mt-3 text-sm ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
              - Rishi Nalem, Creator of Nalem Study Circle
            </p>
          </div>

          {/* Connect Section */}
          <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
            <h5 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
              🤝 Connect & Collaborate
            </h5>
            <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Interested in educational technology, AI applications, or collaborative projects? Let's connect!
            </p>
            <div className="flex flex-wrap gap-3">
              <button className={`flex items-center space-x-2 px-4 py-2 rounded-xl transition-all duration-300 ${darkMode ? 'bg-blue-800 text-blue-300 hover:bg-blue-700' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}`}>
                <Linkedin className="w-4 h-4" />
                <span>LinkedIn</span>
              </button>
              <button className={`flex items-center space-x-2 px-4 py-2 rounded-xl transition-all duration-300 ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>
                <Github className="w-4 h-4" />
                <span>GitHub</span>
              </button>
              <button className={`flex items-center space-x-2 px-4 py-2 rounded-xl transition-all duration-300 ${darkMode ? 'bg-purple-800 text-purple-300 hover:bg-purple-700' : 'bg-purple-100 text-purple-700 hover:bg-purple-200'}`}>
                <Globe className="w-4 h-4" />
                <span>Portfolio</span>
              </button>
            </div>
          </div>

          {/* Thank You */}
          <div className={`p-6 rounded-2xl text-center ${darkMode ? 'bg-gradient-to-r from-purple-900/30 to-pink-900/30 border border-purple-500/30' : 'bg-gradient-to-r from-purple-100 to-pink-100 border border-purple-300'}`}>
            <h5 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-purple-300' : 'text-purple-800'}`}>
              🙏 Thank You for Using Nalem Study Circle
            </h5>
            <p className={`${darkMode ? 'text-purple-200' : 'text-purple-700'}`}>
              Your learning journey matters, and I'm honored to be part of it. Keep exploring, keep growing!
            </p>
          </div>
        </div>

        <button
          onClick={() => setShowDeveloperModal(false)}
          className="w-full mt-6 bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 rounded-xl font-semibold hover:scale-105 transition-all duration-300"
        >
          Close
        </button>
      </div>
    </div>
  );

  // NEW: Badge Achievement Modal
  const BadgeModal = () => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`max-w-md w-full p-6 rounded-3xl ${darkMode ? 'bg-gray-900 border border-yellow-500' : 'bg-white border border-yellow-300'} shadow-2xl`}>
        <div className="text-center">
          <div className={`w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center ${newBadgeEarned?.color || 'bg-yellow-500'}`}>
            <span className="text-3xl">{newBadgeEarned?.icon || '🏆'}</span>
          </div>
          <h3 className={`text-2xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
            Badge Earned!
          </h3>
          <h4 className={`text-xl font-semibold mb-3 ${darkMode ? 'text-yellow-300' : 'text-yellow-600'}`}>
            {newBadgeEarned?.name}
          </h4>
          <p className={`mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {newBadgeEarned?.description}
          </p>
          <div className={`p-4 rounded-xl mb-6 ${darkMode ? 'bg-yellow-900/30 border border-yellow-700' : 'bg-yellow-100 border border-yellow-300'}`}>
            <p className={`text-sm ${darkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>
              🎉 Keep up the amazing work! You're building great learning habits.
            </p>
          </div>
          <button
            onClick={() => {
              setShowBadgeModal(false);
              setNewBadgeEarned(null);
            }}
            className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 text-white py-3 rounded-xl font-semibold hover:scale-105 transition-all duration-300"
          >
            Awesome!
          </button>
        </div>
      </div>
    </div>
  );

  // NEW: Course Analytics Modal
  const AnalyticsModal = () => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6 rounded-3xl ${darkMode ? 'bg-gray-900 border border-gray-700' : 'bg-white border border-gray-200'} shadow-2xl`}>
        <div className="flex justify-between items-center mb-6">
          <h3 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>Course Analytics</h3>
          <button
            onClick={() => setShowAnalyticsModal(false)}
            className={`p-2 rounded-xl ${darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'} transition-colors duration-300`}
          >
            <X className={`w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
          </button>
        </div>

        {currentAnalytics && (
          <div className="space-y-6">
            {/* Course Overview */}
            <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gradient-to-r from-purple-900/30 to-blue-900/30 border border-purple-500/30' : 'bg-gradient-to-r from-purple-100 to-blue-100 border border-purple-300'}`}>
              <div className="flex items-center justify-between mb-4">
                <h4 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  🎉 Congratulations!
                </h4>
                <Trophy className={`w-8 h-8 ${darkMode ? 'text-yellow-400' : 'text-yellow-500'}`} />
              </div>
              <p className={`text-lg mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                You've completed: <span className="font-bold">{currentAnalytics.courseTitle}</span>
              </p>
              <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Final completion rate: {Math.round(currentAnalytics.completionRate)}%
              </p>
            </div>

            {/* Statistics Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                <div className="flex items-center space-x-3">
                  <Clock className={`w-6 h-6 ${darkMode ? 'text-blue-400' : 'text-blue-500'}`} />
                  <div>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Study Time</p>
                    <p className={`font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                      {Math.round(currentAnalytics.totalStudyTime / 3600000)}h
                    </p>
                  </div>
                </div>
              </div>
              
              <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                <div className="flex items-center space-x-3">
                  <BookOpen className={`w-6 h-6 ${darkMode ? 'text-green-400' : 'text-green-500'}`} />
                  <div>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Lessons</p>
                    <p className={`font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                      {currentAnalytics.lessonsCompleted}/{currentAnalytics.totalLessons}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                <div className="flex items-center space-x-3">
                  <Target className={`w-6 h-6 ${darkMode ? 'text-purple-400' : 'text-purple-500'}`} />
                  <div>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Quiz Average</p>
                    <p className={`font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                      {currentAnalytics.averageQuizScore ? `${Math.round(currentAnalytics.averageQuizScore)}%` : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                <div className="flex items-center space-x-3">
                  <Flame className={`w-6 h-6 ${darkMode ? 'text-orange-400' : 'text-orange-500'}`} />
                  <div>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Current Streak</p>
                    <p className={`font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                      {currentAnalytics.currentStreak} days
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* AI Insights */}
            <div className={`p-6 rounded-2xl ${darkMode ? 'bg-blue-900/20 border border-blue-700' : 'bg-blue-50 border border-blue-300'}`}>
              <h5 className={`text-lg font-semibold mb-3 flex items-center ${darkMode ? 'text-blue-300' : 'text-blue-800'}`}>
                <Brain className="w-5 h-5 mr-2" />
                AI Learning Insights
              </h5>
              <p className={`${darkMode ? 'text-blue-200' : 'text-blue-700'}`}>
                {currentAnalytics.aiInsights}
              </p>
            </div>

            {/* Progress Visualization */}
            <div className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
              <h5 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                Your Learning Journey
              </h5>
              <div className={`w-full h-4 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-300'} mb-2`}>
                <div 
                  className="h-full bg-gradient-to-r from-green-500 to-blue-500 rounded-full transition-all duration-500"
                  style={{ width: `${currentAnalytics.completionRate}%` }}
                />
              </div>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Course completion: {Math.round(currentAnalytics.completionRate)}%
              </p>
            </div>

            <button
              onClick={() => setShowAnalyticsModal(false)}
              className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 rounded-xl font-semibold hover:scale-105 transition-all duration-300"
            >
              Continue Learning Journey
            </button>
          </div>
        )}
      </div>
    </div>
  );

  // NEW: Practice Test Modal
  const PracticeTestModal = () => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`max-w-3xl w-full p-6 rounded-3xl ${darkMode ? 'bg-gray-900 border border-gray-700' : 'bg-white border border-gray-200'} shadow-2xl`}>
        <div className="flex justify-between items-center mb-6">
          <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
            {currentPracticeTest?.title}
          </h3>
          <button
            onClick={() => {
              setPracticeTestMode(false);
              setCurrentPracticeTest(null);
            }}
            className={`p-2 rounded-xl ${darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'} transition-colors duration-300`}
          >
            <X className={`w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
          </button>
        </div>

        {currentPracticeTest && (
          <div className="space-y-6">
            <div className={`p-4 rounded-xl ${darkMode ? 'bg-blue-900/30 border border-blue-700' : 'bg-blue-100 border border-blue-300'}`}>
              <h4 className={`font-semibold mb-2 ${darkMode ? 'text-blue-300' : 'text-blue-800'}`}>
                Test Information
              </h4>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <p className={`${darkMode ? 'text-blue-200' : 'text-blue-700'}`}>
                  <strong>Total Time:</strong> {currentPracticeTest.totalTime} minutes
                </p>
                <p className={`${darkMode ? 'text-blue-200' : 'text-blue-700'}`}>
                  <strong>Sections:</strong> {currentPracticeTest.sections?.length || 0}
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <h5 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>Test Sections:</h5>
              {currentPracticeTest.sections?.map((section, index) => (
                <div key={index} className={`p-4 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                  <div className="flex justify-between items-center">
                    <div>
                      <h6 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                        {section.name}
                      </h6>
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {section.questions} questions • {section.timeLimit} minutes
                      </p>
                    </div>
                    <Clock className={`w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
                  </div>
                </div>
              ))}
            </div>

            <div className={`p-4 rounded-xl ${darkMode ? 'bg-yellow-900/30 border border-yellow-700' : 'bg-yellow-100 border border-yellow-300'}`}>
              <p className={`text-sm ${darkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>
                ⏰ This is a timed practice test. Make sure you have enough time to complete all sections without interruption.
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setPracticeTestMode(false);
                  setCurrentPracticeTest(null);
                }}
                className={`flex-1 py-3 rounded-xl font-semibold transition-all duration-300 ${
                  darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  // Start the practice test
                  awardPoints('practice_test_start', 25);
                  setPracticeTestMode(false);
                  // Here you would implement the actual test interface
                  alert('Practice test started! (Full implementation would show test interface)');
                }}
                className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 rounded-xl font-semibold hover:scale-105 transition-all duration-300"
              >
                Start Test
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // NEW: Study Scheduling Modal
  const StudySchedulingModal = () => {
    const [selectedCourse, setSelectedCourse] = useState('');
    const [selectedDay, setSelectedDay] = useState('');
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');
    
    const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className={`max-w-md w-full p-6 rounded-3xl ${darkMode ? 'bg-gray-900 border border-gray-700' : 'bg-white border border-gray-200'} shadow-2xl`}>
          <div className="flex justify-between items-center mb-6">
            <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>Schedule Study Time</h3>
            <button
              onClick={() => setShowSchedulingModal(false)}
              className={`p-2 rounded-xl ${darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'} transition-colors duration-300`}
            >
              <X className={`w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Select Course
              </label>
              <select
                value={selectedCourse}
                onChange={(e) => setSelectedCourse(e.target.value)}
                className={`w-full p-3 rounded-xl border ${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-800'} focus:outline-none focus:border-purple-500`}
              >
                <option value="">Choose a course...</option>
                {userGoals.map(goal => (
                  <option key={goal.id} value={goal.id}>{goal.title}</option>
                ))}
              </select>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Day of Week
              </label>
              <select
                value={selectedDay}
                onChange={(e) => setSelectedDay(e.target.value)}
                className={`w-full p-3 rounded-xl border ${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-800'} focus:outline-none focus:border-purple-500`}
              >
                <option value="">Choose a day...</option>
                {daysOfWeek.map(day => (
                  <option key={day} value={day}>{day}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Start Time
                </label>
                <input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className={`w-full p-3 rounded-xl border ${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-800'} focus:outline-none focus:border-purple-500`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  End Time
                </label>
                <input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className={`w-full p-3 rounded-xl border ${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-800'} focus:outline-none focus:border-purple-500`}
                />
              </div>
            </div>
          </div>

          <div className="flex space-x-3 mt-6">
            <button
              onClick={() => setShowSchedulingModal(false)}
              className={`flex-1 py-3 rounded-xl font-semibold transition-all duration-300 ${darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (selectedCourse && selectedDay && startTime && endTime) {
                  const course = userGoals.find(g => g.id == selectedCourse);
                  addStudyTimeSlot(selectedCourse, course?.title || 'Course', selectedDay, startTime, endTime);
                  setShowSchedulingModal(false);
                  setSelectedCourse('');
                  setSelectedDay('');
                  setStartTime('');
                  setEndTime('');
                }
              }}
              disabled={!selectedCourse || !selectedDay || !startTime || !endTime}
              className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 rounded-xl font-semibold hover:scale-105 transition-all duration-300 disabled:opacity-50"
            >
              Schedule
            </button>
          </div>
        </div>
      </div>
    );
  };

  // NEW: Vocabulary Modal
  const VocabularyModal = () => {
    const fileInputRef = useRef(null);
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className={`max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6 rounded-3xl ${darkMode ? 'bg-gray-900 border border-gray-700' : 'bg-white border border-gray-200'} shadow-2xl`}>
          <div className="flex justify-between items-center mb-6">
            <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>Vocabulary Learning</h3>
            <div className="flex items-center space-x-2">
              {vocabularyLists.length > 0 && (
                <button
                  onClick={() => {
                    if (window.confirm(`Are you sure you want to delete all ${vocabularyLists.length} vocabulary lists? This action cannot be undone.`)) {
                      setVocabularyLists([]);
                      setCurrentVocabList(null);
                      setFlashcardMode(false);
                      setVocabularyQuizMode(false);
                      setCurrentFlashcard(0);
                      setShowAnswer(false);
                      awardPoints('vocabulary_cleanup_all', 10);
                    }
                  }}
                  className={`p-2 rounded-xl transition-colors duration-300 ${
                    darkMode 
                      ? 'hover:bg-red-900/30 text-red-400 hover:text-red-300' 
                      : 'hover:bg-red-100 text-red-500 hover:text-red-600'
                  }`}
                  title="Delete all vocabulary lists"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              )}
              <button
                onClick={() => {
                  setShowVocabularyModal(false);
                  // If we came from Smart File Upload, return there
                  if (vocabularyModalSource === 'smart-upload') {
                    setTimeout(() => {
                      setShowNotesUpload(true);
                      setVocabularyModalSource(null); // Reset source
                    }, 300);
                  }
                }}
                className={`p-2 rounded-xl ${darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'} transition-colors duration-300`}
              >
                <X className={`w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
              </button>
            </div>
          </div>

          {/* Upload Section */}
          <div className={`p-6 rounded-2xl mb-6 ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
            <h4 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Upload Vocabulary</h4>
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.txt,.pdf"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (file) {
                      await uploadVocabularyList(file);
                      e.target.value = '';
                    }
                  }}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-4 py-2 rounded-xl font-semibold hover:scale-105 transition-all duration-300 flex items-center space-x-2"
                >
                  <Upload className="w-4 h-4" />
                  <span>Upload File</span>
                </button>
              </div>
              
              {/* AI Loading Indicator */}
              {aiLoading && (
                <div className={`p-3 rounded-lg ${darkMode ? 'bg-blue-900/30 border border-blue-700' : 'bg-blue-50 border border-blue-200'}`}>
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    <span className={`text-sm ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                      AI is analyzing your content and extracting vocabulary...
                    </span>
                  </div>
                </div>
              )}
              
              {/* Upload Instructions */}
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700/50 border border-gray-600' : 'bg-white/80 border border-gray-200'}`}>
                <h5 className={`font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Supported Formats:</h5>
                <ul className={`text-sm space-y-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  <li>• <strong>CSV Format:</strong> word,definition,example,difficulty,category</li>
                  <li>• <strong>Plain Text (.txt):</strong> AI will automatically extract key vocabulary and generate definitions</li>
                  <li>• <strong>PDF Files:</strong> Text will be extracted and analyzed by AI</li>
                </ul>
                <p className={`text-xs mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  💡 For best results with plain text, upload content-rich documents like articles, textbooks, or study materials.
                </p>
              </div>
            </div>
          </div>

          {/* Vocabulary Lists */}
          <div className="grid md:grid-cols-2 gap-4">
            {vocabularyLists.map(list => (
              <div key={list.id} className={`p-4 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-gray-100'} hover:scale-105 transition-all duration-300`}>
                <div className="flex items-center justify-between mb-2">
                  <h5 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>{list.name}</h5>
                  <div className="flex items-center space-x-2">
                    {list.aiGenerated && (
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        darkMode ? 'bg-purple-900/50 text-purple-300 border border-purple-700' : 'bg-purple-100 text-purple-700 border border-purple-300'
                      }`}>
                        AI Generated
                      </span>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteVocabularyList(list.id);
                      }}
                      className={`p-1.5 rounded-lg transition-all duration-300 hover:scale-110 ${
                        darkMode 
                          ? 'hover:bg-red-900/30 text-red-400 hover:text-red-300' 
                          : 'hover:bg-red-100 text-red-500 hover:text-red-600'
                      }`}
                      title="Delete vocabulary list"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <p className={`text-sm mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {list.words.length} words • {Math.round(list.progress || 0)}% mastered
                  {list.words.length > 0 && (
                    <span className="ml-2">
                      • {list.words.filter(w => w.difficulty === 'easy').length} easy • {list.words.filter(w => w.difficulty === 'medium').length} medium • {list.words.filter(w => w.difficulty === 'hard').length} hard
                    </span>
                  )}
                </p>
                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      setCurrentVocabList(list);
                      setFlashcardMode(true);
                      setCurrentFlashcard(0);
                      setShowAnswer(false);
                    }}
                    className="flex-1 bg-gradient-to-r from-green-500 to-blue-500 text-white py-2 px-3 rounded-lg text-sm font-medium hover:scale-105 transition-all duration-300"
                  >
                    Flashcards
                  </button>
                  <button
                    onClick={() => {
                      setCurrentVocabList(list);
                      setVocabularyQuizMode(true);
                    }}
                    className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 text-white py-2 px-3 rounded-lg text-sm font-medium hover:scale-105 transition-all duration-300"
                  >
                    Quiz
                  </button>
                </div>
                
                {/* AI Enhancement Button */}
                {(!list.aiGenerated && !list.aiEnhanced) && (
                  <div className="mt-3">
                    <button
                      onClick={async () => {
                        try {
                          await enhanceVocabularyWithAI(list);
                          alert('Vocabulary list enhanced with AI-generated definitions!');
                        } catch (error) {
                          alert(`Failed to enhance vocabulary: ${error.message}`);
                        }
                      }}
                      disabled={aiLoading}
                      className={`w-full py-2 px-3 rounded-lg text-sm font-medium transition-all duration-300 ${
                        aiLoading 
                          ? 'bg-gray-400 text-gray-600 cursor-not-allowed' 
                          : 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:scale-105'
                      }`}
                    >
                      {aiLoading ? 'Enhancing...' : '✨ Enhance with AI'}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {vocabularyLists.length === 0 && (
            <div className="text-center py-8">
              <FileText className={`w-16 h-16 mx-auto mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
              <p className={`text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                No vocabulary lists yet. Upload a CSV or TXT file to get started!
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // NEW: Flashcard Modal
  const FlashcardModal = () => {
    if (!currentVocabList || !flashcardMode) return null;
    
    const currentWord = currentVocabList.words[currentFlashcard];
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className={`max-w-2xl w-full p-6 rounded-3xl ${darkMode ? 'bg-gray-900 border border-gray-700' : 'bg-white border border-gray-200'} shadow-2xl`}>
          <div className="flex justify-between items-center mb-6">
            <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
              Flashcards: {currentVocabList.name}
            </h3>
            <button
              onClick={() => {
                setFlashcardMode(false);
                setCurrentVocabList(null);
                // If opened from Smart Upload, return there
                if (flashcardSource === 'smart-upload') {
                  setTimeout(() => {
                    setShowNotesUpload(true);
                    setFlashcardSource(null); // Reset source
                  }, 300);
                }
              }}
              className={`p-2 rounded-xl ${darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'} transition-colors duration-300`}
            >
              <X className={`w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
            </button>
          </div>

          <div className="text-center mb-6">
            <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-2`}>
              {currentFlashcard + 1} of {currentVocabList.words.length}
            </div>
            <div className={`w-full h-2 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-300'} mb-4`}>
              <div 
                className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all duration-300"
                style={{ width: `${((currentFlashcard + 1) / currentVocabList.words.length) * 100}%` }}
              />
            </div>
          </div>

          <div 
            className={`min-h-[250px] p-8 rounded-2xl cursor-pointer transition-all duration-300 hover:scale-105 ${darkMode ? 'bg-gradient-to-br from-purple-900/30 to-blue-900/30 border border-purple-500/30' : 'bg-gradient-to-br from-purple-100 to-blue-100 border border-purple-300'}`}
            onClick={() => setShowAnswer(!showAnswer)}
          >
            <div className="text-center">
              {!showAnswer ? (
                <div>
                  {/* Front of card - Only the word */}
                  <div className="flex items-center justify-center space-x-4 mb-4">
                    <h4 className={`text-4xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                      {currentWord.word}
                    </h4>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        speakWord(currentWord.word);
                      }}
                      className={`p-2 rounded-xl transition-all duration-300 ${darkMode ? 'bg-gray-800 hover:bg-gray-700 text-blue-400' : 'bg-blue-100 hover:bg-blue-200 text-blue-600'}`}
                    >
                      {speechEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
                    </button>
                  </div>
                  
                  <p className={`text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Click to reveal definition
                  </p>
                </div>
              ) : (
                <div>
                  {/* Back of card - Word, definition, and example */}
                  <div className="flex items-center justify-center space-x-4 mb-6">
                    <h4 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                      {currentWord.word}
                    </h4>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        speakWord(currentWord.word);
                      }}
                      className={`p-2 rounded-xl transition-all duration-300 ${darkMode ? 'bg-gray-800 hover:bg-gray-700 text-blue-400' : 'bg-blue-100 hover:bg-blue-200 text-blue-600'}`}
                    >
                      {speechEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
                    </button>
                  </div>
                  
                  {/* Definition - Main content */}
                  <div className={`p-6 rounded-xl mb-4 ${darkMode ? 'bg-gray-800/50 border border-gray-700' : 'bg-white/80 border border-gray-200'}`}>
                    <p className={`text-xl leading-relaxed ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                      {currentWord.definition}
                    </p>
                  </div>
                  
                  {/* Example - Secondary content */}
                  {currentWord.example && (
                    <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800/30 border border-gray-600' : 'bg-gray-50 border border-gray-200'}`}>
                      <p className={`text-sm italic ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        <span className="font-semibold">Example:</span> {currentWord.example}
                      </p>
                    </div>
                  )}
                  
                  {/* Category and Difficulty - Subtle badges */}
                  <div className="flex justify-center space-x-3 mt-4">
                    {currentWord.category && currentWord.category !== 'general' && (
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        darkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-700'
                      }`}>
                        {currentWord.category}
                      </span>
                    )}
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      currentWord.difficulty === 'easy' 
                        ? darkMode ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-700'
                        : currentWord.difficulty === 'hard'
                        ? darkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-700'
                        : darkMode ? 'bg-yellow-900/30 text-yellow-300' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {currentWord.difficulty}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-between items-center mt-6">
            <button
              onClick={() => {
                if (currentFlashcard > 0) {
                  setCurrentFlashcard(prev => prev - 1);
                  setShowAnswer(false);
                }
              }}
              disabled={currentFlashcard === 0}
              className={`flex items-center space-x-2 px-4 py-2 rounded-xl font-semibold transition-all duration-300 ${
                currentFlashcard === 0 
                  ? 'opacity-50 cursor-not-allowed' 
                  : darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              <ChevronLeft className="w-4 h-4" />
              <span>Previous</span>
            </button>

            <div className="flex space-x-2">
              <button
                onClick={() => setSpeechEnabled(!speechEnabled)}
                className={`p-2 rounded-xl transition-all duration-300 ${
                  speechEnabled 
                    ? 'bg-gradient-to-r from-blue-500 to-green-500 text-white' 
                    : darkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-200 text-gray-600'
                }`}
              >
                {speechEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              </button>
              <button
                onClick={() => setShowAnswer(!showAnswer)}
                className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-4 py-2 rounded-xl font-semibold hover:scale-105 transition-all duration-300 flex items-center space-x-2"
              >
                <RotateCcw className="w-4 h-4" />
                <span>Flip</span>
              </button>
              <button
                onClick={() => deleteWordFromVocabularyList(currentVocabList.id, currentFlashcard)}
                className={`p-2 rounded-xl transition-all duration-300 hover:scale-105 ${
                  darkMode 
                    ? 'hover:bg-red-900/30 text-red-400 hover:text-red-300' 
                    : 'hover:bg-red-100 text-red-500 hover:text-red-600'
                }`}
                title="Delete this word"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>

            <button
              onClick={() => {
                if (currentFlashcard < currentVocabList.words.length - 1) {
                  setCurrentFlashcard(prev => prev + 1);
                  setShowAnswer(false);
                } else {
                  // Completed all flashcards
                  awardPoints('flashcard_completion', 50);
                  setFlashcardMode(false);
                  // If opened from Smart Upload, return there
                  if (flashcardSource === 'smart-upload') {
                    setTimeout(() => {
                      setShowNotesUpload(true);
                      setFlashcardSource(null); // Reset source
                    }, 300);
                  }
                }
              }}
              className="flex items-center space-x-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-2 rounded-xl font-semibold hover:scale-105 transition-all duration-300"
            >
              <span>{currentFlashcard === currentVocabList.words.length - 1 ? 'Finish' : 'Next'}</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  // NEW: Notes Upload Modal
  const NotesUploadModal = () => {
    const fileInputRef = useRef(null);
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className={`max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6 rounded-3xl ${darkMode ? 'bg-gray-900 border border-gray-700' : 'bg-white border border-gray-200'} shadow-2xl`}>
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>Smart File Upload</h3>
              <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Upload notes or vocabulary - AI automatically detects the type
              </p>
            </div>
            <button
              onClick={() => setShowNotesUpload(false)}
              className={`p-2 rounded-xl ${darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'} transition-colors duration-300`}
            >
              <X className={`w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
            </button>
          </div>

          {/* Info Box */}
          <div className={`p-4 rounded-xl mb-6 ${darkMode ? 'bg-blue-500/10 border border-blue-500/30' : 'bg-blue-50 border border-blue-200'}`}>
            <div className="flex items-start space-x-3">
              <Sparkles className={`w-5 h-5 mt-0.5 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
              <div className={`text-sm ${darkMode ? 'text-blue-300' : 'text-blue-800'}`}>
                <p className="font-semibold mb-1">🎯 Smart Detection Enabled</p>
                <p>• <strong>Vocabulary lists</strong> (words with definitions) → Auto-creates flashcards & displays below</p>
                <p>• <strong>Educational notes</strong> (paragraphs) → Saved below for course generation</p>
              </div>
            </div>
          </div>

          {/* Upload Section */}
          <div className={`p-6 rounded-2xl mb-6 ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
            <h4 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Upload Your Files</h4>
            <div className="flex items-center space-x-4">
              <input
                ref={fileInputRef}
                type="file"
                accept=".txt,.md,.pdf,.ppt,.pptx"
                onChange={async (e) => {
                  const file = e.target.files[0];
                  if (file) {
                    await uploadNotesFile(file);
                    e.target.value = '';
                  }
                }}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="bg-gradient-to-r from-green-500 to-blue-500 text-white px-4 py-2 rounded-xl font-semibold hover:scale-105 transition-all duration-300 flex items-center space-x-2"
              >
                <Upload className="w-4 h-4" />
                <span>Upload Notes</span>
              </button>
              <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Supported: TXT, MD, PDF, PPT, PPTX files
              </div>
            </div>
          </div>

          {/* Uploaded Notes */}
          <div className="grid md:grid-cols-2 gap-4">
            {uploadedNotes.map(note => (
              <div key={note.id} className={`p-4 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-gray-100'} hover:scale-105 transition-all duration-300`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h5 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>{note.name}</h5>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Uploaded: {new Date(note.uploadedAt).toLocaleDateString()}
                    </p>
                  </div>
                  {note.processed && (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  )}
                </div>
                <p className={`text-sm mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {note.content.substring(0, 100)}...
                </p>
                <button
                  onClick={() => generateCourseFromNotes(note.id)}
                  disabled={note.processed || isGenerating}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white py-2 px-3 rounded-lg font-medium hover:scale-105 transition-all duration-300 disabled:opacity-50"
                >
                  {note.processed ? 'Course Generated' : 'Generate Course'}
                </button>
              </div>
            ))}
          </div>

          {uploadedNotes.length === 0 && vocabularyLists.length === 0 && (
            <div className="text-center py-8">
              <FileText className={`w-16 h-16 mx-auto mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
              <p className={`text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                No files uploaded yet. Upload your study notes or vocabulary lists!
              </p>
              <div className={`mt-6 p-4 rounded-lg ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-gray-100 border border-gray-200'} text-left max-w-md mx-auto`}>
                <h5 className={`font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Supported File Types:</h5>
                <ul className={`text-sm space-y-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  <li>• <strong>Plain Text (.txt, .md):</strong> Study notes, lecture notes, textbooks</li>
                  <li>• <strong>PDF Files (.pdf):</strong> Text will be extracted automatically</li>
                  <li>• <strong>PowerPoint (.pptx):</strong> Slide content and speaker notes will be extracted</li>
                </ul>
                <p className={`text-xs mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  💡 AI will analyze your notes and create a personalized learning roadmap!
                </p>
              </div>
            </div>
          )}

          {/* Vocabulary Flashcards Section */}
          {vocabularyLists.length > 0 && (
            <div className="mt-8">
              <h4 className={`text-lg font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                📚 Vocabulary Flashcards
              </h4>
              <div className="grid md:grid-cols-2 gap-4">
                {vocabularyLists.map(list => (
                  <div key={list.id} className={`p-4 rounded-xl ${darkMode ? 'bg-purple-900/20 border border-purple-500/30' : 'bg-purple-50 border border-purple-200'}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h5 className={`font-semibold ${darkMode ? 'text-purple-300' : 'text-purple-800'}`}>
                          {list.name}
                        </h5>
                        <p className={`text-sm ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                          {list.words.length} words
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => {
                            if (window.confirm(`Delete "${list.name}"? This action cannot be undone.`)) {
                              setVocabularyLists(prev => prev.filter(v => v.id !== list.id));
                              if (currentVocabList?.id === list.id) {
                                setCurrentVocabList(null);
                                setFlashcardMode(false);
                              }
                            }
                          }}
                          className={`p-1.5 rounded-lg transition-colors ${
                            darkMode
                              ? 'hover:bg-red-900/30 text-red-400 hover:text-red-300'
                              : 'hover:bg-red-100 text-red-500 hover:text-red-600'
                          }`}
                          title="Delete vocabulary list"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        <BookOpen className={`w-5 h-5 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                      </div>
                    </div>

                    {/* Display first 3 words as preview */}
                    <div className="mb-3 space-y-2">
                      {list.words.slice(0, 3).map((word, idx) => (
                        <div key={idx} className={`p-2 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                          <p className={`font-semibold text-sm ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                            {word.word}
                          </p>
                          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            {word.definition.substring(0, 60)}...
                          </p>
                        </div>
                      ))}
                      {list.words.length > 3 && (
                        <p className={`text-xs text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          +{list.words.length - 3} more words
                        </p>
                      )}
                    </div>

                    <button
                      onClick={() => {
                        setCurrentVocabList(list);
                        setCurrentFlashcard(0);
                        setShowAnswer(false);
                        setFlashcardSource('smart-upload'); // Track source
                        setShowNotesUpload(false); // Close Smart Upload modal first
                        setTimeout(() => {
                          setFlashcardMode(true); // Open flashcard modal after brief delay
                        }, 100);
                      }}
                      className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white py-2 px-3 rounded-lg font-medium hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-2"
                    >
                      <Zap className="w-4 h-4" />
                      <span>Study Flashcards</span>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // NEW: Vocabulary Quiz Modal
  const VocabularyQuizModal = () => {
    const [currentQuizQuestion, setCurrentQuizQuestion] = useState(0);
    const [userAnswer, setUserAnswer] = useState('');
    const [quizResults, setQuizResults] = useState(null);
    const [showResult, setShowResult] = useState(false);
    
    if (!currentVocabList || !vocabularyQuizMode) return null;
    
    const quizData = generateFillInBlankQuiz(currentVocabList);
    const currentQuestion = quizData.questions[currentQuizQuestion];
    
    const checkAnswer = () => {
      const isCorrect = userAnswer.toLowerCase().trim() === currentQuestion.correctAnswer.toLowerCase().trim();
      setShowResult(true);
      
      if (currentQuizQuestion === quizData.questions.length - 1) {
        // Quiz completed
        awardPoints('vocabulary_quiz_completion', 75);
        setQuizResults({ completed: true });
      }
    };
    
    const nextQuestion = () => {
      if (currentQuizQuestion < quizData.questions.length - 1) {
        setCurrentQuizQuestion(prev => prev + 1);
        setUserAnswer('');
        setShowResult(false);
      } else {
        setVocabularyQuizMode(false);
        setCurrentVocabList(null);
      }
    };
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className={`max-w-2xl w-full p-6 rounded-3xl ${darkMode ? 'bg-gray-900 border border-gray-700' : 'bg-white border border-gray-200'} shadow-2xl`}>
          <div className="flex justify-between items-center mb-6">
            <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
              {quizData.title}
            </h3>
            <button
              onClick={() => {
                setVocabularyQuizMode(false);
                setCurrentVocabList(null);
              }}
              className={`p-2 rounded-xl ${darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'} transition-colors duration-300`}
            >
              <X className={`w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
            </button>
          </div>

          <div className="text-center mb-6">
            <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-2`}>
              Question {currentQuizQuestion + 1} of {quizData.questions.length}
            </div>
            <div className={`w-full h-2 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-300'} mb-4`}>
              <div 
                className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all duration-300"
                style={{ width: `${((currentQuizQuestion + 1) / quizData.questions.length) * 100}%` }}
              />
            </div>
          </div>

          <div className={`p-6 rounded-2xl mb-6 ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
            <p className={`text-lg mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
              Fill in the blank:
            </p>
            <p className={`text-xl font-medium mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              {currentQuestion.question}
            </p>
            
            {!showResult ? (
              <div className="space-y-4">
                <input
                  type="text"
                  value={userAnswer}
                  onChange={(e) => setUserAnswer(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && checkAnswer()}
                  placeholder="Enter your answer..."
                  className={`w-full p-3 rounded-xl border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-800'} focus:outline-none focus:border-purple-500`}
                />
                <button
                  onClick={checkAnswer}
                  disabled={!userAnswer.trim()}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 rounded-xl font-semibold hover:scale-105 transition-all duration-300 disabled:opacity-50"
                >
                  Check Answer
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className={`p-4 rounded-xl ${
                  userAnswer.toLowerCase().trim() === currentQuestion.correctAnswer.toLowerCase().trim()
                    ? darkMode ? 'bg-green-900/30 border border-green-500' : 'bg-green-100 border border-green-500'
                    : darkMode ? 'bg-red-900/30 border border-red-500' : 'bg-red-100 border border-red-500'
                }`}>
                  <p className={`font-semibold mb-2 ${
                    userAnswer.toLowerCase().trim() === currentQuestion.correctAnswer.toLowerCase().trim()
                      ? 'text-green-500' : 'text-red-500'
                  }`}>
                    {userAnswer.toLowerCase().trim() === currentQuestion.correctAnswer.toLowerCase().trim() ? '✓ Correct!' : '✗ Incorrect'}
                  </p>
                  <p className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <strong>Correct answer:</strong> {currentQuestion.correctAnswer}
                  </p>
                  <p className={`text-sm mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    <strong>Definition:</strong> {currentQuestion.definition}
                  </p>
                </div>
                
                <button
                  onClick={nextQuestion}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 rounded-xl font-semibold hover:scale-105 transition-all duration-300"
                >
                  {currentQuizQuestion === quizData.questions.length - 1 ? 'Finish Quiz' : 'Next Question'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // NEW: Delete Confirmation Modal
  const DeleteConfirmationModal = () => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`max-w-md w-full p-6 rounded-3xl ${darkMode ? 'bg-gray-900 border border-red-700' : 'bg-white border border-red-300'} shadow-2xl`}>
        <div className="text-center mb-6">
          <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${darkMode ? 'bg-red-900/50' : 'bg-red-100'}`}>
            <AlertTriangle className={`w-8 h-8 ${darkMode ? 'text-red-400' : 'text-red-600'}`} />
          </div>
          <h3 className={`text-xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
            Delete Learning Goal?
          </h3>
          <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-4`}>
            You're about to permanently delete:
          </p>
          <div className={`p-4 rounded-xl ${darkMode ? 'bg-red-900/30 border border-red-700' : 'bg-red-50 border border-red-300'} mb-4`}>
            <h4 className={`font-semibold ${darkMode ? 'text-red-300' : 'text-red-800'}`}>
              "{goalToDelete?.title}"
            </h4>
            <p className={`text-sm mt-1 ${darkMode ? 'text-red-400' : 'text-red-700'}`}>
              {goalToDelete?.description}
            </p>
          </div>
          <div className={`p-4 rounded-xl ${darkMode ? 'bg-yellow-900/30 border border-yellow-700' : 'bg-yellow-100 border border-yellow-300'} text-left`}>
            <h5 className={`font-semibold mb-2 ${darkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>
              ⚠️ This action cannot be undone
            </h5>
            <ul className={`text-sm space-y-1 ${darkMode ? 'text-yellow-200' : 'text-yellow-700'}`}>
              <li>• All progress and completed lessons will be lost</li>
              <li>• Generated quizzes and study materials will be deleted</li>
              <li>• This goal won't appear when you login again</li>
              <li>• Your study statistics will be updated</li>
            </ul>
          </div>
        </div>

        <div className="flex space-x-3">
          <button
            onClick={cancelDeleteGoal}
            className={`flex-1 py-3 rounded-xl font-semibold transition-all duration-300 ${
              darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Cancel
          </button>
          <button
            onClick={confirmDeleteGoal}
            className="flex-1 bg-gradient-to-r from-red-500 to-red-600 text-white py-3 rounded-xl font-semibold hover:scale-105 transition-all duration-300"
          >
            Delete Permanently
          </button>
        </div>
      </div>
    </div>
  );

  // Pomodoro Timer Component
  const PomodoroTimer = () => (
    <div className={`fixed bottom-6 right-6 z-40 ${darkMode ? 'bg-gray-900 border border-gray-700' : 'bg-white border border-gray-200'} rounded-2xl shadow-2xl p-4`}>
      <div className="text-center">
        <div className={`text-xs font-medium mb-2 ${pomodoroType === 'work' ? 'text-green-600' : 'text-blue-600'}`}>
          {pomodoroType === 'work' ? '🍅 Focus Session' : '☕ Break Time'} - Session {pomodoroSession}
        </div>
        
        <div className={`text-2xl font-bold mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
          {formatTime(pomodoroTime)}
        </div>
        
        {/* Progress Ring */}
        <div className="relative w-16 h-16 mx-auto mb-3">
          <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 64 64">
            <circle
              cx="32" cy="32" r="28"
              fill="none"
              stroke={darkMode ? '#374151' : '#e5e7eb'}
              strokeWidth="4"
            />
            <circle
              cx="32" cy="32" r="28"
              fill="none"
              stroke={pomodoroType === 'work' ? '#10b981' : '#3b82f6'}
              strokeWidth="4"
              strokeDasharray={`${2 * Math.PI * 28}`}
              strokeDashoffset={`${2 * Math.PI * 28 * (pomodoroTime / (pomodoroType === 'work' ? customPomodoroSettings.workDuration * 60 : customPomodoroSettings.shortBreak * 60))}`}
              className="transition-all duration-1000"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className={`text-xs font-medium ${pomodoroType === 'work' ? 'text-green-600' : 'text-blue-600'}`}>
              {Math.round((1 - pomodoroTime / (pomodoroType === 'work' ? customPomodoroSettings.workDuration * 60 : customPomodoroSettings.shortBreak * 60)) * 100)}%
            </span>
          </div>
        </div>
        
        <div className="flex space-x-2">
          <button
            onClick={pomodoroActive ? pausePomodoro : startPomodoro}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all duration-300 ${
              pomodoroType === 'work'
                ? 'bg-green-500 hover:bg-green-600 text-white'
                : 'bg-blue-500 hover:bg-blue-600 text-white'
            }`}
          >
            {pomodoroActive ? 'Pause' : 'Start'}
          </button>
          <button
            onClick={resetPomodoro}
            className={`py-2 px-3 rounded-lg text-sm font-medium transition-all duration-300 ${
              darkMode 
                ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
            }`}
          >
            Reset
          </button>
          <button
            onClick={() => setShowPomodoroModal(true)}
            className={`py-2 px-3 rounded-lg text-sm font-medium transition-all duration-300 ${
              darkMode 
                ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
            }`}
          >
            ⚙️
          </button>
        </div>
      </div>
    </div>
  );

  // Pomodoro Settings Modal
  const PomodoroModal = () => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`max-w-md w-full p-6 rounded-3xl ${darkMode ? 'bg-gray-900 border border-gray-700' : 'bg-white border border-gray-200'} shadow-2xl`}>
        <div className="flex justify-between items-center mb-6">
          <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>Pomodoro Settings</h3>
          <button
            onClick={() => setShowPomodoroModal(false)}
            className={`p-2 rounded-xl ${darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'} transition-colors duration-300`}
          >
            <X className={`w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Work Duration (minutes)
            </label>
            <input
              type="number"
              min="10"
              max="60"
              value={customPomodoroSettings.workDuration}
              onChange={(e) => setCustomPomodoroSettings(prev => ({...prev, workDuration: parseInt(e.target.value)}))}
              className={`w-full p-3 rounded-xl border transition-all duration-300 ${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-300 text-gray-800'} focus:outline-none focus:border-purple-500`}
            />
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Short Break (minutes)
            </label>
            <input
              type="number"
              min="3"
              max="15"
              value={customPomodoroSettings.shortBreak}
              onChange={(e) => setCustomPomodoroSettings(prev => ({...prev, shortBreak: parseInt(e.target.value)}))}
              className={`w-full p-3 rounded-xl border transition-all duration-300 ${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-300 text-gray-800'} focus:outline-none focus:border-purple-500`}
            />
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Long Break (minutes)
            </label>
            <input
              type="number"
              min="10"
              max="30"
              value={customPomodoroSettings.longBreak}
              onChange={(e) => setCustomPomodoroSettings(prev => ({...prev, longBreak: parseInt(e.target.value)}))}
              className={`w-full p-3 rounded-xl border transition-all duration-300 ${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-300 text-gray-800'} focus:outline-none focus:border-purple-500`}
            />
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Sessions until Long Break
            </label>
            <input
              type="number"
              min="2"
              max="8"
              value={customPomodoroSettings.sessionsUntilLongBreak}
              onChange={(e) => setCustomPomodoroSettings(prev => ({...prev, sessionsUntilLongBreak: parseInt(e.target.value)}))}
              className={`w-full p-3 rounded-xl border transition-all duration-300 ${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-300 text-gray-800'} focus:outline-none focus:border-purple-500`}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <span className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Auto-start when studying</span>
              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Automatically start timer when entering lessons</p>
            </div>
            <button
              onClick={() => setAutoStartPomodoro(!autoStartPomodoro)}
              className={`relative w-12 h-6 rounded-full transition-colors duration-300 ${autoStartPomodoro ? 'bg-purple-500' : 'bg-gray-300'}`}
            >
              <div className={`absolute w-5 h-5 bg-white rounded-full top-0.5 transition-transform duration-300 ${autoStartPomodoro ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </button>
          </div>
        </div>

        <div className="flex space-x-3 mt-6">
          <button
            onClick={() => setShowPomodoroModal(false)}
            className={`flex-1 py-3 rounded-xl font-semibold transition-all duration-300 ${
              darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Cancel
          </button>
          <button
            onClick={() => {
              // Apply settings and reset timer
              setPomodoroTime(customPomodoroSettings.workDuration * 60);
              setPomodoroType('work');
              setShowPomodoroModal(false);
            }}
            className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 rounded-xl font-semibold hover:scale-105 transition-all duration-300"
          >
            Apply Settings
          </button>
        </div>
      </div>
    </div>
  );

  // Quiz Help Modal
  const QuizHelpModal = () => {
    if (!currentQuiz || helpQuestion === null) return null;
    
    const question = currentQuiz.questions[helpQuestion];
    if (!question) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className={`max-w-lg w-full p-6 rounded-3xl ${darkMode ? 'bg-gray-900 border border-gray-700' : 'bg-white border border-gray-200'} shadow-2xl`}>
          <div className="text-center mb-6">
            <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${darkMode ? 'bg-blue-900/50' : 'bg-blue-100'}`}>
              <MessageCircle className={`w-8 h-8 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
            </div>
            <h3 className={`text-xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
              Need Help with This Question?
            </h3>
            <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              You've been working on this question for a while. Would you like some guidance?
            </p>
          </div>

          <div className={`p-4 rounded-xl mb-6 ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
            <p className={`font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
              Question: {question.question}
            </p>
            <div className="space-y-2">
              {question.options.map((option, index) => (
                <div key={index} className={`p-2 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                  <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {String.fromCharCode(65 + index)}. {option}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className={`p-4 rounded-xl mb-6 ${darkMode ? 'bg-yellow-900/30 border border-yellow-700' : 'bg-yellow-100 border border-yellow-300'}`}>
            <p className={`text-sm ${darkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>
              ⚠️ If you accept help, this question will be marked incorrect, but you'll receive a detailed step-by-step explanation.
            </p>
          </div>

          <div className="flex space-x-3">
            <button
              onClick={declineHelp}
              className={`flex-1 py-3 rounded-xl font-semibold transition-all duration-300 ${
                darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              No, I'll Keep Trying
            </button>
            <button
              onClick={acceptHelp}
              className="flex-1 bg-gradient-to-r from-blue-500 to-purple-500 text-white py-3 rounded-xl font-semibold hover:scale-105 transition-all duration-300"
            >
              Yes, Help Me Learn
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Schedule Modal
  const ScheduleModal = () => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`max-w-md w-full p-6 rounded-3xl ${darkMode ? 'bg-gray-900 border border-gray-700' : 'bg-white border border-gray-200'} shadow-2xl`}>
        <div className="text-center mb-6">
          <Calendar className={`w-12 h-12 mx-auto mb-4 ${darkMode ? 'text-purple-400' : 'text-purple-500'}`} />
          <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>Plan Your Study Schedule</h3>
          <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'} mt-2`}>
            Select the days you want to study each week
          </p>
        </div>

        <div className="space-y-3 mb-6">
          {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day, index) => (
            <label key={day} className={`flex items-center justify-between p-3 rounded-xl cursor-pointer transition-all duration-300 ${
              selectedDays.includes(index)
                ? 'bg-purple-500 text-white'
                : darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}>
              <span className="font-medium">{day}</span>
              <input
                type="checkbox"
                checked={selectedDays.includes(index)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedDays(prev => [...prev, index]);
                  } else {
                    setSelectedDays(prev => prev.filter(d => d !== index));
                  }
                }}
                className="hidden"
              />
              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                selectedDays.includes(index) ? 'border-white bg-white' : 'border-gray-400'
              }`}>
                {selectedDays.includes(index) && <CheckCircle className="w-4 h-4 text-purple-500" />}
              </div>
            </label>
          ))}
        </div>

        <div className={`p-4 rounded-xl mb-6 ${darkMode ? 'bg-blue-900/30 border border-blue-700' : 'bg-blue-100 border border-blue-300'}`}>
          <p className={`text-sm ${darkMode ? 'text-blue-300' : 'text-blue-800'}`}>
            📊 Recommended: 3-5 days per week for optimal learning retention
          </p>
          <p className={`text-xs mt-1 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
            Selected: {selectedDays.length} days per week
          </p>
        </div>

        <div className="flex space-x-3">
          <button
            onClick={() => {
              setShowScheduleModal(false);
              setSelectedDays([]);
            }}
            className={`flex-1 py-3 rounded-xl font-semibold transition-all duration-300 ${
              darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Cancel
          </button>
          <button
            onClick={() => {
              if (selectedDays.length > 0) {
                const schedule = generateStudySchedule(selectedDays, '30 days');
                setStudySchedule(schedule);
                setShowScheduleModal(false);
                generateAIRoadmap();
              }
            }}
            disabled={selectedDays.length === 0}
            className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 rounded-xl font-semibold hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:hover:scale-100"
          >
            Create Plan
          </button>
        </div>
      </div>
    </div>
  );

  const startSession = () => {
    setCurrentSession({ start: Date.now(), duration: 0 });
  };

  const endSession = () => {
    if (currentSession.start) {
      const sessionDuration = Date.now() - currentSession.start;
      setTotalStudyTime(prev => prev + sessionDuration);
      
      // Award points based on study time (1 point per minute)
      const studyMinutes = Math.floor(sessionDuration / 60000);
      if (studyMinutes > 0) {
        awardPoints('study_time', studyMinutes);
      }
      
      updateDailyProgress();
      setCurrentSession({ start: null, duration: 0 });
    }
  };

  const updateDailyProgress = () => {
    const today = new Date().toISOString().split('T')[0];
    const isFirstSessionToday = !dailyProgress[today];
    
    setDailyProgress(prev => ({
      ...prev,
      [today]: (prev[today] || 0) + 1
    }));
    setLastActivityDate(today);
    
    // Award points for daily study (only first session of the day gets points)
    if (isFirstSessionToday) {
      awardPoints('daily_study', 25);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const todayStr = today.toISOString().split('T')[0];
    const yesterdayStr = yesterday.toISOString().split('T')[0];
    
    if (dateString === todayStr) return 'Today';
    if (dateString === yesterdayStr) return 'Yesterday';
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getDaysUntilNext = () => {
    if (!studySchedule) return null;
    
    const today = new Date();
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    
    for (let i = 1; i <= 7; i++) {
      const nextDate = new Date(today);
      nextDate.setDate(nextDate.getDate() + i);
      const dayName = dayNames[nextDate.getDay()];
      
      if (studySchedule.weekly_pattern[dayName]?.active) {
        return i === 1 ? 'Tomorrow' : `In ${i} days (${dayName})`;
      }
    }
    return null;
  };

  const completeLesson = async (lessonIndex) => {
    const newCompletedLessons = new Set([...completedLessons, lessonIndex]);
    setCompletedLessons(newCompletedLessons);
    
    const newProgress = Math.min(100, (newCompletedLessons.size / (currentRoadmap?.lessons?.length || 1)) * 100);
    setProgress(newProgress);
    
    // Award points for lesson completion
    awardPoints('lesson_completion', 50);
    
    // Update current roadmap
    if (currentRoadmap) {
      const updatedRoadmap = {
        ...currentRoadmap,
        progress: newProgress,
        completedLessons: Array.from(newCompletedLessons)
      };
      setCurrentRoadmap(updatedRoadmap);
      
      // Update in user goals
      const updatedGoals = userGoals.map(goal => 
        goal.id === currentRoadmap.id ? updatedRoadmap : goal
      );
      setUserGoals(updatedGoals);
      
      // Check if course is completed
      if (newProgress >= 100) {
        awardPoints('course_completion', 500);
        
        // Generate comprehensive analytics
        const analytics = await generateCourseAnalytics(currentRoadmap.id);
        setCourseAnalytics(prev => ({
          ...prev,
          [currentRoadmap.id]: analytics
        }));
        
        // Show analytics modal
        setCurrentAnalytics(analytics);
        setShowAnalyticsModal(true);
      }
    }
    
    // Update user profile
    if (currentUser) {
      const updatedUser = {
        ...currentUser,
        profile: {
          ...currentUser.profile,
          completedLessons: newCompletedLessons.size
        }
      };
      setCurrentUser(updatedUser);
    }
    
    endSession();
  };

  // Voice recognition simulation
  // startListening is now handled locally in OnboardingPage

  // Enhanced Onboarding Page
  const OnboardingPage = () => {
    const [learningGoalBuffer, setLearningGoalBuffer] = useState("");

    // If you want to support voice demo, update buffer and main goal
    const handleStartListening = () => {
      setIsListening(true);
      setTimeout(() => {
        const demoText = "Learn Python to build a website in 30 days";
        setLearningGoalBuffer(demoText);
        setLearningGoal(demoText);
        setIsListening(false);
      }, 3000);
    };

    return (
      <div className={`min-h-screen transition-all duration-500 ${darkMode ? 'bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900' : 'bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50'}`}>
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto">
            <button
              onClick={() => setCurrentView(isAuthenticated ? 'dashboard' : 'landing')}
              className={`mb-8 p-2 rounded-xl transition-all duration-300 ${darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-800'}`}
            >
              <ChevronLeft className="w-6 h-6" />
            </button>

            <div className="text-center mb-12">
              <h2 className={`text-4xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                What do you want to learn?
              </h2>
              <p className={`text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Tell us your goal and our AI will create a personalized learning path just for you
              </p>
            </div>

            <div className={`p-8 rounded-3xl backdrop-blur-sm ${darkMode ? 'bg-white/10 border border-white/20' : 'bg-white/70 border border-white/50'} shadow-xl mb-8`}>
              <div className="relative">
                <textarea
                  value={learningGoalBuffer}
                  onChange={(e) => setLearningGoalBuffer(e.target.value)}
                  placeholder="e.g., SAT test prep for college admission, AP Biology exam preparation, Learn Python to build a website in 30 days, Master digital marketing for my startup, NEET preparation for medical entrance"
                  className={`w-full p-4 rounded-2xl border-2 transition-all duration-300 resize-none ${darkMode ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-400 focus:border-purple-500' : 'bg-white border-gray-200 text-gray-800 placeholder-gray-500 focus:border-purple-500'} focus:outline-none`}
                  rows={4}
                  data-tutorial="learning-goal-input"
                />
                <button
                  onClick={handleStartListening}
                  className={`absolute bottom-4 right-4 p-2 rounded-xl transition-all duration-300 ${isListening ? 'bg-red-500 text-white' : darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                  {isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                </button>
              </div>

              {isListening && (
                <div className="mt-4 text-center">
                  <div className="flex justify-center space-x-1 mb-2">
                    {[0, 1, 2].map(i => (
                      <div key={i} className={`w-2 h-8 rounded-full animate-pulse ${darkMode ? 'bg-purple-400' : 'bg-purple-500'}`} style={{animationDelay: `${i * 0.2}s`}} />
                    ))}
                  </div>
                  <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Listening...</p>
                </div>
              )}
            </div>

            <button
              onClick={() => {
                const goal = (learningGoal || learningGoalBuffer).trim();
                if (goal) {
                  setLearningGoal(goal);
                  setShowScheduleModal(true);
                }
              }}
              disabled={!(learningGoal || learningGoalBuffer).trim() || isGenerating}
              className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-4 rounded-2xl text-lg font-semibold hover:scale-105 transition-all duration-300 shadow-xl disabled:opacity-50 disabled:hover:scale-100 flex items-center justify-center space-x-2"
              data-tutorial="create-learning-goal-button"
            >
              {isGenerating ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>AI is creating your learning path...</span>
                </>
              ) : (
                <>
                  <Calendar className="w-5 h-5" />
                  <span>Plan Your Study Schedule</span>
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>

            {/* Alternative Learning Methods */}
            <div className="mt-8">
              <div className={`text-center mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                <p className="text-sm font-medium">Or create a learning path from:</p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {/* Upload Notes Button */}
                <button
                  onClick={() => setShowNotesUpload(true)}
                  data-tutorial="upload-notes"
                  className={`p-6 rounded-2xl border-2 transition-all duration-300 hover:scale-105 ${
                    darkMode
                      ? 'bg-white/5 border-white/20 hover:bg-white/10'
                      : 'bg-white border-gray-200 hover:border-purple-300'
                  }`}
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-r from-green-500 to-blue-500 flex items-center justify-center">
                      <Upload className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-left">
                      <h3 className={`font-bold text-lg ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                        Upload Notes
                      </h3>
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        PDF, TXT, or MD files
                      </p>
                    </div>
                  </div>
                </button>

                {/* YouTube Upload Button */}
                <button
                  onClick={() => setShowYouTubeUpload(true)}
                  data-tutorial="youtube-upload"
                  className={`p-6 rounded-2xl border-2 transition-all duration-300 hover:scale-105 ${
                    darkMode
                      ? 'bg-white/5 border-white/20 hover:bg-white/10'
                      : 'bg-white border-gray-200 hover:border-red-300'
                  }`}
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-r from-red-500 to-pink-500 flex items-center justify-center">
                      <Youtube className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-left">
                      <h3 className={`font-bold text-lg ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                        YouTube Video
                      </h3>
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Learn from any video
                      </p>
                    </div>
                  </div>
                </button>

                {/* Discover Feed Button */}
                <button
                  onClick={() => setShowDiscoverFeed(true)}
                  data-tutorial="discover-feed"
                  className={`p-6 rounded-2xl border-2 transition-all duration-300 hover:scale-105 ${
                    darkMode
                      ? 'bg-white/5 border-white/20 hover:bg-white/10'
                      : 'bg-white border-gray-200 hover:border-purple-300'
                  }`}
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                      <Sparkles className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-left">
                      <h3 className={`font-bold text-lg ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                        Discover
                      </h3>
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Learn from peers
                      </p>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const RoadmapPage = () => {
    if (!currentRoadmap) return <div>Loading...</div>;
    
    return (
      <div className={`min-h-screen transition-all duration-500 ${darkMode ? 'bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900' : 'bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50'}`}>
        <div className="container mx-auto px-4 py-8">
          <div className="flex justify-between items-center mb-8">
            <button
              onClick={() => setCurrentView(isAuthenticated ? 'dashboard' : 'onboarding')}
              className={`p-2 rounded-xl transition-all duration-300 ${darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-800'}`}
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <div className="flex items-center space-x-4">
              <div className={`flex items-center space-x-2 px-4 py-2 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
                <Award className={`w-5 h-5 ${darkMode ? 'text-yellow-400' : 'text-yellow-500'}`} />
                <span className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>{currentUser?.profile.streak || 0} day streak</span>
              </div>
              <button
                onClick={() => setCurrentView('chat')}
                className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-2 rounded-xl font-semibold hover:scale-105 transition-all duration-300 shadow-lg flex items-center space-x-2"
                data-tutorial="ai-tutor"
              >
                <MessageCircle className="w-5 h-5" />
                <span>AI Tutor ({questionsLeft}/10)</span>
              </button>
            </div>
          </div>

          <div className="mb-8">
            <h1 className={`text-4xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
              {currentRoadmap.title}
            </h1>
            <p className={`text-lg mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {currentRoadmap.description}
            </p>
            
            <div className="flex items-center space-x-6 mb-6">
              <div className={`flex items-center space-x-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                <Calendar className="w-5 h-5" />
                <span>{currentRoadmap.duration}</span>
              </div>
              <div className={`flex items-center space-x-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                <BarChart3 className="w-5 h-5" />
                <span>{Math.round(progress)}% Complete</span>
              </div>
              <div className={`flex items-center space-x-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                <BookOpen className="w-5 h-5" />
                <span>{completedLessons.size}/{currentRoadmap.lessons?.length || 0} Lessons</span>
              </div>
            </div>
            
            <div className={`w-full h-3 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} overflow-hidden`}>
              <div
                className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* SAT Practice Test Section - Show ONLY for SAT learning goals */}
          {currentRoadmap.testType === 'SAT' && (
            <div className={`mb-8 p-6 rounded-3xl backdrop-blur-sm ${darkMode ? 'bg-blue-500/10 border border-blue-500/30' : 'bg-blue-50 border border-blue-200'} shadow-xl`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center">
                      <FileText className="w-6 h-6 text-white" />
                    </div>
                    <h3 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                      SAT Practice Tests
                    </h3>
                  </div>
                  <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Access real SAT practice questions from the OpenSAT database. Choose by domain (Math, Reading, Writing), difficulty level, and test length.
                  </p>

                  {satTestResults.length > 0 && (
                    <div className={`mb-4 p-4 rounded-lg ${darkMode ? 'bg-white/5' : 'bg-white/50'}`}>
                      <p className={`text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Recent Practice Tests:
                      </p>
                      <div className="space-y-2">
                        {satTestResults.slice(-3).reverse().map((result, idx) => (
                          <div key={idx} className={`flex items-center justify-between text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            <span>{result.domain === 'all' ? 'Mixed' : result.domain} - {result.difficulty === 'all' ? 'Mixed' : result.difficulty}</span>
                            <span className={`font-bold ${result.percentage >= 70 ? 'text-green-500' : result.percentage >= 50 ? 'text-yellow-500' : 'text-red-500'}`}>
                              {result.percentage}% ({result.correct}/{result.total})
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <button
                    onClick={() => setShowSATPracticeTest(true)}
                    className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white px-6 py-3 rounded-xl font-semibold hover:scale-105 transition-all duration-300 shadow-lg flex items-center space-x-2"
                  >
                    <Target className="w-5 h-5" />
                    <span>Start SAT Practice Test</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ACT Practice Test Section - Show ONLY for ACT learning goals */}
          {currentRoadmap.testType === 'ACT' && (
            <div className={`mb-8 p-6 rounded-3xl backdrop-blur-sm ${darkMode ? 'bg-purple-500/10 border border-purple-500/30' : 'bg-purple-50 border border-purple-200'} shadow-xl`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                      <FileText className="w-6 h-6 text-white" />
                    </div>
                    <h3 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                      ACT Practice Tests
                    </h3>
                  </div>
                  <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Access curated ACT practice questions across all four sections. Choose by section (English, Math, Reading, Science), difficulty level, and test mode.
                  </p>

                  {actTestResults.length > 0 && (
                    <div className={`mb-4 p-4 rounded-lg ${darkMode ? 'bg-white/5' : 'bg-white/50'}`}>
                      <p className={`text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Recent Practice Tests:
                      </p>
                      <div className="space-y-2">
                        {actTestResults.slice(-3).reverse().map((result, idx) => (
                          <div key={idx} className={`flex items-center justify-between text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            <span>{result.domain === 'all' ? 'Mixed' : result.domain} - {result.difficulty === 'all' ? 'Mixed' : result.difficulty}</span>
                            <span className={`font-bold ${result.actScore >= 30 ? 'text-green-500' : result.actScore >= 24 ? 'text-yellow-500' : 'text-red-500'}`}>
                              ACT Score: {result.actScore}/36
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <button
                    onClick={() => setShowACTPracticeTest(true)}
                    className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-xl font-semibold hover:scale-105 transition-all duration-300 shadow-lg flex items-center space-x-2"
                  >
                    <Target className="w-5 h-5" />
                    <span>Start ACT Practice Test</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* YouTube Flashcards Section - Show when flashcards are generated */}
          {currentRoadmap.flashcards && currentRoadmap.flashcards.length > 0 && (
            <div className={`mb-8 p-6 rounded-3xl backdrop-blur-sm ${darkMode ? 'bg-purple-500/10 border border-purple-500/30' : 'bg-purple-50 border border-purple-200'} shadow-xl`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                      <span className="text-2xl">🗂️</span>
                    </div>
                    <h3 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                      Video Flashcards
                    </h3>
                  </div>
                  <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Master key concepts, formulas, and definitions from the video with AI-generated flashcards.
                  </p>

                  <div className={`mb-4 p-4 rounded-lg ${darkMode ? 'bg-white/5' : 'bg-white/50'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <p className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {currentRoadmap.flashcards.length} Flashcards Generated
                      </p>
                      <div className="flex gap-2 text-xs">
                        <span className={`px-2 py-1 rounded ${darkMode ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-700'}`}>
                          {currentRoadmap.flashcards.filter(f => f.category === 'formula').length} Formulas
                        </span>
                        <span className={`px-2 py-1 rounded ${darkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-700'}`}>
                          {currentRoadmap.flashcards.filter(f => f.category === 'definition').length} Definitions
                        </span>
                        <span className={`px-2 py-1 rounded ${darkMode ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700'}`}>
                          {currentRoadmap.flashcards.filter(f => f.category === 'concept').length} Concepts
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-48 overflow-y-auto">
                      {currentRoadmap.flashcards.slice(0, 6).map((card, idx) => (
                        <div
                          key={idx}
                          className={`p-3 rounded-lg border ${darkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'} hover:scale-105 transition-transform cursor-pointer`}
                        >
                          <div className="flex items-start gap-2">
                            <span className="text-lg">
                              {card.category === 'formula' ? '📐' : card.category === 'definition' ? '📖' : '💡'}
                            </span>
                            <div className="flex-1">
                              <p className={`text-sm font-medium line-clamp-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                {card.front}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <button
                    onClick={() => {
                      // Create a vocabulary list from flashcards for the existing flashcard system
                      const vocabList = {
                        id: `flashcards-${currentRoadmap.title}`,
                        title: `${currentRoadmap.title} - Key Concepts`,
                        words: currentRoadmap.flashcards.map((card, idx) => ({
                          id: idx,
                          word: card.front,
                          definition: card.back,
                          mastered: false
                        }))
                      };
                      setCurrentVocabList(vocabList);
                      setFlashcardMode(true);
                    }}
                    className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-xl font-semibold hover:scale-105 transition-all duration-300 shadow-lg flex items-center space-x-2"
                  >
                    <BookOpen className="w-5 h-5" />
                    <span>Study Flashcards ({currentRoadmap.flashcards.length})</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* NEET Practice Test Section - Show ONLY for NEET learning goals */}
          {currentRoadmap.testType === 'NEET' && (
            <div className={`mb-8 p-6 rounded-3xl backdrop-blur-sm ${darkMode ? 'bg-green-500/10 border border-green-500/30' : 'bg-green-50 border border-green-200'} shadow-xl`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center">
                      <FileText className="w-6 h-6 text-white" />
                    </div>
                    <h3 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                      NEET Practice Tests
                    </h3>
                  </div>
                  <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Access curated NEET practice questions across Physics, Chemistry, and Biology. Prepare for medical entrance with section-wise and full-length tests.
                  </p>
                  <button
                    onClick={() => setShowNEETPracticeTest(true)}
                    className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-3 rounded-xl font-semibold hover:scale-105 transition-all duration-300 shadow-lg flex items-center space-x-2"
                  >
                    <Target className="w-5 h-5" />
                    <span>Start NEET Practice Test</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* JEE Practice Test Section - Show ONLY for JEE learning goals */}
          {currentRoadmap.testType === 'JEE' && (
            <div className={`mb-8 p-6 rounded-3xl backdrop-blur-sm ${darkMode ? 'bg-orange-500/10 border border-orange-500/30' : 'bg-orange-50 border border-orange-200'} shadow-xl`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center">
                      <FileText className="w-6 h-6 text-white" />
                    </div>
                    <h3 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                      JEE Practice Tests
                    </h3>
                  </div>
                  <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Access curated JEE practice questions across Physics, Chemistry, and Mathematics. Prepare for engineering entrance with targeted practice.
                  </p>
                  <button
                    onClick={() => setShowJEEPracticeTest(true)}
                    className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-3 rounded-xl font-semibold hover:scale-105 transition-all duration-300 shadow-lg flex items-center space-x-2"
                  >
                    <Target className="w-5 h-5" />
                    <span>Start JEE Practice Test</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Code Interpreter Section - Show for programming learning goals */}
          {((currentRoadmap.title?.toLowerCase().includes('programming') ||
            currentRoadmap.title?.toLowerCase().includes('python') ||
            currentRoadmap.title?.toLowerCase().includes('javascript') ||
            currentRoadmap.title?.toLowerCase().includes('java') ||
            currentRoadmap.title?.toLowerCase().includes('c++') ||
            currentRoadmap.title?.toLowerCase().includes('coding') ||
            currentRoadmap.title?.toLowerCase().includes('software') ||
            currentRoadmap.title?.toLowerCase().includes('developer') ||
            currentRoadmap.description?.toLowerCase().includes('programming') ||
            currentRoadmap.description?.toLowerCase().includes('python') ||
            currentRoadmap.description?.toLowerCase().includes('javascript') ||
            currentRoadmap.description?.toLowerCase().includes('java') ||
            currentRoadmap.description?.toLowerCase().includes('c++') ||
            currentRoadmap.description?.toLowerCase().includes('coding') ||
            currentRoadmap.description?.toLowerCase().includes('software') ||
            currentRoadmap.description?.toLowerCase().includes('developer'))) && (
            <div className={`mb-8 p-6 rounded-3xl backdrop-blur-sm ${darkMode ? 'bg-cyan-500/10 border border-cyan-500/30' : 'bg-cyan-50 border border-cyan-200'} shadow-xl`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center">
                      <Code className="w-6 h-6 text-white" />
                    </div>
                    <h3 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                      Interactive Code Interpreter
                    </h3>
                  </div>
                  <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Practice coding with AI-generated challenges. Write, run, and test your code in real-time across multiple programming languages.
                  </p>
                  <div className={`mb-4 p-4 rounded-xl ${darkMode ? 'bg-cyan-900/20' : 'bg-cyan-100/50'}`}>
                    <p className={`text-sm font-semibold mb-2 ${darkMode ? 'text-cyan-300' : 'text-cyan-800'}`}>
                      ✨ Features:
                    </p>
                    <ul className={`text-sm space-y-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <li>• AI-generated coding challenges with test cases</li>
                      <li>• Support for Python, JavaScript, Java, C++, and more</li>
                      <li>• Real-time code execution and validation</li>
                      <li>• Hints and solutions when you need help</li>
                    </ul>
                  </div>
                  <button
                    onClick={() => {
                      analytics.trackFeatureUsage('code_interpreter_opened');
                      setShowCodeInterpreter(true);
                    }}
                    className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-6 py-3 rounded-xl font-semibold hover:scale-105 transition-all duration-300 shadow-lg flex items-center space-x-2"
                  >
                    <Code className="w-5 h-5" />
                    <span>Open Code Interpreter</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="grid gap-6">
            {currentRoadmap.lessons?.map((lesson, index) => (
              <div 
                key={index}
                className={`p-6 rounded-3xl backdrop-blur-sm transition-all duration-300 hover:scale-105 cursor-pointer ${
                  completedLessons.has(index) 
                    ? darkMode ? 'bg-green-500/20 border border-green-500/30' : 'bg-green-500/10 border border-green-500/20'
                    : darkMode ? 'bg-white/10 border border-white/20' : 'bg-white/70 border border-white/50'
                } shadow-xl`}
                onClick={() => {
                  setCurrentLesson(index);
                  setCurrentView('lesson');
                  startSession();
                }}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        completedLessons.has(index) 
                          ? 'bg-green-500 text-white' 
                          : darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'
                      }`}>
                        {completedLessons.has(index) ? <CheckCircle className="w-5 h-5" /> : lesson.day}
                      </div>
                      <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                        Day {lesson.day}: {lesson.title}
                      </h3>
                    </div>
                    <p className={`mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {lesson.description}
                    </p>
                    <div className={`flex items-center space-x-4 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      <div className="flex items-center space-x-1">
                        <Clock className="w-4 h-4" />
                        <span>{lesson.duration}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <BookOpen className="w-4 h-4" />
                        <span>{lesson.tasks?.length || 0} tasks</span>
                      </div>
                    </div>
                  </div>
                  <ChevronRight className={`w-6 h-6 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                </div>
              </div>
            )) || (
              <div className={`p-8 rounded-3xl backdrop-blur-sm ${darkMode ? 'bg-white/10 border border-white/20' : 'bg-white/70 border border-white/50'} shadow-xl text-center`}>
                <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>No lessons available yet.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // FIXED: Quiz functions moved to main component level to prevent state reset
  const lesson = currentRoadmap?.lessons?.[currentLesson];

  // Timer effect for current question - FIXED: Removed problematic dependencies
  // Note: hintShown intentionally excluded from dependencies to prevent constant re-renders
  useEffect(() => {
    let interval = null;
    if (quizStarted && !quizCompleted && questionStartTime) {
      interval = setInterval(() => {
        const timeSpent = (Date.now() - questionStartTime) / 1000;
        setTimeSpentOnQuestion(timeSpent);
        
        // Show hint if user is stuck (after 45 seconds) - using refs to avoid dependency issues
        if (timeSpent > 45) {
          setShowHint(prev => {
            // Only show hint if not already shown for this question
            if (!hintShown[currentQuestionIndex] && !prev) {
              return true;
            }
            return prev;
          });
        }
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [quizStarted, quizCompleted, questionStartTime, currentQuestionIndex]);

  // Check for existing quiz on component mount - FIXED: removed lessonQuizzes from dependencies
  useEffect(() => {
    if (lesson && currentRoadmap && !quizInitialized) {
      const quizKey = `${currentRoadmap.id}_lesson_${currentLesson}`;
      const existingQuiz = lessonQuizzes[quizKey];
      if (existingQuiz) {
        setLessonQuiz(existingQuiz);
      }
      setQuizInitialized(true);
    }
  }, [lesson, currentRoadmap, currentLesson, quizInitialized]);

  // FIXED: Reset quiz state when lesson changes to prevent glitching
  useEffect(() => {
    // Reset quiz state when lesson changes
    setLessonQuiz(null);
    setQuizStarted(false);
    setQuizCompleted(false);
    setCurrentQuestionIndex(0);
    setUserAnswers({});
    setQuestionStartTime(null);
    setShowHint(false);
    setHintShown({});
    setTimeSpentOnQuestion(0);
    setQuizResults(null);
    setQuizFeedback(null);
    setQuizLoading(false);
    setQuizInitialized(false);
  }, [currentLesson, currentRoadmap?.id]);

  // FIXED: Quiz functions moved to main component level
  const generateLessonQuiz = async () => {
    if (!lesson || !currentRoadmap || quizLoading) return;
    
    // Create a unique key for this specific lesson
    const quizKey = `${currentRoadmap.id}_lesson_${currentLesson}`;
    
    // Check if quiz already exists - if so, just set it and start immediately
    if (lessonQuizzes[quizKey]) {
      setLessonQuiz(lessonQuizzes[quizKey]);
      setQuizStarted(true);
      setCurrentQuestionIndex(0);
      setQuestionStartTime(Date.now());
      setUserAnswers({});
      setHintShown({});
      setShowHint(false);
      setQuizCompleted(false);
      setQuizResults(null);
      setQuizFeedback(null);
      setTimeSpentOnQuestion(0);
      return;
    }

    // FIXED: Set loading state immediately for better UX
    setQuizLoading(true);
    
    // FIXED: Use setTimeout to ensure UI updates before heavy computation
    setTimeout(async () => {

    try {
      const messages = [
        {
          role: 'system',
          content: `Create a comprehensive quiz specifically for this lesson content. Return JSON with this structure:
          {
            "estimatedMinutes": 8,
            "questions": [
              {
                "question": "Question text directly related to the lesson content",
                "options": ["Option A", "Option B", "Option C", "Option D"],
                "correct": 0,
                "difficulty": "easy|medium|hard",
                "estimatedSeconds": 90,
                "hint": "Helpful hint when user is stuck",
                "explanation": {
                  "correct_reason": "Why the correct answer is right",
                  "incorrect_reasons": {
                    "0": "Why option A is wrong (if not correct)",
                    "1": "Why option B is wrong (if not correct)", 
                    "2": "Why option C is wrong (if not correct)",
                    "3": "Why option D is wrong (if not correct)"
                  },
                  "concept": "Key concept being tested",
                  "step_by_step": [
                    "Step 1: Identify the key information from the lesson",
                    "Step 2: Apply the concept learned", 
                    "Step 3: Eliminate incorrect options",
                    "Step 4: Select the best answer"
                  ]
                }
              }
            ]
          }
          
          Make each question UNIQUE and test understanding of the specific lesson content. Include realistic time estimates and helpful hints.`
        },
        {
          role: 'user',
          content: `Create 6-7 multiple choice questions specifically for this lesson:

          Lesson Title: "${lesson.title}"
          Day: ${lesson.day}
          Lesson Content: "${lesson.content}"
          Lesson Tasks: "${lesson.tasks?.join(', ') || ''}"

          Requirements:
          1. Questions must test understanding of THIS specific lesson on Day ${lesson.day}
          2. Include realistic time estimates for each question
          3. Provide helpful hints for when students get stuck
          4. Include detailed explanations for correct and incorrect answers
          5. Mix of difficulty levels (easy, medium, hard)
          6. Cover different aspects of the lesson content
          7. Make questions unique to Day ${lesson.day} content`
        }
      ];

      const response = await makeOpenAIRequest(messages, 2500);
      let quizData;
      
      try {
        quizData = JSON.parse(response);
      } catch (parseError) {
        console.error('Error parsing quiz JSON:', parseError);
        throw new Error('Invalid quiz format received');
      }
      
      // Validate quiz structure
      if (!quizData.questions || !Array.isArray(quizData.questions) || quizData.questions.length === 0) {
        throw new Error('Invalid quiz structure');
      }
      
      // Store the quiz with unique key
      const updatedQuizzes = {
        ...lessonQuizzes,
        [quizKey]: quizData
      };
      setLessonQuizzes(updatedQuizzes);
      setLessonQuiz(quizData);
      
      // Save immediately to prevent loss
      if (currentUser) {
        saveCurrentSession();
      }
      
      setQuizLoading(false);
      
      // Immediately start the quiz after generation
      setQuizStarted(true);
      setCurrentQuestionIndex(0);
      setQuestionStartTime(Date.now());
      setUserAnswers({});
      setHintShown({});
      setShowHint(false);
      setQuizCompleted(false);
      setQuizResults(null);
      setQuizFeedback(null);
      setTimeSpentOnQuestion(0);
      
    } catch (error) {
      console.error('Error generating lesson quiz:', error);
      
      // Enhanced fallback quiz specific to the lesson
      const fallbackQuiz = {
        estimatedMinutes: 6,
        questions: [
          {
            question: `Based on Day ${lesson.day}: "${lesson.title}", what is the main learning objective?`,
            options: ["Basic understanding", "Advanced implementation", "Practical application", "Conceptual overview"],
            correct: 2,
            difficulty: "medium",
            estimatedSeconds: 90,
            hint: "Think about what hands-on skills this lesson is trying to teach you.",
            explanation: {
              correct_reason: "The lesson focuses on practical application of the concepts taught",
              incorrect_reasons: {
                "0": "This goes beyond basic understanding to practical skills",
                "1": "This is introductory level, not advanced implementation",
                "3": "While conceptual, the emphasis is on practical application"
              },
              concept: `Core learning objective of ${lesson.title}`,
              step_by_step: [
                "Step 1: Review the lesson title and content",
                "Step 2: Identify the practical skills being taught",
                "Step 3: Consider the hands-on nature of the tasks",
                "Step 4: Select the option that emphasizes practical application"
              ]
            }
          },
          {
            question: `What is the primary focus of Day ${lesson.day}?`,
            options: ["Theory only", "Practice and application", "Memorization", "Reading comprehension"],
            correct: 1,
            difficulty: "easy",
            estimatedSeconds: 60,
            hint: "Look at the tasks and content to see what type of learning is emphasized.",
            explanation: {
              correct_reason: "The lesson emphasizes hands-on practice and real-world application",
              incorrect_reasons: {
                "0": "The lesson includes practical tasks, not just theory",
                "2": "The focus is on understanding and applying, not memorizing",
                "3": "While reading is involved, the emphasis is on doing"
              },
              concept: `Learning approach for ${lesson.title}`,
              step_by_step: [
                "Step 1: Examine the lesson tasks",
                "Step 2: Look for hands-on activities",
                "Step 3: Identify practical applications",
                "Step 4: Choose the option that best describes the approach"
              ]
            }
          }
        ]
      };
      
      // Store the fallback quiz
      const updatedQuizzes = {
        ...lessonQuizzes,
        [quizKey]: fallbackQuiz
      };
      setLessonQuizzes(updatedQuizzes);
      setLessonQuiz(fallbackQuiz);
      
      setQuizLoading(false);
      
      // Immediately start the fallback quiz
      setQuizStarted(true);
      setCurrentQuestionIndex(0);
      setQuestionStartTime(Date.now());
      setUserAnswers({});
      setHintShown({});
      setShowHint(false);
      setQuizCompleted(false);
      setQuizResults(null);
      setQuizFeedback(null);
      setTimeSpentOnQuestion(0);
    }
    }, 0); // Close setTimeout
  };

  // FIXED: Stable selectAnswer function to prevent quiz restart
  const selectAnswer = React.useCallback((optionIndex) => {
    // Guard against invalid states that could cause quiz restart
    if (quizCompleted || !quizStarted || !lessonQuiz) return;
    
    setUserAnswers(prev => ({
      ...prev,
      [currentQuestionIndex]: optionIndex
    }));
  }, [currentQuestionIndex, quizCompleted, quizStarted, lessonQuiz]);

  // FIXED: Stable nextQuestion function with guards - removed circular dependency
  const nextQuestion = React.useCallback(() => {
    if (quizCompleted || !quizStarted || !lessonQuiz) return; // Guard against invalid states
    
    if (currentQuestionIndex < lessonQuiz.questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setQuestionStartTime(Date.now());
      setTimeSpentOnQuestion(0);
      setShowHint(false);
    } else {
      // Call completeQuiz directly without dependency to avoid circular reference
      setQuizCompleted(true);
      
      // Calculate results
      let correctCount = 0;
      lessonQuiz.questions.forEach((question, index) => {
        if (userAnswers[index] === question.correct) {
          correctCount++;
        }
      });
      
      const score = Math.round((correctCount / lessonQuiz.questions.length) * 100);
      const results = {
        score,
        correctCount,
        totalQuestions: lessonQuiz.questions.length,
        passed: score >= 80 // Changed to 80% for auto-progression
      };
      setQuizResults(results);

      // Generate AI feedback and award points
      const generateFeedback = async () => {
        try {
          const messages = [
            {
              role: 'system',
              content: 'Provide encouraging, personalized feedback for this quiz performance. Focus on learning and improvement.'
            },
            {
              role: 'user',
              content: `Quiz Results: Score ${score}% (${correctCount}/${lessonQuiz.questions.length} correct). Lesson: Day ${lesson.day} - ${lesson.title}. Provide encouraging feedback.`
            }
          ];

          const feedback = await makeOpenAIRequest(messages, 500);
          setQuizFeedback(feedback);
        } catch (error) {
          setQuizFeedback(`Great job completing the quiz for Day ${lesson.day}! Review the explanations below to reinforce your learning.`);
        }

        // Award points based on score using new points system
        if (currentUser && currentUser.id && currentRoadmap) {
          const earnedPoints = pointsSystem.calculateQuizPoints(score);

          // Award points
          await pointsSystem.awardPoints(
            currentUser.id,
            earnedPoints,
            'quiz_completion',
            {
              score: score,
              goalId: currentRoadmap.id,
              dayNumber: lesson.day,
              lessonTitle: lesson.title
            }
          );

          // Record lesson progress (auto-completes if 80%+)
          await pointsSystem.recordLessonProgress(
            currentUser.id,
            currentRoadmap.id,
            lesson.day,
            score,
            earnedPoints
          );

          // Check for new achievements
          const achievements = await pointsSystem.checkAchievements(currentUser.id);
          if (achievements && achievements.length > 0) {
            setNewAchievements(achievements);
            setShowAchievementModal(true);
          }

          // Reload user points
          await loadUserPoints();

          // Auto-complete and progress to next lesson if 80%+
          if (results.passed) {
            completeLesson(currentLesson);
            setTimeout(() => {
              // Auto-advance to next lesson
              if (currentLesson < currentRoadmap.lessons.length - 1) {
                setCurrentLesson(currentLesson + 1);
                setQuizCompleted(false);
                setQuizStarted(false);
                setLessonQuiz(null);
                setUserAnswers({});
                setCurrentQuestionIndex(0);
                setQuizResults(null);
                setQuizFeedback(null);
              }
            }, 4000); // Show results for 4 seconds before advancing
          }
        }
      };

      generateFeedback();
    }
  }, [currentQuestionIndex, lessonQuiz, quizCompleted, quizStarted, userAnswers, lesson, awardPoints, completeLesson, currentLesson]);

  // FIXED: Stable useHint function
  const useHint = React.useCallback(() => {
    if (quizCompleted || !quizStarted) return; // Guard against invalid states
    
    setHintShown(prev => ({
      ...prev,
      [currentQuestionIndex]: true
    }));
    setShowHint(false);
  }, [currentQuestionIndex, quizCompleted, quizStarted]);

  const formatQuizTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const retakeQuiz = () => {
    setQuizStarted(true); // Keep it started, just reset
    setQuizCompleted(false);
    setCurrentQuestionIndex(0);
    setQuestionStartTime(Date.now());
    setUserAnswers({});
    setHintShown({});
    setShowHint(false);
    setQuizResults(null);
    setQuizFeedback(null);
    setTimeSpentOnQuestion(0);
  };

// Replace your existing LessonPage component with this fixed version
const LessonPage = () => {

  const generateLessonQuiz = async () => {
    if (!lesson || !currentRoadmap || quizLoading) return;
    
    // Create a unique key for this specific lesson
    const quizKey = `${currentRoadmap.id}_lesson_${currentLesson}`;
    
    // Check if quiz already exists - if so, just set it and start immediately
    if (lessonQuizzes[quizKey]) {
      setLessonQuiz(lessonQuizzes[quizKey]);
      setQuizStarted(true);
      setCurrentQuestionIndex(0);
      setQuestionStartTime(Date.now());
      setUserAnswers({});
      setHintShown({});
      setShowHint(false);
      setQuizCompleted(false);
      setQuizResults(null);
      setQuizFeedback(null);
      setTimeSpentOnQuestion(0);
      return;
    }

    // FIXED: Set loading state immediately for better UX
    setQuizLoading(true);
    
    // FIXED: Use setTimeout to ensure UI updates before heavy computation
    setTimeout(async () => {

    try {
      const messages = [
        {
          role: 'system',
          content: `Create a comprehensive quiz specifically for this lesson content. Return JSON with this structure:
          {
            "estimatedMinutes": 8,
            "questions": [
              {
                "question": "Question text directly related to the lesson content",
                "options": ["Option A", "Option B", "Option C", "Option D"],
                "correct": 0,
                "difficulty": "easy|medium|hard",
                "estimatedSeconds": 90,
                "hint": "Helpful hint when user is stuck",
                "explanation": {
                  "correct_reason": "Why the correct answer is right",
                  "incorrect_reasons": {
                    "0": "Why option A is wrong (if not correct)",
                    "1": "Why option B is wrong (if not correct)", 
                    "2": "Why option C is wrong (if not correct)",
                    "3": "Why option D is wrong (if not correct)"
                  },
                  "concept": "Key concept being tested",
                  "step_by_step": [
                    "Step 1: Identify the key information from the lesson",
                    "Step 2: Apply the concept learned", 
                    "Step 3: Eliminate incorrect options",
                    "Step 4: Select the best answer"
                  ]
                }
              }
            ]
          }
          
          Make each question UNIQUE and test understanding of the specific lesson content. Include realistic time estimates and helpful hints.`
        },
        {
          role: 'user',
          content: `Create 6-7 multiple choice questions specifically for this lesson:

          Lesson Title: "${lesson.title}"
          Day: ${lesson.day}
          Lesson Content: "${lesson.content}"
          Lesson Tasks: "${lesson.tasks?.join(', ') || ''}"

          Requirements:
          1. Questions must test understanding of THIS specific lesson on Day ${lesson.day}
          2. Include realistic time estimates for each question
          3. Provide helpful hints for when students get stuck
          4. Include detailed explanations for correct and incorrect answers
          5. Mix of difficulty levels (easy, medium, hard)
          6. Cover different aspects of the lesson content
          7. Make questions unique to Day ${lesson.day} content`
        }
      ];

      const response = await makeOpenAIRequest(messages, 2500);
      let quizData;
      
      try {
        quizData = JSON.parse(response);
      } catch (parseError) {
        console.error('Error parsing quiz JSON:', parseError);
        throw new Error('Invalid quiz format received');
      }
      
      // Validate quiz structure
      if (!quizData.questions || !Array.isArray(quizData.questions) || quizData.questions.length === 0) {
        throw new Error('Invalid quiz structure');
      }
      
      // Store the quiz with unique key
      const updatedQuizzes = {
        ...lessonQuizzes,
        [quizKey]: quizData
      };
      setLessonQuizzes(updatedQuizzes);
      setLessonQuiz(quizData);
      
      // Save immediately to prevent loss
      if (currentUser) {
        saveCurrentSession();
      }
      
      setQuizLoading(false);
      
      // Immediately start the quiz after generation
      setQuizStarted(true);
      setCurrentQuestionIndex(0);
      setQuestionStartTime(Date.now());
      setUserAnswers({});
      setHintShown({});
      setShowHint(false);
      setQuizCompleted(false);
      setQuizResults(null);
      setQuizFeedback(null);
      setTimeSpentOnQuestion(0);
      
    } catch (error) {
      console.error('Error generating lesson quiz:', error);
      
      // Enhanced fallback quiz specific to the lesson
      const fallbackQuiz = {
        estimatedMinutes: 6,
        questions: [
          {
            question: `Based on Day ${lesson.day}: "${lesson.title}", what is the main learning objective?`,
            options: ["Basic understanding", "Advanced implementation", "Practical application", "Conceptual overview"],
            correct: 2,
            difficulty: "medium",
            estimatedSeconds: 90,
            hint: "Think about what hands-on skills this lesson is trying to teach you.",
            explanation: {
              correct_reason: "The lesson focuses on practical application of the concepts taught",
              incorrect_reasons: {
                "0": "This goes beyond basic understanding to practical skills",
                "1": "This is introductory level, not advanced implementation",
                "3": "While conceptual, the emphasis is on practical application"
              },
              concept: `Core learning objective of ${lesson.title}`,
              step_by_step: [
                "Step 1: Review the lesson's main focus and goals",
                "Step 2: Identify what type of learning outcome is emphasized", 
                "Step 3: Consider the lesson's practical components",
                "Step 4: Select the option that best matches the hands-on approach"
              ]
            }
          },
          {
            question: `Which approach would best help you master the concepts in Day ${lesson.day}?`,
            options: ["Theoretical study only", "Hands-on practice with guidance", "Memorization of facts", "Passive observation"],
            correct: 1,
            difficulty: "easy",
            estimatedSeconds: 60,
            hint: "Consider which method would give you the most practical experience.",
            explanation: {
              correct_reason: "Hands-on practice with guidance reinforces learning through active engagement",
              incorrect_reasons: {
                "0": "Theory alone doesn't develop practical skills",
                "2": "Memorization doesn't build true understanding",
                "3": "Passive observation lacks the active learning component"
              },
              concept: `Effective learning methodology for ${lesson.title}`,
              step_by_step: [
                "Step 1: Consider what the lesson is trying to teach",
                "Step 2: Think about effective learning strategies",
                "Step 3: Match the approach to the lesson's practical nature",
                "Step 4: Choose the most active learning method"
              ]
            }
          },
          {
            question: `What should you be able to do after completing Day ${lesson.day}: "${lesson.title}"?`,
            options: ["Recite definitions", "Apply the concepts practically", "Discuss theory only", "Identify terminology"],
            correct: 1,
            difficulty: "medium",
            estimatedSeconds: 75,
            hint: "Focus on the practical outcomes this lesson aims to achieve.",
            explanation: {
              correct_reason: "The lesson is designed to help you apply concepts in real situations",
              incorrect_reasons: {
                "0": "Reciting definitions is just the first step, not the end goal",
                "2": "Theory discussion alone doesn't demonstrate mastery",
                "3": "Identifying terms is basic knowledge, not practical competency"
              },
              concept: `Expected outcomes from ${lesson.title}`,
              step_by_step: [
                "Step 1: Think about the lesson's ultimate goal",
                "Step 2: Consider what demonstrates true understanding",
                "Step 3: Focus on practical application vs. memorization",
                "Step 4: Select the outcome that shows real competency"
              ]
            }
          },
          {
            question: `Based on the tasks in Day ${lesson.day}, what is the most critical skill to develop?`,
            options: ["Speed of completion", "Attention to detail", "Problem-solving approach", "Memory retention"],
            correct: 2,
            difficulty: "hard",
            estimatedSeconds: 120,
            hint: "Think about what skill will help you in real-world applications of this knowledge.",
            explanation: {
              correct_reason: "Problem-solving approach is fundamental to applying what you learn in various contexts",
              incorrect_reasons: {
                "0": "Speed comes with practice, but understanding the approach is more important",
                "1": "While important, attention to detail is secondary to understanding the process",
                "3": "Memory is helpful but problem-solving skills are more valuable long-term"
              },
              concept: `Critical thinking skills for ${lesson.title}`,
              step_by_step: [
                "Step 1: Analyze what makes someone successful in this area",
                "Step 2: Consider which skill transfers to new situations",
                "Step 3: Think about long-term learning goals",
                "Step 4: Choose the skill that enables continued growth"
              ]
            }
          }
        ]
      };
      
      // Store the fallback quiz
      const updatedQuizzes = {
        ...lessonQuizzes,
        [quizKey]: fallbackQuiz
      };
      setLessonQuizzes(updatedQuizzes);
      setLessonQuiz(fallbackQuiz);
      
      setQuizLoading(false);
      
      // Immediately start the fallback quiz
      setQuizStarted(true);
      setCurrentQuestionIndex(0);
      setQuestionStartTime(Date.now());
      setUserAnswers({});
      setHintShown({});
      setShowHint(false);
      setQuizCompleted(false);
      setQuizResults(null);
      setQuizFeedback(null);
      setTimeSpentOnQuestion(0);
    }
    }, 0); // Close setTimeout
  };

  // FIXED: Stable selectAnswer function to prevent quiz restart
  const selectAnswer = React.useCallback((optionIndex) => {
    // Guard against invalid states that could cause quiz restart
    if (quizCompleted || !quizStarted || !lessonQuiz) return;
    
    setUserAnswers(prev => ({
      ...prev,
      [currentQuestionIndex]: optionIndex
    }));
  }, [currentQuestionIndex, quizCompleted, quizStarted, lessonQuiz]);

  // FIXED: Stable nextQuestion function with guards - removed circular dependency
  const nextQuestion = React.useCallback(() => {
    if (quizCompleted || !quizStarted || !lessonQuiz) return; // Guard against invalid states
    
    if (currentQuestionIndex < lessonQuiz.questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setQuestionStartTime(Date.now());
      setTimeSpentOnQuestion(0);
      setShowHint(false);
    } else {
      // Call completeQuiz directly without dependency to avoid circular reference
      setQuizCompleted(true);
      
      // Calculate results
      let correctCount = 0;
      lessonQuiz.questions.forEach((question, index) => {
        if (userAnswers[index] === question.correct) {
          correctCount++;
        }
      });
      
      const score = Math.round((correctCount / lessonQuiz.questions.length) * 100);
      const results = {
        score,
        correctCount,
        totalQuestions: lessonQuiz.questions.length,
        passed: score >= 80 // Changed to 80% for auto-progression
      };
      setQuizResults(results);

      // Generate AI feedback and award points
      const generateFeedback = async () => {
        try {
          const messages = [
            {
              role: 'system',
              content: 'Provide encouraging, personalized feedback for this quiz performance. Focus on learning and improvement.'
            },
            {
              role: 'user',
              content: `Quiz Results: Score ${score}% (${correctCount}/${lessonQuiz.questions.length} correct). Lesson: Day ${lesson.day} - ${lesson.title}. Provide encouraging feedback.`
            }
          ];

          const feedback = await makeOpenAIRequest(messages, 500);
          setQuizFeedback(feedback);
        } catch (error) {
          setQuizFeedback(`Great job completing the quiz for Day ${lesson.day}! Review the explanations below to reinforce your learning.`);
        }

        // Award points based on score using new points system
        if (currentUser && currentUser.id && currentRoadmap) {
          const earnedPoints = pointsSystem.calculateQuizPoints(score);

          // Award points
          await pointsSystem.awardPoints(
            currentUser.id,
            earnedPoints,
            'quiz_completion',
            {
              score: score,
              goalId: currentRoadmap.id,
              dayNumber: lesson.day,
              lessonTitle: lesson.title
            }
          );

          // Record lesson progress (auto-completes if 80%+)
          await pointsSystem.recordLessonProgress(
            currentUser.id,
            currentRoadmap.id,
            lesson.day,
            score,
            earnedPoints
          );

          // Check for new achievements
          const achievements = await pointsSystem.checkAchievements(currentUser.id);
          if (achievements && achievements.length > 0) {
            setNewAchievements(achievements);
            setShowAchievementModal(true);
          }

          // Reload user points
          await loadUserPoints();

          // Auto-complete and progress to next lesson if 80%+
          if (results.passed) {
            completeLesson(currentLesson);
            setTimeout(() => {
              // Auto-advance to next lesson
              if (currentLesson < currentRoadmap.lessons.length - 1) {
                setCurrentLesson(currentLesson + 1);
                setQuizCompleted(false);
                setQuizStarted(false);
                setLessonQuiz(null);
                setUserAnswers({});
                setCurrentQuestionIndex(0);
                setQuizResults(null);
                setQuizFeedback(null);
              }
            }, 4000); // Show results for 4 seconds before advancing
          }
        }
      };

      generateFeedback();
    }
  }, [currentQuestionIndex, lessonQuiz, quizCompleted, quizStarted, userAnswers, lesson, awardPoints, completeLesson, currentLesson]);

  // FIXED: Stable useHint function
  const useHint = React.useCallback(() => {
    if (quizCompleted || !quizStarted) return; // Guard against invalid states
    
    setHintShown(prev => ({
      ...prev,
      [currentQuestionIndex]: true
    }));
    setShowHint(false);
  }, [currentQuestionIndex, quizCompleted, quizStarted]);


  const formatQuizTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const retakeQuiz = () => {
    setQuizStarted(true); // Keep it started, just reset
    setQuizCompleted(false);
    setCurrentQuestionIndex(0);
    setQuestionStartTime(Date.now());
    setUserAnswers({});
    setHintShown({});
    setShowHint(false);
    setQuizResults(null);
    setQuizFeedback(null);
    setTimeSpentOnQuestion(0);
  };

  if (!lesson) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900' : 'bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50'}`}>
        <div className={`text-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>
          <h2 className="text-2xl font-bold mb-4">Lesson not found</h2>
          <button
            onClick={() => setCurrentView('roadmap')}
            className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-xl font-semibold hover:scale-105 transition-all duration-300"
          >
            Back to Roadmap
          </button>
        </div>
      </div>
    );
  }

  // Additional Resources Component
  const AdditionalResources = () => {
    if (!lesson.resources) return null;

    return (
      <div className={`p-6 rounded-3xl backdrop-blur-sm ${darkMode ? 'bg-white/10 border border-white/20' : 'bg-white/70 border border-white/50'} shadow-xl mb-6`}>
        <div className="flex items-center space-x-2 mb-6">
          <Globe className={`w-6 h-6 ${darkMode ? 'text-blue-400' : 'text-blue-500'}`} />
          <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>Additional Resources</h3>
          <span className={`px-2 py-1 text-xs rounded-full ${darkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>
            AI Curated
          </span>
        </div>

        <div className="space-y-6">
          {/* Video Resources */}
          {lesson.resources.videos && lesson.resources.videos.length > 0 && (
            <div>
              <h4 className={`text-lg font-semibold mb-3 flex items-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                <div className="w-2 h-2 bg-red-500 rounded-full mr-2"></div>
                📹 Video Tutorials
              </h4>
              <div className="space-y-3">
                {lesson.resources.videos.map((video, index) => (
                  <a
                    key={index}
                    href={video.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`block p-4 rounded-xl transition-all duration-300 hover:scale-105 ${darkMode ? 'bg-gray-800 hover:bg-gray-700 border border-gray-700' : 'bg-gray-50 hover:bg-gray-100 border border-gray-200'}`}
                  >
                    <div className="flex items-start space-x-3">
                      <div className={`p-2 rounded-lg ${darkMode ? 'bg-red-900/50' : 'bg-red-100'}`}>
                        <div className={`w-4 h-4 ${darkMode ? 'text-red-400' : 'text-red-600'}`}>▶</div>
                      </div>
                      <div className="flex-1">
                        <h5 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                          {video.title}
                        </h5>
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {video.channel} • {video.duration}
                        </p>
                      </div>
                      <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        🔗
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Book Resources */}
          {lesson.resources.books && lesson.resources.books.length > 0 && (
            <div>
              <h4 className={`text-lg font-semibold mb-3 flex items-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                📚 Recommended Reading
              </h4>
              <div className="space-y-3">
                {lesson.resources.books.map((book, index) => (
                  <a
                    key={index}
                    href={book.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`block p-4 rounded-xl transition-all duration-300 hover:scale-105 ${darkMode ? 'bg-gray-800 hover:bg-gray-700 border border-gray-700' : 'bg-gray-50 hover:bg-gray-100 border border-gray-200'}`}
                  >
                    <div className="flex items-start space-x-3">
                      <div className={`p-2 rounded-lg ${darkMode ? 'bg-green-900/50' : 'bg-green-100'}`}>
                        <BookOpen className={`w-4 h-4 ${darkMode ? 'text-green-400' : 'text-green-600'}`} />
                      </div>
                      <div className="flex-1">
                        <h5 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                          {book.title}
                        </h5>
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          by {book.author} {book.pages && `• Pages ${book.pages}`}
                        </p>
                      </div>
                      <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        🔗
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Article Resources */}
          {lesson.resources.articles && lesson.resources.articles.length > 0 && (
            <div>
              <h4 className={`text-lg font-semibold mb-3 flex items-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                📄 Articles & Guides
              </h4>
              <div className="space-y-3">
                {lesson.resources.articles.map((article, index) => (
                  <a
                    key={index}
                    href={article.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`block p-4 rounded-xl transition-all duration-300 hover:scale-105 ${darkMode ? 'bg-gray-800 hover:bg-gray-700 border border-gray-700' : 'bg-gray-50 hover:bg-gray-100 border border-gray-200'}`}
                  >
                    <div className="flex items-start space-x-3">
                      <div className={`p-2 rounded-lg ${darkMode ? 'bg-blue-900/50' : 'bg-blue-100'}`}>
                        <FileText className={`w-4 h-4 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                      </div>
                      <div className="flex-1">
                        <h5 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                          {article.title}
                        </h5>
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {article.source}
                        </p>
                      </div>
                      <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        🔗
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Practice Resources */}
          {lesson.resources.practice && lesson.resources.practice.length > 0 && (
            <div>
              <h4 className={`text-lg font-semibold mb-3 flex items-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                <div className="w-2 h-2 bg-purple-500 rounded-full mr-2"></div>
                💪 Practice & Exercises
              </h4>
              <div className="space-y-3">
                {lesson.resources.practice.map((practice, index) => (
                  <a
                    key={index}
                    href={practice.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`block p-4 rounded-xl transition-all duration-300 hover:scale-105 ${darkMode ? 'bg-gray-800 hover:bg-gray-700 border border-gray-700' : 'bg-gray-50 hover:bg-gray-100 border border-gray-200'}`}
                  >
                    <div className="flex items-start space-x-3">
                      <div className={`p-2 rounded-lg ${darkMode ? 'bg-purple-900/50' : 'bg-purple-100'}`}>
                        <Target className={`w-4 h-4 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                      </div>
                      <div className="flex-1">
                        <h5 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                          {practice.title}
                        </h5>
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {practice.source}
                        </p>
                        {practice.problems && practice.problems.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {practice.problems.slice(0, 3).map((problem, pIndex) => (
                              <span
                                key={pIndex}
                                className={`text-xs px-2 py-1 rounded ${darkMode ? 'bg-purple-900/30 text-purple-300' : 'bg-purple-100 text-purple-700'}`}
                              >
                                {problem}
                              </span>
                            ))}
                            {practice.problems.length > 3 && (
                              <span className={`text-xs px-2 py-1 rounded ${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-600'}`}>
                                +{practice.problems.length - 3} more
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        🔗
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const currentQuestion = lessonQuiz?.questions[currentQuestionIndex];

  return (
    <div className={`min-h-screen transition-all duration-500 ${darkMode ? 'bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900' : 'bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50'}`}>
      <div className="container mx-auto px-4 py-8">
        <button
          onClick={() => {
            endSession();
            setCurrentView('roadmap');
          }}
          className={`mb-8 p-2 rounded-xl transition-all duration-300 ${darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-800'}`}
        >
          <ChevronLeft className="w-6 h-6" />
        </button>

        <div className="max-w-4xl mx-auto">
          <h1 className={`text-4xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
            Day {lesson.day}: {lesson.title}
          </h1>
          <p className={`text-lg mb-8 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {lesson.description}
          </p>

          <div className="grid lg:grid-cols-2 gap-8">
            <div>
              <div className={`p-6 rounded-3xl backdrop-blur-sm ${darkMode ? 'bg-white/10 border border-white/20' : 'bg-white/70 border border-white/50'} shadow-xl mb-6`}>
                <h3 className={`text-xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Lesson Content</h3>
                <div className={`prose ${darkMode ? 'prose-invert' : ''} max-w-none`}>
                  <p className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{lesson.content}</p>
                </div>
              </div>

              {/* Interactive Simulations - Auto-detected based on lesson content */}
              {(() => {
                try {
                  const lessonText = (lesson.title + ' ' + lesson.content).toLowerCase();
                  const simulations = [];

                  // Simplified onInteraction - no callbacks needed for now to prevent infinite loops
                  // Points will be awarded through other mechanisms

                  // Physics simulations
                  if (lessonText.includes('incline') || lessonText.includes('friction') || lessonText.includes('normal force')) {
                    simulations.push(<PhysicsSimulation key="incline" type="incline" darkMode={darkMode} />);
                  }
                  if (lessonText.includes('pendulum') || lessonText.includes('oscillation') || lessonText.includes('energy conservation')) {
                    simulations.push(<PhysicsSimulation key="pendulum" type="pendulum" darkMode={darkMode} />);
                  }
                  if (lessonText.includes('projectile') || lessonText.includes('trajectory') || lessonText.includes('parabolic')) {
                    simulations.push(<PhysicsSimulation key="projectile" type="projectile" darkMode={darkMode} />);
                  }
                  if (lessonText.includes('collision') || lessonText.includes('momentum')) {
                    simulations.push(<PhysicsSimulation key="collision" type="collision" darkMode={darkMode} />);
                  }

                  // Chemistry simulations
                  // More specific pH detection - exclude physics-related terms
                  if ((lessonText.includes(' ph ') || lessonText.includes('ph scale') || lessonText.includes('ph level') ||
                       lessonText.includes('acidity') || lessonText.includes('acidic') || lessonText.includes('acid') ||
                       lessonText.includes('base') || lessonText.includes('alkaline')) &&
                      !lessonText.includes('physics') && !lessonText.includes('phase')) {
                    simulations.push(<ChemistrySimulation key="ph" type="ph_scale" darkMode={darkMode} />);
                  }
                  if (lessonText.includes('temperature') && (lessonText.includes('kinetic') || lessonText.includes('reaction rate'))) {
                    simulations.push(<ChemistrySimulation key="temp" type="temperature" darkMode={darkMode} />);
                  }
                  if (lessonText.includes('molarity') || lessonText.includes('concentration') || lessonText.includes('solution')) {
                    simulations.push(<ChemistrySimulation key="molarity" type="molarity" darkMode={darkMode} />);
                  }
                  if (lessonText.includes('stoichiometry') || lessonText.includes('chemical reaction') || lessonText.includes('balanced equation')) {
                    simulations.push(<ChemistrySimulation key="reaction" type="reaction" darkMode={darkMode} />);
                  }

                  // Math visualizations
                  if (lessonText.includes('quadratic') || lessonText.includes('parabola') || lessonText.includes('x²')) {
                    simulations.push(<MathVisualization key="quadratic" type="quadratic" darkMode={darkMode} />);
                  }
                  if (lessonText.includes('sine') || lessonText.includes('cosine') || lessonText.includes('trigonometric')) {
                    simulations.push(<MathVisualization key="trig" type="trigonometric" darkMode={darkMode} />);
                  }
                  if (lessonText.includes('linear') || lessonText.includes('slope') || lessonText.includes('y = mx + b')) {
                    simulations.push(<MathVisualization key="linear" type="linear" darkMode={darkMode} />);
                  }

                  return simulations.length > 0 ? (
                    <div className="mb-6 space-y-4">
                      {simulations}
                    </div>
                  ) : null;
                } catch (error) {
                  console.error('Error rendering simulations:', error);
                  return null; // Fail gracefully without crashing
                }
              })()}

              {/* Additional Resources Section */}
              <AdditionalResources />

              {lesson.tasks && (
                <div className={`p-6 rounded-3xl backdrop-blur-sm ${darkMode ? 'bg-white/10 border border-white/20' : 'bg-white/70 border border-white/50'} shadow-xl`}>
                  <h3 className={`text-xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Tasks</h3>
                  <div className="space-y-3">
                    {lesson.tasks.map((task, i) => (
                      <div key={i} className="flex items-center space-x-3">
                        <Circle className={`w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                        <span className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{task}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div>
              {/* AI-Generated Quiz Section */}
              <div className={`p-6 rounded-3xl backdrop-blur-sm ${darkMode ? 'bg-white/10 border border-white/20' : 'bg-white/70 border border-white/50'} shadow-xl`}>
                <div className="flex items-center space-x-2 mb-6">
                  <Target className={`w-6 h-6 ${darkMode ? 'text-purple-400' : 'text-purple-500'}`} />
                  <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>Day {lesson.day} Quiz</h3>
                  <span className={`px-2 py-1 text-xs rounded-full ${darkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>
                    AI Generated
                  </span>
                </div>

                {!lessonQuiz ? (
                  <div className="text-center py-8">
                    <Brain className={`w-16 h-16 mx-auto mb-4 ${darkMode ? 'text-blue-400' : 'text-blue-500'}`} />
                    <h4 className={`text-lg font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                      Ready to test your knowledge?
                    </h4>
                    <p className={`mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Take an AI-generated quiz to reinforce what you've learned in Day {lesson.day}.
                    </p>
                    <button
                      onClick={generateLessonQuiz}
                      disabled={quizLoading}
                      className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-xl font-semibold hover:scale-105 transition-all duration-300 shadow-lg disabled:opacity-50 flex items-center space-x-2 mx-auto"
                    >
                      {quizLoading ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          <span>Generating Quiz...</span>
                        </>
                      ) : (
                        <>
                          <Zap className="w-5 h-5" />
                          <span>Generate Quiz</span>
                        </>
                      )}
                    </button>
                  </div>
                ) : !quizCompleted ? (
                  <div className="space-y-6">
                    {/* Progress and Timer */}
                    <div className="flex justify-between items-center">
                      <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Question {currentQuestionIndex + 1} of {lessonQuiz.questions.length}
                      </div>
                      <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Time: {formatQuizTime(timeSpentOnQuestion)}
                      </div>
                    </div>
                    
                    <div className={`w-full h-2 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-300'}`}>
                      <div 
                        className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all duration-300"
                        style={{ width: `${((currentQuestionIndex + 1) / lessonQuiz.questions.length) * 100}%` }}
                      />
                    </div>

                    {/* Question */}
                    <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                      <div className="flex justify-between items-start mb-4">
                        <h4 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                          {currentQuestion.question}
                        </h4>
                        <span className={`px-2 py-1 text-xs rounded ${
                          currentQuestion.difficulty === 'easy' ? 'bg-green-500/20 text-green-600' :
                          currentQuestion.difficulty === 'medium' ? 'bg-yellow-500/20 text-yellow-600' :
                          'bg-red-500/20 text-red-600'
                        }`}>
                          {currentQuestion.difficulty}
                        </span>
                      </div>
                      
                      <div className="space-y-3">
                        {currentQuestion.options.map((option, index) => (
                          <button
                            key={index}
                            onClick={() => selectAnswer(index)}
                            className={`w-full p-3 rounded-xl text-left transition-all duration-300 ${
                              userAnswers[currentQuestionIndex] === index
                                ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                                : darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                          >
                            <span className="font-medium mr-3">{String.fromCharCode(65 + index)}.</span>
                            {option}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Hint Section */}
                    {showHint && !hintShown[currentQuestionIndex] && (
                      <div className={`p-4 rounded-xl ${darkMode ? 'bg-yellow-900/30 border border-yellow-700' : 'bg-yellow-100 border border-yellow-300'}`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <h5 className={`font-semibold ${darkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>
                              💡 Need a hint?
                            </h5>
                            <p className={`text-sm ${darkMode ? 'text-yellow-200' : 'text-yellow-700'}`}>
                              You've been thinking about this question for a while.
                            </p>
                          </div>
                          <button
                            onClick={useHint}
                            className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-4 py-2 rounded-lg font-semibold hover:scale-105 transition-all duration-300"
                          >
                            Show Hint
                          </button>
                        </div>
                      </div>
                    )}

                    {hintShown[currentQuestionIndex] && (
                      <div className={`p-4 rounded-xl ${darkMode ? 'bg-blue-900/30 border border-blue-700' : 'bg-blue-100 border border-blue-300'}`}>
                        <h5 className={`font-semibold mb-2 ${darkMode ? 'text-blue-300' : 'text-blue-800'}`}>
                          💡 Hint:
                        </h5>
                        <p className={`${darkMode ? 'text-blue-200' : 'text-blue-700'}`}>
                          {currentQuestion.hint}
                        </p>
                      </div>
                    )}

                    {/* Navigation */}
                    <div className="flex justify-between">
                      <button
                        onClick={() => {
                          if (currentQuestionIndex > 0) {
                            setCurrentQuestionIndex(prev => prev - 1);
                            setQuestionStartTime(Date.now());
                            setTimeSpentOnQuestion(0);
                            setShowHint(false);
                          }
                        }}
                        disabled={currentQuestionIndex === 0}
                        className={`px-4 py-2 rounded-xl font-semibold transition-all duration-300 ${
                          currentQuestionIndex === 0 
                            ? 'opacity-50 cursor-not-allowed' 
                            : darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        Previous
                      </button>
                      
                      <button
                        onClick={nextQuestion}
                        disabled={userAnswers[currentQuestionIndex] === undefined}
                        className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-2 rounded-xl font-semibold hover:scale-105 transition-all duration-300 disabled:opacity-50"
                      >
                        {currentQuestionIndex === lessonQuiz.questions.length - 1 ? 'Finish Quiz' : 'Next Question'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Results */}
                    <div className="text-center">
                      <div className={`w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center ${
                        quizResults.passed ? 'bg-gradient-to-r from-green-500 to-blue-500' : 'bg-gradient-to-r from-orange-500 to-red-500'
                      }`}>
                        {quizResults.passed ? (
                          <Trophy className="w-10 h-10 text-white" />
                        ) : (
                          <RotateCcw className="w-10 h-10 text-white" />
                        )}
                      </div>
                      <h4 className={`text-2xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                        {quizResults.passed ? 'Great Job!' : 'Keep Learning!'}
                      </h4>
                      <p className={`text-lg mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        You scored {quizResults.score}% ({quizResults.correctCount}/{quizResults.totalQuestions}) on Day {lesson.day}
                      </p>
                      
                      {quizFeedback && (
                        <div className={`p-4 rounded-xl mb-6 ${darkMode ? 'bg-blue-900/30 border border-blue-700' : 'bg-blue-100 border border-blue-300'}`}>
                          <p className={`${darkMode ? 'text-blue-200' : 'text-blue-700'}`}>{quizFeedback}</p>
                        </div>
                      )}
                    </div>

                    {/* Detailed Results */}
                    <div className="space-y-4">
                      <h5 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                        Question Review:
                      </h5>
                      {lessonQuiz.questions.map((question, index) => {
                        const userAnswer = userAnswers[index];
                        const isCorrect = userAnswer === question.correct;
                        
                        return (
                          <div key={index} className={`p-4 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                            <div className="flex items-start space-x-3 mb-3">
                              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold ${
                                isCorrect ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                              }`}>
                                {isCorrect ? '✓' : '✗'}
                              </div>
                              <div className="flex-1">
                                <p className={`font-medium mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                                  {question.question}
                                </p>
                                <p className={`text-sm mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                  Your answer: {question.options[userAnswer]} {isCorrect ? '(Correct)' : '(Incorrect)'}
                                </p>
                                {!isCorrect && (
                                  <p className={`text-sm mb-2 ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                                    Correct answer: {question.options[question.correct]}
                                  </p>
                                )}
                                <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                                  <p className={`text-sm font-medium mb-1 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                                    Explanation:
                                  </p>
                                  <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    {question.explanation.correct_reason}
                                  </p>
                                  {question.explanation.step_by_step && (
                                    <div className="mt-2">
                                      <p className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                        How to approach this:
                                      </p>
                                      <ol className={`text-xs mt-1 space-y-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                        {question.explanation.step_by_step.map((step, stepIndex) => (
                                          <li key={stepIndex}>{step}</li>
                                        ))}
                                      </ol>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {quizResults.passed ? (
                      <div className={`p-4 rounded-xl text-center ${darkMode ? 'bg-green-900/30 border border-green-700' : 'bg-green-100 border border-green-300'}`}>
                        <p className={`${darkMode ? 'text-green-300' : 'text-green-800'}`}>
                          🎉 Day {lesson.day} completed! The lesson will be marked as complete automatically.
                        </p>
                      </div>
                    ) : (
                      <div className="flex space-x-3">
                        <button
                          onClick={retakeQuiz}
                          className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 text-white py-3 rounded-xl font-semibold hover:scale-105 transition-all duration-300"
                        >
                          Retake Quiz
                        </button>
                        <button
                          onClick={() => completeLesson(currentLesson)}
                          className={`flex-1 py-3 rounded-xl font-semibold transition-all duration-300 ${darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                        >
                          Continue Anyway
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

  const ChatPage = () => (
    <div className={`min-h-screen transition-all duration-500 ${darkMode ? 'bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900' : 'bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50'}`}>
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <button
            onClick={() => setCurrentView(isAuthenticated ? 'dashboard' : 'roadmap')}
            className={`p-2 rounded-xl transition-all duration-300 ${darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-800'}`}
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <div className={`px-4 py-2 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
            <span className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
              {questionsLeft} questions left today
            </span>
          </div>
        </div>

        <div className="max-w-4xl mx-auto">
          <h1 className={`text-4xl font-bold mb-8 text-center ${darkMode ? 'text-white' : 'text-gray-800'}`}>
            AI Tutor Chat
          </h1>

          <div className={`h-96 overflow-y-auto p-6 rounded-3xl backdrop-blur-sm ${darkMode ? 'bg-white/10 border border-white/20' : 'bg-white/70 border border-white/50'} shadow-xl mb-6`}>
            {chatMessages.length === 0 ? (
              <div className="text-center py-16">
                <MessageCircle className={`w-16 h-16 mx-auto mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                <p className={`text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Ask me anything about your learning journey!
                </p>
                {!BACKEND_URL && (
                  <p className={`text-sm mt-2 ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                    Configure the API key in the program for AI responses
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {chatMessages.map((message, index) => (
                  <div key={index} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-xs lg:max-w-md px-4 py-3 rounded-2xl ${
                      message.type === 'user'
                        ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                        : darkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-200 text-gray-800'
                    }`}>
                      {message.content}
                    </div>
                  </div>
                ))}
                <div ref={chatEndRef} />
              </div>
            )}
          </div>

          <div className="flex space-x-4">
            <input
              type="text"
              value={chatInputBuffer}
              onChange={(e) => setChatInputBuffer(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  setChatInput(chatInputBuffer);
                  sendAIMessage();
                }
              }}
              placeholder="Ask your AI tutor anything..."
              disabled={questionsLeft <= 0}
              className={`flex-1 p-4 rounded-2xl border-2 transition-all duration-300 ${
                darkMode 
                  ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-400 focus:border-purple-500' 
                  : 'bg-white border-gray-200 text-gray-800 placeholder-gray-500 focus:border-purple-500'
              } focus:outline-none disabled:opacity-50`}
            />
            <button
              onClick={() => {
                setChatInput(chatInputBuffer);
                sendAIMessage();
              }}
              disabled={!chatInputBuffer.trim() || questionsLeft <= 0}
              className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-4 rounded-2xl font-semibold hover:scale-105 transition-all duration-300 shadow-lg disabled:opacity-50 disabled:hover:scale-100"
            >
              Send
            </button>
          </div>

          {questionsLeft <= 0 && (
            <div className={`mt-4 p-4 rounded-xl ${darkMode ? 'bg-yellow-900/50 border border-yellow-700' : 'bg-yellow-100 border border-yellow-300'} text-center`}>
              <p className={`${darkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>
                You've reached your daily question limit. Upgrade to Nalem Study Circle Pro for unlimited questions!
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Auth Component
  // Replace the entire AuthPage component with this updated version

const AuthPage = ({ showLogin, setShowLogin }) => {
  const [authForm, setAuthForm] = useState({ email: '', password: '', name: '' });
  const [passwordValidation, setPasswordValidation] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });

  // Password validation logic local to AuthPage
  const validatePassword = (password) => {
    const validation = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };
    setPasswordValidation(validation);
    return Object.values(validation).every(Boolean);
  };

  // Reset form when switching between login and signup
  useEffect(() => {
    setAuthForm({ email: '', password: '', name: '' });
    setPasswordValidation({
      length: false,
      uppercase: false,
      lowercase: false,
      number: false,
      special: false
    });
  }, [showLogin]);

  return (
    <div className={`min-h-screen transition-all duration-500 ${darkMode ? 'bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900' : 'bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50'}`}>
      <div className="container mx-auto px-4 py-8 flex items-center justify-center min-h-screen">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Brain className="w-8 h-8 text-white" />
            </div>
            <h1 className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}> 
              {showLogin ? 'Welcome Back' : 'Join Nalem Study Circle'}
            </h1>
            <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'} mt-2`}>
              {showLogin ? 'Sign in to continue your learning journey' : 'Create your account to get started'}
            </p>
          </div>

          <div className={`p-8 rounded-3xl backdrop-blur-sm ${darkMode ? 'bg-white/10 border border-white/20' : 'bg-white/70 border border-white/50'} shadow-xl`}>
            {/* Google Sign In Button */}
            <button
              onClick={handleGoogleAuth}
              disabled={authLoading}
              className={`w-full mb-6 p-3 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center space-x-3 ${
                darkMode 
                  ? 'bg-white text-gray-800 hover:bg-gray-100' 
                  : 'bg-white text-gray-800 hover:bg-gray-50 border border-gray-300'
              } shadow-lg hover:scale-105 disabled:opacity-50 disabled:hover:scale-100`}
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span>{authLoading ? 'Signing in...' : `Continue with Google`}</span>
            </button>

            <div className="relative mb-6">
              <div className={`absolute inset-0 flex items-center`}>
                <div className={`w-full border-t ${darkMode ? 'border-gray-600' : 'border-gray-300'}`} />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className={`px-2 ${darkMode ? 'bg-gray-900 text-gray-400' : 'bg-white text-gray-500'}`}>
                  or continue with email
                </span>
              </div>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); handleAuth(showLogin, authForm); }}>
              {!showLogin && (
                <div className="mb-4">
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={authForm.name}
                    onChange={(e) => setAuthForm(prev => ({...prev, name: e.target.value}))}
                    className={`w-full p-3 rounded-xl border transition-all duration-300 ${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-800'} focus:outline-none focus:border-purple-500`}
                    required={!showLogin}
                  />
                </div>
              )}
              
              <div className="mb-4">
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Email Address
                </label>
                <div className="relative">
                  <Mail className={`absolute left-3 top-3 w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                  <input
                    type="email"
                    value={authForm.email}
                    onChange={(e) => setAuthForm(prev => ({...prev, email: e.target.value}))}
                    className={`w-full pl-10 pr-4 py-3 rounded-xl border transition-all duration-300 ${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-800'} focus:outline-none focus:border-purple-500`}
                    required
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Password
                </label>
                <div className="relative">
                  <Lock className={`absolute left-3 top-3 w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={authForm.password}
                    onChange={(e) => {
                      setAuthForm(prev => ({ ...prev, password: e.target.value }));
                      if (!showLogin) {
                        validatePassword(e.target.value);
                      }
                    }}
                    className={`w-full pl-10 pr-12 py-3 rounded-xl border transition-all duration-300 ${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-800'} focus:outline-none focus:border-purple-500`}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={`absolute right-3 top-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {!showLogin && authForm.password && (
                <div className={`mb-4 p-3 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                  <p className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Password Requirements:
                </p>
                <div className="space-y-1 text-xs">
                  {[
                    { key: 'length', text: 'At least 8 characters' },
                    { key: 'uppercase', text: 'One uppercase letter' },
                    { key: 'lowercase', text: 'One lowercase letter' },
                    { key: 'number', text: 'One number' },
                    { key: 'special', text: 'One special character' }
                  ].map(requirement => (
                    <div key={requirement.key} className="flex items-center space-x-2">
                      <div className={`w-3 h-3 rounded-full ${
                        passwordValidation[requirement.key] ? 'bg-green-500' : 'bg-gray-400'
                      }`} />
                      <span className={`${
                        passwordValidation[requirement.key] 
                          ? 'text-green-600' 
                          : darkMode ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        {requirement.text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={authLoading}
              className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 rounded-xl font-semibold hover:scale-105 transition-all duration-300 shadow-lg disabled:opacity-50"
            >
              {authLoading ? 'Processing...' : (showLogin ? 'Sign In' : 'Create Account')}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => setShowLogin(!showLogin)}
              className={`text-sm ${darkMode ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-500'} transition-colors duration-300`}
            >
              {showLogin ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
            </button>
          </div>
          
          {/* Demo Login Option 
          <div className="mt-6 pt-6 border-t border-gray-300/20">
            <button
              onClick={loginAsDemo}
              disabled={authLoading}
              className={`w-full py-3 rounded-xl font-semibold transition-all duration-300 hover:scale-105 ${
                darkMode 
                  ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              } disabled:opacity-50`}
            >
              Try Demo Version
            </button>
          </div>
          */}
        </div>
      </div>
    </div>
  </div>
);
};

  // Settings Component
  const SettingsModal = () => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`max-w-md w-full p-6 rounded-3xl ${darkMode ? 'bg-gray-900 border border-gray-700' : 'bg-white border border-gray-200'} shadow-2xl`}>
        <div className="flex justify-between items-center mb-6">
          <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>Settings</h3>
          <button
            onClick={() => setShowSettings(false)}
            className={`p-2 rounded-xl ${darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'} transition-colors duration-300`}
          >
            <X className={`w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
          </button>
        </div>

        <div className="space-y-4">
          <div className={`p-4 rounded-xl ${darkMode ? 'bg-green-900/30 border border-green-700' : 'bg-green-100 border border-green-300'}`}>
            <p className={`text-sm ${darkMode ? 'text-green-300' : 'text-green-800'}`}>
              ✅ API Key configured in the program
            </p>
          </div>

          <div className="flex items-center justify-between">
            <span className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Dark Mode</span>
            <button
              onClick={() => setDarkMode(!darkMode)}
              className={`relative w-12 h-6 rounded-full transition-colors duration-300 ${darkMode ? 'bg-purple-500' : 'bg-gray-300'}`}
            >
              <div className={`absolute w-5 h-5 bg-white rounded-full top-0.5 transition-transform duration-300 ${darkMode ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </button>
          </div>

          <LanguageSetting 
            currentLanguage={currentLanguage}
            setShowLanguageModal={setShowLanguageModal}
            darkMode={darkMode}
          />

          <div className={`p-4 rounded-xl ${darkMode ? 'bg-blue-900/30 border border-blue-700' : 'bg-blue-100 border border-blue-300'}`}>
            <h4 className={`font-semibold mb-2 ${darkMode ? 'text-blue-300' : 'text-blue-800'}`}>Account Info</h4>
            <p className={`text-sm ${darkMode ? 'text-blue-300' : 'text-blue-800'}`}>
              {currentUser?.name} ({currentUser?.email})
            </p>
            <p className={`text-xs ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
              Member since: {currentUser?.profile.joinDate}
            </p>
          </div>

          <div className={`p-4 rounded-xl ${darkMode ? 'bg-purple-900/30 border border-purple-700' : 'bg-purple-100 border border-purple-300'}`}>
            <h4 className={`font-semibold mb-2 ${darkMode ? 'text-purple-300' : 'text-purple-800'}`}>Learning Stats</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className={`${darkMode ? 'text-purple-200' : 'text-purple-700'}`}>Points: {userPoints.toLocaleString()}</p>
                <p className={`${darkMode ? 'text-purple-200' : 'text-purple-700'}`}>Badges: {userBadges.length}</p>
              </div>
              <div>
                <p className={`${darkMode ? 'text-purple-200' : 'text-purple-700'}`}>Courses: {userGoals.length}</p>
                <p className={`${darkMode ? 'text-purple-200' : 'text-purple-700'}`}>Vocab Lists: {vocabularyLists.length}</p>
              </div>
            </div>
          </div>

          <button
            onClick={() => {
              localStorage.removeItem('flowtutor_tutorial_completed');
              setShowSettings(false);
              // Wait for modal to close, then start tutorial
              setTimeout(() => {
                const { startTutorial } = require('./Tutorial');
                startTutorial(darkMode);
              }, 500);
            }}
            className={`w-full p-4 rounded-xl ${darkMode ? 'bg-yellow-900/30 border border-yellow-700 hover:bg-yellow-900/40' : 'bg-yellow-100 border border-yellow-300 hover:bg-yellow-200'} transition-all duration-300 font-medium`}
          >
            <span className={`${darkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>🎓 Restart Tutorial</span>
          </button>
        </div>

        <button
          onClick={() => setShowSettings(false)}
          className="w-full mt-6 bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 rounded-xl font-semibold hover:scale-105 transition-all duration-300"
        >
          Close
        </button>
      </div>
    </div>
  );

  // Dashboard Component
  const Dashboard = () => (
    <div className={`min-h-screen transition-all duration-500 ${darkMode ? 'bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900' : 'bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50'}`}>
      <div className="container mx-auto px-4 py-8">
        <nav className="flex justify-between items-center mb-8">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                Welcome back, {currentUser?.name}!
                {currentUser?.id === 'demo-user-123' && (
                  <span className={`ml-2 px-2 py-1 text-xs rounded-full ${darkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-800'}`}>
                    Demo Mode
                  </span>
                )}
              </h1>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {currentUser?.id === 'demo-user-123' 
                  ? 'Exploring Nalem Study Circle with sample data'
                  : 'Ready to continue learning?'
                }
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            {currentUser && (
              <PointsDisplay
                userId={currentUser.id}
                darkMode={darkMode}
                compact={true}
              />
            )}
            <LanguageButton
              currentLanguage={currentLanguage}
              setShowLanguageModal={setShowLanguageModal}
              darkMode={darkMode}
            />
            {currentUser?.email === 'pranayrishi.nalem@gmail.com' && (
              <button
                onClick={() => setShowAnalyticsDashboard(true)}
                className={`p-2 rounded-xl transition-all duration-300 ${darkMode ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white' : 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600'} shadow-lg`}
                title="Analytics Dashboard"
              >
                <BarChart3 className="w-5 h-5" />
              </button>
            )}
            <button
              onClick={() => setShowSettings(true)}
              className={`p-2 rounded-xl transition-all duration-300 ${darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-600 hover:bg-gray-50'} shadow-lg`}
              data-tutorial="settings"
            >
              <Settings className="w-5 h-5" />
            </button>
            <button
              onClick={logout}
              className={`p-2 rounded-xl transition-all duration-300 ${darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-600 hover:bg-gray-50'} shadow-lg`}
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </nav>

        <div className="grid lg:grid-cols-3 gap-8 mb-8">
          <div className={`p-6 rounded-3xl backdrop-blur-sm ${darkMode ? 'bg-white/10 border border-white/20' : 'bg-white/70 border border-white/50'} shadow-xl`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>{t('activeGoals')}</h3>
              <Target className={`w-6 h-6 ${darkMode ? 'text-purple-400' : 'text-purple-500'}`} />
            </div>
            <div className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
              {userGoals.length}
            </div>
          </div>

          {/* NEW: Points Display */}
          <div className={`p-6 rounded-3xl backdrop-blur-sm ${darkMode ? 'bg-white/10 border border-white/20' : 'bg-white/70 border border-white/50'} shadow-xl`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>{t('learningPoints')}</h3>
              <Star className={`w-6 h-6 ${darkMode ? 'text-yellow-400' : 'text-yellow-500'}`} />
            </div>
            <div className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
              {userPoints.toLocaleString()}
            </div>
            <div className={`text-sm mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {userBadges.length} badge{userBadges.length !== 1 ? 's' : ''} earned
            </div>
          </div>

          <div className={`p-6 rounded-3xl backdrop-blur-sm ${darkMode ? 'bg-white/10 border border-white/20' : 'bg-white/70 border border-white/50'} shadow-xl`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>{t('completedLessons')}</h3>
              <BookOpen className={`w-6 h-6 ${darkMode ? 'text-green-400' : 'text-green-500'}`} />
            </div>
            <div className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
              {completedLessons.size}
            </div>
          </div>
        </div>

        {/* NEW: Badges Section */}
        {userBadges.length > 0 && (
          <div className={`p-6 rounded-3xl backdrop-blur-sm ${darkMode ? 'bg-white/10 border border-white/20' : 'bg-white/70 border border-white/50'} shadow-xl mb-8`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>{t('achievements')}</h3>
              <Trophy className={`w-6 h-6 ${darkMode ? 'text-yellow-400' : 'text-yellow-500'}`} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {userBadges.slice(0, 6).map((userBadge, index) => {
                const badge = Object.values(BADGE_SYSTEM).flat().find(b => b.id === userBadge.id);
                if (!badge) return null;
                return (
                  <div key={index} className={`p-3 rounded-xl text-center ${darkMode ? 'bg-gray-800' : 'bg-gray-100'} hover:scale-105 transition-all duration-300`}>
                    <div className={`w-12 h-12 mx-auto mb-2 rounded-full flex items-center justify-center ${badge.color}`}>
                      <span className="text-xl">{badge.icon}</span>
                    </div>
                    <h4 className={`text-xs font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                      {badge.name}
                    </h4>
                    <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {new Date(userBadge.earnedAt).toLocaleDateString()}
                    </p>
                  </div>
                );
              })}
              {userBadges.length > 6 && (
                <div className={`p-3 rounded-xl text-center ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} flex items-center justify-center`}>
                  <span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    +{userBadges.length - 6} more
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-8">
          <div className={`p-6 rounded-3xl backdrop-blur-sm ${darkMode ? 'bg-white/10 border border-white/20' : 'bg-white/70 border border-white/50'} shadow-xl`}>
            <h3 className={`text-xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Your Learning Goals</h3>
            
            {userGoals.length === 0 ? (
              <div className="text-center py-8">
                <Target className={`w-16 h-16 mx-auto mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-4`}>
                  No learning goals yet. Create your first one!
                </p>
                <button
                  onClick={() => setCurrentView('onboarding')}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-xl font-semibold hover:scale-105 transition-all duration-300 shadow-lg"
                >
                  Create Your First Goal
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {userGoals.map((goal, index) => (
                  <div key={goal.id} className={`p-4 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-gray-100'} hover:scale-105 transition-all duration-300 group`}>
                    <div className="flex items-start justify-between">
                      <div 
                        className="flex-1 cursor-pointer"
                        onClick={() => {
                          setCurrentRoadmap(goal);
                          setCurrentView('roadmap');
                          // Restore progress for this goal
                          setProgress(goal.progress || 0);
                          setCompletedLessons(new Set(goal.completedLessons || []));
                        }}
                      >
                        <h4 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>{goal.title}</h4>
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-2`}>{goal.duration}</p>
                        <div className={`w-full h-2 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                          <div className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all duration-300" style={{width: `${goal.progress || 0}%`}} />
                        </div>
                        <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                          {Math.round(goal.progress || 0)}% complete
                        </p>
                      </div>
                      
                      {/* NEW: Delete Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteGoal(goal);
                        }}
                        className={`ml-3 p-2 rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-300 ${
                          darkMode 
                            ? 'bg-red-900/50 text-red-400 hover:bg-red-900 hover:text-red-300' 
                            : 'bg-red-100 text-red-600 hover:bg-red-200 hover:text-red-700'
                        }`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
                <button
                  onClick={() => setCurrentView('onboarding')}
                  className={`w-full p-4 rounded-xl border-2 border-dashed transition-all duration-300 ${darkMode ? 'border-gray-600 text-gray-400 hover:border-purple-500' : 'border-gray-300 text-gray-600 hover:border-purple-500'} hover:scale-105`}
                >
                  <Plus className="w-6 h-6 mx-auto mb-2" />
                  Add New Goal
                </button>
              </div>
            )}
          </div>

          <div className={`p-6 rounded-3xl backdrop-blur-sm ${darkMode ? 'bg-white/10 border border-white/20' : 'bg-white/70 border border-white/50'} shadow-xl`}>
            <h3 className={`text-xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>{t('quickActions')}</h3>
            <div className="space-y-3">
              <button
                onClick={() => setCurrentView('onboarding')}
                className="w-full p-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-semibold hover:scale-105 transition-all duration-300 shadow-lg flex items-center justify-center space-x-2"
              >
                <Plus className="w-5 h-5" />
                <span>{t('createNewPath')}</span>
              </button>
              
              {currentRoadmap && (
                <button
                  onClick={() => setCurrentView('roadmap')}
                  className={`w-full p-4 rounded-xl font-semibold hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-2 ${darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  <BookOpen className="w-5 h-5" />
                  <span>{t('continueCurrentPath')}</span>
                </button>
              )}
              
              <button
                onClick={() => setCurrentView('chat')}
                className={`w-full p-4 rounded-xl font-semibold hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-2 ${darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                <MessageCircle className="w-5 h-5" />
                <span>{t('aiTutor')} ({questionsLeft}/10)</span>
              </button>

              {/* NEW: Advanced Feature Buttons */}
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setShowTutorialGenerator(true)}
                  className={`p-3 rounded-xl font-semibold hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-2 ${darkMode ? 'bg-purple-800 text-purple-300 hover:bg-purple-700' : 'bg-purple-100 text-purple-700 hover:bg-purple-200'}`}
                >
                  <Video className="w-4 h-4" />
                  <span>AI Tutorials</span>
                </button>

                <button
                  onClick={() => setShowSchedulingModal(true)}
                  className={`p-3 rounded-xl font-semibold hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-2 ${darkMode ? 'bg-green-800 text-green-300 hover:bg-green-700' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}
                >
                  <Calendar className="w-4 h-4" />
                  <span>{t('schedule')}</span>
                </button>

                <button
                  onClick={() => setShowNotesUpload(true)}
                  className={`p-3 rounded-xl font-semibold hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-2 ${darkMode ? 'bg-orange-800 text-orange-300 hover:bg-orange-700' : 'bg-orange-100 text-orange-700 hover:bg-orange-200'}`}
                >
                  <Upload className="w-4 h-4" />
                  <span>Upload Files</span>
                </button>

                <button
                  onClick={() => setShowYouTubeUpload(true)}
                  className={`p-3 rounded-xl font-semibold hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-2 ${darkMode ? 'bg-red-800 text-red-300 hover:bg-red-700' : 'bg-red-100 text-red-700 hover:bg-red-200'}`}
                  data-tutorial="youtube-upload"
                >
                  <Youtube className="w-4 h-4" />
                  <span>YouTube</span>
                </button>
              </div>

              {/* Discover Section - Prominent Feature */}
              <button
                onClick={() => setShowDiscoverFeed(true)}
                className={`w-full p-4 rounded-2xl font-bold hover:scale-105 transition-all duration-300 shadow-lg ${
                  darkMode
                    ? 'bg-gradient-to-r from-purple-900 to-pink-900 text-white border-2 border-purple-500'
                    : 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                }`}
                data-tutorial="discover-feed"
              >
                <div className="flex items-center justify-center space-x-3">
                  <Sparkles className="w-6 h-6" />
                  <div className="text-left">
                    <div className="text-lg">Discover</div>
                    <div className="text-xs opacity-90">Learn from peers • Share knowledge</div>
                  </div>
                </div>
              </button>
            </div>

            {/* Study Time Slots Display */}
            {studyTimeSlots.length > 0 && (
              <div className="mt-6">
                <h4 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Today's Schedule</h4>
                <div className="space-y-2">
                  {studyTimeSlots
                    .filter(slot => slot.dayOfWeek === ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][new Date().getDay()])
                    .map(slot => (
                    <div key={slot.id} className={`p-3 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-gray-100'} flex items-center justify-between`}>
                      <div>
                        <h5 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                          {slot.courseName}
                        </h5>
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {slot.startTime} - {slot.endTime}
                        </p>
                      </div>
                      <button
                        onClick={() => removeStudyTimeSlot(slot.id)}
                        className={`p-1 rounded ${darkMode ? 'text-red-400 hover:bg-red-900/30' : 'text-red-600 hover:bg-red-100'} transition-colors duration-300`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {Object.keys(dailyProgress).length > 0 && (
              <div className="mt-6">
                <h4 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Recent Activity</h4>
                <div className="space-y-2">
                  {Object.entries(dailyProgress).slice(-5).map(([date, sessions]) => (
                    <div key={date} className={`flex justify-between items-center ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      <span>{formatDate(date)}</span>
                      <div className="flex items-center space-x-2">
                        <span>{sessions} session{sessions > 1 ? 's' : ''}</span>
                        <div className={`w-2 h-2 rounded-full ${sessions > 0 ? 'bg-green-500' : 'bg-gray-400'}`} />
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Current Date Display */}
                <div className={`mt-4 p-3 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                  <div className="flex justify-between items-center">
                    <span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Today: {currentDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                    </span>
                    <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {dailyProgress[currentDate.toISOString().split('T')[0]] ? '✅ Studied today' : '⏰ No study yet'}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Study Schedule Display */}
            {studySchedule && (
              <div className="mt-6">
                <h4 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Your Schedule</h4>
                <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                  <div className="flex justify-between items-center mb-2">
                    <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {studySchedule.total_days} days per week
                    </span>
                    <span className={`text-xs ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                      {getDaysUntilNext()}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(studySchedule.weekly_pattern).map(([day, info]) => (
                      info.active && (
                        <span key={day} className={`text-xs px-2 py-1 rounded ${darkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>
                          {day.slice(0, 3)}
                        </span>
                      )
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Missed Days Warning */}
            {missedDays.length > 0 && (
              <div className="mt-6">
                <div className={`p-4 rounded-xl ${darkMode ? 'bg-yellow-900/50 border border-yellow-700' : 'bg-yellow-100 border border-yellow-300'}`}>
                  <h4 className={`font-semibold mb-2 ${darkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>
                    📅 Missed Study Days
                  </h4>
                  <p className={`text-sm ${darkMode ? 'text-yellow-200' : 'text-yellow-700'}`}>
                    Don't worry! Your learning plan will adapt to help you catch up.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Landing Page
  const LandingPage = () => (
    <div className={`min-h-screen transition-all duration-500 ${darkMode ? 'bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900' : 'bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50'}`}>
      <div className="container mx-auto px-4 py-8">
        <nav className="flex justify-between items-center mb-16">
          <div className="flex items-center space-x-2">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <span className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>Nalem Study Circle</span>
          </div>
          <div className="flex items-center space-x-4">
            <DeveloperButton
              onClick={() => setShowDeveloperModal(true)}
              darkMode={darkMode}
            />
            <button
              onClick={() => setDarkMode(!darkMode)}
              className={`p-2 rounded-xl transition-all duration-300 ${darkMode ? 'bg-gray-800 text-yellow-400' : 'bg-white text-gray-600'} shadow-lg hover:scale-105`}
            >
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <button
              onClick={() => setCurrentView('auth')}
              className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-2 rounded-xl font-semibold hover:scale-105 transition-all duration-300 shadow-lg"
            >
              Get Started
            </button>
          </div>
        </nav>

        <div className="text-center mb-16">
          <h1 className={`text-6xl font-bold mb-6 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
            Your AI Learning
            <span className="bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent"> Coach</span>
          </h1>
          <p className={`text-xl mb-8 max-w-2xl mx-auto ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Build personalized learning paths in seconds. Master any skill with AI-powered guidance, interactive lessons, and real-time support.
          </p>
          <button
            onClick={() => setCurrentView('auth')}
            className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-4 rounded-2xl text-lg font-semibold hover:scale-105 transition-all duration-300 shadow-xl"
          >
            Start Learning Now
          </button>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {[
            { icon: Zap, title: "Real AI Integration", desc: "Powered by advanced AI for authentic, personalized learning paths and tutoring" },
            { icon: Target, title: "Persistent Progress", desc: "Your learning goals and progress are automatically saved across all sessions" },
            { icon: MessageCircle, title: "Multi-User Support", desc: "Multiple accounts with individual progress tracking and personalized experiences" }
          ].map((feature, i) => (
            <div key={i} className={`p-8 rounded-3xl backdrop-blur-sm transition-all duration-300 hover:scale-105 ${darkMode ? 'bg-white/10 border border-white/20' : 'bg-white/70 border border-white/50'} shadow-xl`}>
              <feature.icon className={`w-12 h-12 mb-4 ${darkMode ? 'text-purple-400' : 'text-purple-500'}`} />
              <h3 className={`text-xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>{feature.title}</h3>
              <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{feature.desc}</p>
            </div>
          ))}
        </div>

        {/* Footer with Legal Links */}
        <footer className="mt-16 border-t border-gray-300/20 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                <Brain className="w-4 h-4 text-white" />
              </div>
              <span className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>Nalem Study Circle</span>
              <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>© 2025</span>
            </div>
            <div className="flex items-center space-x-6">
              <DeveloperFooterLink
                onClick={() => setShowDeveloperModal(true)}
                darkMode={darkMode}
              />
              <FooterLegalLinks 
                setShowTermsModal={setShowTermsModal}
                setShowPrivacyModal={setShowPrivacyModal}
                darkMode={darkMode}
              />
              <button
                onClick={() => setShowLanguageModal(true)}
                className={`text-sm ${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-800'} transition-colors duration-300 flex items-center space-x-1`}
              >
                <Languages className="w-4 h-4" />
                <span>{SUPPORTED_LANGUAGES[currentLanguage]?.flag} {SUPPORTED_LANGUAGES[currentLanguage]?.name}</span>
              </button>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );

  return (
    <div className="font-sans">
      <Tutorial darkMode={darkMode} autoStart={true} />
      {showSettings && <SettingsModal />}
      {showScheduleModal && <ScheduleModal />}
      {showPomodoroModal && <PomodoroModal />}
      {showHelpModal && <QuizHelpModal />}
      {showDeleteModal && <DeleteConfirmationModal />}
      {showBadgeModal && <BadgeModal />}
      {showAnalyticsModal && <AnalyticsModal />}
      {practiceTestMode && <PracticeTestModal />}
      {showSchedulingModal && <StudySchedulingModal />}
      {showVocabularyModal && <VocabularyModal />}
      {flashcardMode && <FlashcardModal />}
      {vocabularyQuizMode && <VocabularyQuizModal />}
      {showNotesUpload && <NotesUploadModal />}
      {showTutorialGenerator && (
        <AITutorialGenerator
          darkMode={darkMode}
          currentUser={currentUser}
          onClose={() => setShowTutorialGenerator(false)}
        />
      )}
      {showYouTubeUpload && (
        <YouTubeUpload
          onGenerateRoadmap={(roadmap) => {
            const newGoals = [...userGoals, roadmap];
            setUserGoals(newGoals);
            setCurrentRoadmap(roadmap);
            setCurrentView('roadmap');
            setShowYouTubeUpload(false);
            saveCurrentSession();
          }}
          onStudyCompanion={(videoInfo) => {
            setCompanionVideoInfo(videoInfo);
            setShowStudyCompanion(true);
          }}
          onClose={() => setShowYouTubeUpload(false)}
          darkMode={darkMode}
          makeOpenAIRequest={makeOpenAIRequest}
        />
      )}

      {showStudyCompanion && companionVideoInfo && (
        <StudyCompanion
          videoInfo={companionVideoInfo}
          onClose={() => {
            setShowStudyCompanion(false);
            setCompanionVideoInfo(null);
          }}
          darkMode={darkMode}
          makeOpenAIRequest={makeOpenAIRequest}
        />
      )}

      {showDiscoverFeed && (
        <DiscoverFeed
          darkMode={darkMode}
          currentUser={currentUser}
          supabase={supabase}
          makeOpenAIRequest={makeOpenAIRequest}
          onClose={() => setShowDiscoverFeed(false)}
          onAddToStudyPlan={(content) => {
            // Add content to user's study plan/roadmap
            console.log('Add to study plan:', content);
            // This can be enhanced later to actually integrate with the roadmap system
          }}
        />
      )}

      {showSATPracticeTest && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="relative w-full max-w-4xl my-8">
            <button
              onClick={() => setShowSATPracticeTest(false)}
              className="absolute -top-4 -right-4 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-100 transition-colors z-10"
            >
              <X className="w-6 h-6 text-gray-600" />
            </button>
            <SATPracticeTest
              onComplete={(result) => {
                setSatTestResults(prev => [...prev, result]);
                analytics.trackTestCompletion('SAT', result);
                setShowSATPracticeTest(false);
              }}
              onClose={() => setShowSATPracticeTest(false)}
            />
          </div>
        </div>
      )}
      {showACTPracticeTest && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="relative w-full max-w-4xl my-8">
            <button
              onClick={() => setShowACTPracticeTest(false)}
              className="absolute -top-4 -right-4 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-100 transition-colors z-10"
            >
              <X className="w-6 h-6 text-gray-600" />
            </button>
            <ACTPracticeTest
              onComplete={(result) => {
                setActTestResults(prev => [...prev, result]);
                analytics.trackTestCompletion('ACT', result);
                setShowACTPracticeTest(false);
              }}
              onClose={() => setShowACTPracticeTest(false)}
            />
          </div>
        </div>
      )}
      {showNEETPracticeTest && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="relative w-full max-w-4xl my-8">
            <button
              onClick={() => setShowNEETPracticeTest(false)}
              className="absolute -top-4 -right-4 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-100 transition-colors z-10"
            >
              <X className="w-6 h-6 text-gray-600" />
            </button>
            <NEETPracticeTest
              onComplete={(result) => {
                setNeetTestResults(prev => [...prev, result]);
                analytics.trackTestCompletion('NEET', result);
                setShowNEETPracticeTest(false);
              }}
              onClose={() => setShowNEETPracticeTest(false)}
            />
          </div>
        </div>
      )}
      {showJEEPracticeTest && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="relative w-full max-w-4xl my-8">
            <button
              onClick={() => setShowJEEPracticeTest(false)}
              className="absolute -top-4 -right-4 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-100 transition-colors z-10"
            >
              <X className="w-6 h-6 text-gray-600" />
            </button>
            <JEEPracticeTest
              onComplete={(result) => {
                setJeeTestResults(prev => [...prev, result]);
                analytics.trackTestCompletion('JEE', result);
                setShowJEEPracticeTest(false);
              }}
              onClose={() => setShowJEEPracticeTest(false)}
            />
          </div>
        </div>
      )}
      {showCodeInterpreter && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-7xl h-[90vh]">
            <CodeInterpreter
              onClose={() => setShowCodeInterpreter(false)}
              makeOpenAIRequest={makeOpenAIRequest}
              currentTopic={currentRoadmap.title || "programming"}
            />
          </div>
        </div>
      )}
      {showAnalyticsDashboard && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 overflow-y-auto">
          <AnalyticsDashboard
            onClose={() => setShowAnalyticsDashboard(false)}
            userEmail={currentUser?.email}
            darkMode={darkMode}
          />
        </div>
      )}
      {showDeveloperModal && (
        <DeveloperModal 
          showDeveloperModal={showDeveloperModal}
          setShowDeveloperModal={setShowDeveloperModal}
          darkMode={darkMode}
        />
      )}
      
      {/* Language Modal */}
      <LanguageModal 
        showLanguageModal={showLanguageModal}
        setShowLanguageModal={setShowLanguageModal}
        currentLanguage={currentLanguage}
        setCurrentLanguage={setCurrentLanguage}
        darkMode={darkMode}
      />
      
      {/* Legal Modals */}
      <TermsModal 
        showTermsModal={showTermsModal}
        setShowTermsModal={setShowTermsModal}
        darkMode={darkMode}
      />
      <PrivacyModal 
        showPrivacyModal={showPrivacyModal}
        setShowPrivacyModal={setShowPrivacyModal}
        darkMode={darkMode}
      />
      
      {/* Donation Banner */}
      <DonationBanner
        darkMode={darkMode}
        showDonationBanner={showDonationBanner}
        setShowDonationBanner={setShowDonationBanner}
        userPoints={userPoints}
        userName={currentUser?.name || 'Learning Enthusiast'}
      />
      
      {/* Floating Donation Button */}
      <FloatingDonationButton
        darkMode={darkMode}
        setShowDonationBanner={setShowDonationBanner}
        isDismissed={donationDismissed}
        onDismiss={handleDonationDismiss}
      />
      
      {/* Floating Pomodoro Timer */}
      {(currentView === 'lesson' || currentView === 'roadmap' || currentView === 'dashboard') && <PomodoroTimer />}

      {/* Achievement Notification Modal */}
      {showAchievementModal && newAchievements.length > 0 && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className={`max-w-md w-full p-8 rounded-3xl ${darkMode ? 'bg-gray-900 border border-gray-700' : 'bg-white border border-gray-200'} shadow-2xl`}>
            <div className="text-center">
              <div className="text-6xl mb-4">🏆</div>
              <h2 className={`text-3xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                Achievement Unlocked!
              </h2>
              {newAchievements.map((achievement, index) => (
                <div key={index} className={`mb-4 p-4 rounded-xl ${darkMode ? 'bg-gray-800' : 'bg-gradient-to-r from-purple-100 to-pink-100'}`}>
                  <div className="text-4xl mb-2">{achievement.icon}</div>
                  <div className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                    {achievement.name}
                  </div>
                  <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {achievement.description}
                  </div>
                  <div className="text-yellow-500 font-bold mt-2">
                    +{achievement.points_reward} Points!
                  </div>
                </div>
              ))}
              <button
                onClick={() => {
                  setShowAchievementModal(false);
                  setNewAchievements([]);
                }}
                className="mt-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-3 rounded-xl font-semibold hover:scale-105 transition-all duration-300"
              >
                Awesome!
              </button>
            </div>
          </div>
        </div>
      )}

      {!isAuthenticated ? (
        <>
          {currentView === 'landing' && <LandingPage />}
          {currentView === 'auth' && <AuthPage showLogin={showLogin} setShowLogin={setShowLogin} />}
        </>
      ) : (
        <>
          {currentView === 'dashboard' && <Dashboard />}
          {currentView === 'onboarding' && <OnboardingPage />}
          {currentView === 'roadmap' && <RoadmapPage />}
          {currentView === 'lesson' && <LessonPage key={`lesson-${currentLesson}-${currentRoadmap?.id}`} />}
          {currentView === 'chat' && <ChatPage />}
        </>
      )}
    </div>
  );
};

export default NalemStudyCircle;




